import {
  renderChart
} from "./chunk-NSXINY2I.js";

// src/widgetShadowCss.ts
var SHADOW_CSS_MARKER = "data-xone-widget-shadow-css";
var SHADOW_CSS_POLL_MS = 100;
var SHADOW_CSS_POLL_MAX = 40;
function ensureStyle(root, css) {
  const existing = root.querySelector(`style[${SHADOW_CSS_MARKER}]`);
  if (existing) {
    if (existing.textContent !== css) existing.textContent = css;
    return;
  }
  const style = document.createElement("style");
  style.setAttribute(SHADOW_CSS_MARKER, "");
  style.textContent = css;
  root.appendChild(style);
}
function applyWidgetShadowCss(el, css) {
  const text = (css ?? "").trim();
  if (!text) return () => {
  };
  let disposed = false;
  let timer;
  let observer = null;
  const attach = (root) => {
    ensureStyle(root, text);
    if (typeof MutationObserver === "undefined") return;
    observer = new MutationObserver(() => {
      if (disposed) return;
      if (!root.querySelector(`style[${SHADOW_CSS_MARKER}]`)) ensureStyle(root, text);
    });
    observer.observe(root, { childList: true });
  };
  const poll = (n) => {
    if (disposed) return;
    const root = el.shadowRoot;
    if (root) {
      attach(root);
      return;
    }
    if (n >= SHADOW_CSS_POLL_MAX) {
      try {
        console.warn(`[xone-island] web-widget-shadow-css: <${el.localName}> sin shadowRoot abierto; CSS no aplicado`);
      } catch {
      }
      return;
    }
    timer = window.setTimeout(() => poll(n + 1), SHADOW_CSS_POLL_MS);
  };
  const tag = el.localName;
  if (el.shadowRoot) {
    attach(el.shadowRoot);
  } else {
    let defined = Promise.resolve();
    try {
      if (typeof customElements !== "undefined" && tag.includes("-")) {
        defined = customElements.whenDefined(tag);
      }
    } catch {
    }
    let started = false;
    const start = () => {
      if (!started) {
        started = true;
        poll(0);
      }
    };
    void defined.then(start, start);
    timer = window.setTimeout(start, SHADOW_CSS_POLL_MS);
  }
  return () => {
    disposed = true;
    if (timer !== void 0) window.clearTimeout(timer);
    observer?.disconnect();
    observer = null;
  };
}

// src/inlineAction.ts
function parseInlineArgs(src, self, bindings) {
  const trimmed = src.trim();
  if (trimmed === "") return [];
  const parts = [];
  let cur = "", quote = "";
  for (let i = 0; i < trimmed.length; i++) {
    const ch = trimmed[i];
    if (quote) {
      if (ch === "\\" && i + 1 < trimmed.length) {
        cur += ch + trimmed[++i];
        continue;
      }
      if (ch === quote) quote = "";
      cur += ch;
    } else if (ch === "'" || ch === '"') {
      quote = ch;
      cur += ch;
    } else if (ch === ",") {
      parts.push(cur);
      cur = "";
    } else cur += ch;
  }
  parts.push(cur);
  const out = [];
  for (const p of parts) {
    const t = p.trim();
    if (t === "self" || t === "This") out.push(self);
    else if (bindings !== void 0 && Object.prototype.hasOwnProperty.call(bindings, t)) out.push(bindings[t]);
    else if (/^'([^']*)'$/.test(t) || /^"([^"]*)"$/.test(t)) out.push(t.slice(1, -1));
    else if (/^-?\d+(\.\d+)?$/.test(t)) out.push(Number(t));
    else if (t === "true") out.push(true);
    else if (t === "false") out.push(false);
    else if (t === "null") out.push(null);
    else return null;
  }
  return out;
}
function execInlineAction(raw, self, onError, bindings) {
  const code = raw.replace(/^javascript:/i, "").trim().replace(/;+$/, "");
  const m = /^([A-Za-z_$][\w$]*)\s*\((.*)\)$/s.exec(code);
  if (!m) return false;
  const fnName = m[1];
  const fn = (typeof window !== "undefined" ? window : globalThis)[fnName];
  if (typeof fn !== "function") return false;
  const args = parseInlineArgs(m[2], self, bindings);
  if (args === null) return false;
  try {
    fn(...args);
    return true;
  } catch (err) {
    try {
      onError?.(err);
    } catch {
    }
    return false;
  }
}

// src/contentsResolve.ts
function resolveContentsColl(appData, srcColl, contentsName) {
  const inst = (contentsName ?? "").replace(/^@/, "").trim();
  return inst && inst !== srcColl ? appData.getCollection(inst, srcColl) : appData.getCollection(srcColl);
}
var rowItemRegistry = /* @__PURE__ */ new WeakMap();
function setRowItem(row, item) {
  if (row && item && typeof item === "object") rowItemRegistry.set(row, item);
}
function getRowItem(row) {
  return row ? rowItemRegistry.get(row) : void 0;
}
function expandRowItem(row) {
  if (!row || !row.classList) return void 0;
  if (!row.classList.contains("xone-expand-child") && !row.classList.contains("xone-expand-summary")) {
    return void 0;
  }
  return getRowItem(row);
}

// src/headerColumnAlign.ts
var COLUMN_MARGIN_EPS = 0.5;
function partitionColumnUnits(cells, eps = COLUMN_MARGIN_EPS) {
  const units = [];
  for (let i = 0; i < cells.length; i++) {
    if (i === 0 || cells[i].marginLeft > eps) {
      units.push([i]);
    } else {
      units[units.length - 1].push(i);
    }
  }
  return units;
}
function computeColumnPinAdjustments(header, row, eps = COLUMN_MARGIN_EPS) {
  const headerUnits = partitionColumnUnits(header, eps);
  const rowUnits = partitionColumnUnits(row, eps);
  if (headerUnits.length < 2) return null;
  if (headerUnits.length !== rowUnits.length) return null;
  const headStarts = headerUnits.map((u) => header[u[0]].left);
  const rowStarts = rowUnits.map((u) => row[u[0]].left);
  const n = headStarts.length;
  let idResidual = 0;
  for (let i = 0; i < n; i++) idResidual += Math.abs(rowStarts[i] - headStarts[i]);
  let shiftResidual = 0;
  for (let i = 1; i < n; i++) shiftResidual += Math.abs(rowStarts[i] - headStarts[i - 1]);
  if (shiftResidual / (n - 1) < idResidual / n) return null;
  const out = [];
  let shift = 0;
  for (let u = 0; u < headerUnits.length; u++) {
    const slotStart = header[headerUnits[u][0]].left;
    const firstIdx = rowUnits[u][0];
    const cell = row[firstIdx];
    const effectiveLeft = cell.left + shift;
    const delta = slotStart - effectiveLeft;
    shift += delta;
    out.push({ index: firstIdx, marginLeft: cell.marginLeft + delta });
  }
  return out;
}
function computeHeaderedTablePins(header, rows, eps = COLUMN_MARGIN_EPS) {
  if (rows.length === 0) return null;
  const perRow = [];
  for (const row of rows) {
    const adj = computeColumnPinAdjustments(header, row, eps);
    if (!adj) return null;
    perRow.push(adj);
  }
  return perRow;
}

// src/fieldHash.ts
function md5Hex(input) {
  const bytes = new TextEncoder().encode(input);
  const len = bytes.length;
  const words = new Uint32Array(len + 72 >>> 6 << 4);
  const view = new DataView(words.buffer);
  for (let i = 0; i < len; i++) view.setUint8(i, bytes[i]);
  view.setUint8(len, 128);
  view.setUint32(words.length - 2 << 2, len << 3, true);
  const S = [7, 12, 17, 22, 5, 9, 14, 20, 4, 11, 16, 23, 6, 10, 15, 21];
  const K = new Uint32Array(64);
  for (let i = 0; i < 64; i++) K[i] = Math.floor(2 ** 32 * Math.abs(Math.sin(i + 1)));
  let a0 = 1732584193, b0 = 4023233417, c0 = 2562383102, d0 = 271733878;
  for (let off = 0; off < words.length; off += 16) {
    let a = a0, b = b0, c = c0, d = d0;
    for (let i = 0; i < 64; i++) {
      let f, g;
      if (i < 16) {
        f = b & c | ~b & d;
        g = i;
      } else if (i < 32) {
        f = d & b | ~d & c;
        g = (5 * i + 1) % 16;
      } else if (i < 48) {
        f = b ^ c ^ d;
        g = (3 * i + 5) % 16;
      } else {
        f = c ^ (b | ~d);
        g = 7 * i % 16;
      }
      const m = view.getUint32(off + g << 2, true);
      const temp = d;
      d = c;
      c = b;
      const sum = a + f + K[i] + m >>> 0;
      const s = S[(i >>> 4 << 2) + (i & 3)];
      b = b + ((sum << s | sum >>> 32 - s) >>> 0) >>> 0;
      a = temp;
    }
    a0 = a0 + a >>> 0;
    b0 = b0 + b >>> 0;
    c0 = c0 + c >>> 0;
    d0 = d0 + d >>> 0;
  }
  const out = new Uint8Array(16);
  const ov = new DataView(out.buffer);
  ov.setUint32(0, a0, true);
  ov.setUint32(4, b0, true);
  ov.setUint32(8, c0, true);
  ov.setUint32(12, d0, true);
  let hex = "";
  for (const byte of out) hex += byte.toString(16).padStart(2, "0");
  return hex;
}
function transformFieldValue(value, hashType, encode) {
  if (value === "") return value;
  const algo = (hashType ?? "").trim().toUpperCase();
  const enc = (encode ?? "").trim().toUpperCase();
  if (algo === "MD5" && enc === "HEX") return md5Hex(value);
  return value;
}

// src/wire/groupTabs.ts
function wireGroupTabs(chassis, hooks) {
  const tablist = chassis.querySelector('[role="tablist"]');
  const groupsEl = chassis.querySelector(".xone-groups");
  if (!groupsEl) return () => {
  };
  const panels = Array.from(groupsEl.children).filter(
    (el) => el instanceof HTMLElement && el.classList.contains("xone-group") && el.getAttribute("role") === "tabpanel"
  );
  const tabs = tablist ? Array.from(tablist.querySelectorAll('[role="tab"]')) : [];
  if (panels.length === 0) return () => {
  };
  const panelById = (id) => panels.find((p) => p.dataset["groupId"] === id);
  const tabById = (id) => tabs.find((t) => t.dataset["groupId"] === id);
  const nameOf = (id) => panelById(id)?.dataset["group"] ?? "";
  const currentActiveId = () => {
    const vis = panels.find((p) => !p.hidden);
    return vis?.dataset["groupId"] ?? null;
  };
  const isDvHidden = (id) => panelById(id)?.hasAttribute("data-group-dv-hidden") === true;
  const visibleTabs = () => tabs.filter((t) => !t.hidden && !isDvHidden(t.dataset["groupId"] ?? ""));
  function activate(id, opts = {}) {
    const { focus = false, fireEvents = true } = opts;
    if (isDvHidden(id)) return;
    const prevId = currentActiveId();
    if (prevId === id) return;
    if (fireEvents && prevId && !hooks.canLeaveGroup(prevId, nameOf(prevId))) {
      return;
    }
    if (fireEvents && prevId) {
      hooks.fireGroupEvent("onlostfocus", prevId, nameOf(prevId));
    }
    for (const p of panels) p.hidden = p.dataset["groupId"] !== id;
    for (const t of tabs) {
      const sel = t.dataset["groupId"] === id;
      t.setAttribute("aria-selected", String(sel));
      t.tabIndex = sel ? 0 : -1;
    }
    const panel = panelById(id);
    if (panel) hooks.hydratePanel(panel);
    if (fireEvents) hooks.fireGroupEvent("onfocus", id, nameOf(id));
    if (focus) tabById(id)?.focus();
    hooks.persistActiveGroup(id);
  }
  const onClick = (e) => {
    const target = e.target;
    const tab = target?.closest('[role="tab"]');
    if (tab && tablist?.contains(tab) && tab.dataset["groupId"]) {
      activate(tab.dataset["groupId"], { focus: true });
    }
  };
  const onKeydown = (e) => {
    const vtabs = visibleTabs();
    if (vtabs.length === 0) return;
    const idx = vtabs.findIndex(
      (t) => t.getAttribute("aria-selected") === "true"
    );
    let next = -1;
    switch (e.key) {
      case "ArrowRight":
      case "ArrowDown":
        next = (idx + 1) % vtabs.length;
        break;
      case "ArrowLeft":
      case "ArrowUp":
        next = (idx - 1 + vtabs.length) % vtabs.length;
        break;
      case "Home":
        next = 0;
        break;
      case "End":
        next = vtabs.length - 1;
        break;
      default:
        return;
    }
    e.preventDefault();
    const id = vtabs[next]?.dataset["groupId"];
    if (id) activate(id, { focus: true });
  };
  tablist?.addEventListener("click", onClick);
  tablist?.addEventListener("keydown", onKeydown);
  const swipeEnabled = chassis.dataset["groupSwipe"] === "true";
  let touchX = 0;
  let touchY = 0;
  const onTouchStart = (e) => {
    const t = e.changedTouches[0];
    if (t) {
      touchX = t.clientX;
      touchY = t.clientY;
    }
  };
  const onTouchEnd = (e) => {
    const t = e.changedTouches[0];
    if (!t) return;
    const dx = t.clientX - touchX;
    const dy = t.clientY - touchY;
    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy)) return;
    const order = panels.filter((p) => !p.hasAttribute("data-group-dv-hidden") && !tabById(p.dataset["groupId"] ?? "")?.hidden).map((p) => p.dataset["groupId"]);
    const cur = currentActiveId();
    const idx = cur ? order.indexOf(cur) : 0;
    const nextIdx = dx < 0 ? idx + 1 : idx - 1;
    if (nextIdx >= 0 && nextIdx < order.length) {
      activate(order[nextIdx], { focus: false });
    }
  };
  if (swipeEnabled) {
    groupsEl.addEventListener("touchstart", onTouchStart, { passive: true });
    groupsEl.addEventListener("touchend", onTouchEnd, { passive: true });
  }
  const isManualTabs = chassis.dataset["manualTabs"] === "true";
  let restoreTimer = null;
  let disposed = false;
  if (!isManualTabs) {
    const saved = hooks.readActiveGroup();
    if (saved && panelById(saved) && saved !== currentActiveId() && !isDvHidden(saved)) {
      activate(saved, { fireEvents: false });
    }
  } else if (hooks.isBackNavigation()) {
    const saved = hooks.readActiveGroup();
    if (saved && panelById(saved) && saved !== currentActiveId() && !isDvHidden(saved)) {
      const cssId = saved.replace(/["\\]/g, "\\$&");
      const MAX_ATTEMPTS = 20;
      let attempt = 0;
      const tick = () => {
        restoreTimer = null;
        if (currentActiveId() === saved || isDvHidden(saved)) return;
        const btn = document.querySelector(
          `xone-island[data-goto-group="${cssId}"][hydrated] button`
        );
        if (btn) {
          btn.click();
          if (currentActiveId() === saved) return;
        }
        attempt += 1;
        if (attempt >= MAX_ATTEMPTS) {
          if (currentActiveId() !== saved) {
            activate(saved, { fireEvents: false });
          }
          return;
        }
        restoreTimer = window.setTimeout(tick, 100);
      };
      let lifecycleGate = null;
      void Promise.race([
        hooks.awaitLifecycleReady().catch(() => void 0),
        new Promise((resolve) => {
          lifecycleGate = window.setTimeout(resolve, 6e3);
        })
      ]).then(() => {
        if (lifecycleGate !== null) window.clearTimeout(lifecycleGate);
        if (disposed) return;
        restoreTimer = window.setTimeout(tick, 0);
      });
    }
  } else {
    hooks.clearActiveGroup();
  }
  chassis.__xoneActivateGroup = (id, opts) => activate(id, { focus: false, ...opts ?? {} });
  return () => {
    disposed = true;
    if (restoreTimer !== null) window.clearTimeout(restoreTimer);
    tablist?.removeEventListener("click", onClick);
    tablist?.removeEventListener("keydown", onKeydown);
    if (swipeEnabled) {
      groupsEl.removeEventListener("touchstart", onTouchStart);
      groupsEl.removeEventListener("touchend", onTouchEnd);
    }
    delete chassis.__xoneActivateGroup;
  };
}

// src/wire/drawer.ts
function wireDrawer(host, hooks) {
  const asideEl = host.querySelector(".xone-drawer");
  const scrimEl = host.querySelector("[data-drawer-scrim]");
  if (!asideEl || !scrimEl) return () => {
  };
  const aside = asideEl;
  const scrim = scrimEl;
  const coll = host.closest(".xone-coll");
  const groupName = aside.querySelector(".xone-group")?.dataset["group"] ?? "";
  let locked = null;
  const isOpen = () => aside.classList.contains("is-open");
  const focusFirst = () => {
    aside.querySelector(
      "[autofocus],button,[href],input,select,textarea,[tabindex]"
    )?.focus();
  };
  const setBackgroundInert = (on) => {
    if (!coll) return;
    for (const child of Array.from(coll.children)) {
      if (child === host) continue;
      child.inert = on;
    }
  };
  const finishOpen = (open) => {
    if (!open) {
      aside.hidden = true;
      scrim.hidden = true;
    }
    hooks.fireGroupEvent(open ? "ondraweropened" : "ondrawerclosed", groupName, {
      slideOffset: open ? 1 : 0
    });
    hooks.fireGroupEvent("ondrawerstatechanged", groupName, { state: "idle" });
    if (open) focusFirst();
  };
  function setOpen(open) {
    if (locked === "open" && !open) return;
    if (locked === "closed" && open) return;
    if (open === isOpen()) return;
    aside.hidden = false;
    scrim.hidden = false;
    void aside.offsetWidth;
    aside.classList.toggle("is-open", open);
    scrim.classList.toggle("is-open", open);
    aside.inert = !open;
    setBackgroundInert(open);
    hooks.fireGroupEvent("ondrawerstatechanged", groupName, { state: "settling" });
    let done = false;
    const onEnd = (e) => {
      if (e.target !== aside || e.propertyName !== "transform") return;
      if (done) return;
      done = true;
      aside.removeEventListener("transitionend", onEnd);
      finishOpen(open);
    };
    aside.addEventListener("transitionend", onEnd);
    setTimeout(() => {
      if (done) return;
      done = true;
      aside.removeEventListener("transitionend", onEnd);
      finishOpen(open);
    }, 350);
  }
  const onScrim = () => setOpen(false);
  const onKey = (e) => {
    if (e.key === "Escape" && isOpen()) setOpen(false);
  };
  scrim.addEventListener("click", onScrim);
  host.ownerDocument.addEventListener("keydown", onKey);
  const handle = {
    open: () => setOpen(true),
    close: () => setOpen(false),
    toggle: () => setOpen(!isOpen()),
    isOpen,
    lock: (closeState) => {
      locked = closeState ? "closed" : "open";
    },
    unlock: () => {
      locked = null;
    }
  };
  host.__xoneDrawer = handle;
  return () => {
    scrim.removeEventListener("click", onScrim);
    host.ownerDocument.removeEventListener("keydown", onKey);
    delete host.__xoneDrawer;
  };
}

// src/dvSafeEval.ts
var DV_TOKEN = /\s+|_coerce|row|==|!=|>=|<=|&&|\|\||[()[\]!<>]|'[^']*'|"[^"]*"|\d+(?:\.\d+)?|[A-Za-z_$][\w$]*/g;
function coerceDv(v) {
  return v === null || v === void 0 ? 0 : v;
}
function compileTranslatedDv(js) {
  const toks = [];
  DV_TOKEN.lastIndex = 0;
  let m;
  let cur = 0;
  while ((m = DV_TOKEN.exec(js)) !== null) {
    if (m.index !== cur) {
      throw new Error(`dvSafeEval: caracter no soportado en pos ${cur}: '${js.slice(cur, m.index)}'`);
    }
    cur = m.index + m[0].length;
    if (!/^\s+$/.test(m[0])) toks.push(m[0]);
  }
  if (cur !== js.length) {
    throw new Error(`dvSafeEval: caracteres sobrantes al final: '${js.slice(cur)}'`);
  }
  let i = 0;
  const peek = () => toks[i];
  const eat = (expected) => {
    const x = toks[i++];
    if (x === void 0) {
      throw new Error(`dvSafeEval: fin inesperado de expresi\xF3n (se esperaba '${expected ?? "token"}')`);
    }
    if (expected !== void 0 && x !== expected) {
      throw new Error(`dvSafeEval: se esperaba '${expected}' pero lleg\xF3 '${x}'`);
    }
    return x;
  };
  const strLit = (t) => t[0] === "'" || t[0] === '"' ? t.slice(1, -1) : t;
  function primary() {
    const t = eat();
    if (t === "(") {
      const e = orExpr();
      eat(")");
      return e;
    }
    if (t === "_coerce") {
      eat("(");
      eat("row");
      eat("[");
      const k = strLit(eat());
      eat("]");
      eat(")");
      return (row) => coerceDv(row[k]);
    }
    if (t === "row") {
      eat("[");
      const k = strLit(eat());
      eat("]");
      return (row) => row[k];
    }
    if (t === "null") return () => null;
    if (t === "undefined") return () => void 0;
    if (t === "true") return () => true;
    if (t === "false") return () => false;
    if (t[0] === "'" || t[0] === '"') {
      const s = t.slice(1, -1);
      return () => s;
    }
    if (/^\d/.test(t)) {
      const n = Number(t);
      return () => n;
    }
    return () => void 0;
  }
  function unary() {
    if (peek() === "!") {
      eat("!");
      const e = unary();
      return (row) => !e(row);
    }
    return primary();
  }
  function cmp() {
    let left = unary();
    for (; ; ) {
      const op = peek();
      if (op !== "==" && op !== "!=" && op !== ">" && op !== "<" && op !== ">=" && op !== "<=") break;
      eat();
      const right = unary();
      const l = left;
      switch (op) {
        // eslint-disable-next-line eqeqeq
        case "==":
          left = (row) => l(row) == right(row);
          break;
        // eslint-disable-next-line eqeqeq
        case "!=":
          left = (row) => l(row) != right(row);
          break;
        case ">":
          left = (row) => l(row) > right(row);
          break;
        case "<":
          left = (row) => l(row) < right(row);
          break;
        case ">=":
          left = (row) => l(row) >= right(row);
          break;
        default:
          left = (row) => l(row) <= right(row);
          break;
      }
    }
    return left;
  }
  function andExpr() {
    let left = cmp();
    while (peek() === "&&") {
      eat("&&");
      const right = cmp();
      const l = left;
      left = (row) => Boolean(l(row)) && Boolean(right(row));
    }
    return left;
  }
  function orExpr() {
    let left = andExpr();
    while (peek() === "||") {
      eat("||");
      const right = andExpr();
      const l = left;
      left = (row) => Boolean(l(row)) || Boolean(right(row));
    }
    return left;
  }
  const fn = orExpr();
  if (i !== toks.length) {
    throw new Error(`dvSafeEval: token inesperado '${peek()}'`);
  }
  return fn;
}

// src/rebindInlineHandlers.ts
var CALL_RE = /^\s*([A-Za-z_$][\w$]*)\s*\(([^]*?)\)\s*;?\s*$/;
function parseLiteralArg(tok) {
  const t = tok.trim();
  if (t === "") return { ok: false };
  if (t === "true") return { ok: true, value: true };
  if (t === "false") return { ok: true, value: false };
  if (t === "null") return { ok: true, value: null };
  if (/^-?\d+(?:\.\d+)?$/.test(t)) return { ok: true, value: Number(t) };
  const q = t[0];
  if ((q === "'" || q === '"') && t.length >= 2 && t[t.length - 1] === q) {
    const inner = t.slice(1, -1);
    if (!inner.includes(q)) return { ok: true, value: inner };
  }
  return { ok: false };
}
function rebindOne(el, win) {
  const code = el.getAttribute("onclick");
  if (code == null) return;
  const m = CALL_RE.exec(code);
  if (!m) return;
  const fnName = m[1];
  const argsStr = m[2];
  if (fnName === void 0 || argsStr === void 0) return;
  const fn = win[fnName];
  if (typeof fn !== "function") return;
  const args = [];
  if (argsStr.trim() !== "") {
    for (const part of argsStr.split(",")) {
      const p = parseLiteralArg(part);
      if (!p.ok) return;
      args.push(p.value);
    }
  }
  el.removeAttribute("onclick");
  el.addEventListener("click", function() {
    try {
      fn.apply(this, args);
    } catch {
    }
  });
}
function rebindInlineHandlers(root) {
  if (!root || typeof root.querySelectorAll !== "function") return;
  const win = typeof window !== "undefined" ? window : void 0;
  if (!win) return;
  if (typeof root.hasAttribute === "function" && root.hasAttribute("onclick")) rebindOne(root, win);
  const nodes = root.querySelectorAll("[onclick]");
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    if (node) rebindOne(node, win);
  }
}

// src/comboCommit.ts
var current = null;
var generation = 0;
function markComboCommit(el) {
  current = el;
  const g = ++generation;
  try {
    setTimeout(() => {
      if (generation === g) current = null;
    }, 0);
  } catch {
    current = null;
  }
}
function getCommittingCombo() {
  return current && current.isConnected ? current : null;
}

// src/buttonCommit.ts
var current2 = null;
var generation2 = 0;
function markButtonCommit(el) {
  current2 = el;
  const g = ++generation2;
  try {
    setTimeout(() => {
      if (generation2 === g) current2 = null;
    }, 0);
  } catch {
    current2 = null;
  }
}
function getCommittingButton() {
  return current2 && current2.isConnected ? current2 : null;
}

// src/XoneIsland.ts
import {
  formatXoneDisplayDate,
  formatXoneInputDate,
  fromStorage,
  NUMERIC_FIELD_TYPES
} from "@xone-web/types";
import { developObjectValue, whenWritesIdle } from "@xone-web/data";
function isEmptyEventHandler(fn) {
  return fn?.xoneEmptyBody === true;
}
var RUNTIME_KEY = "__xoneIslandRuntime";
var CONTENTS_ITEM_CHANGED_EVENT = "xone:contents-item-changed";
function fieldHasRowDependents(depText, field, item) {
  if (!field) return true;
  if (rowImgRecomputePending(depText, item)) return true;
  const text = depText;
  const re = fieldRefRegExp(field);
  let dataProps = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    const before = text.slice(Math.max(0, m.index - 12), m.index);
    const after = text.charAt(m.index + m[0].length);
    if (after === '"') {
      if (/(?:^|[\s<])data-prop="$/.test(before)) {
        dataProps++;
        continue;
      }
      if (/(?:^|\s)(?:xone-)?name="$/.test(before)) continue;
    }
    return true;
  }
  return dataProps > 1;
}
function rowImgRecomputePending(depText, item) {
  if (!depText.includes("data-xone-img-src")) return false;
  if (item === void 0) return true;
  const imgRe = /data-xone-img-src="([^"]*)"/g;
  const fldRe = /##FLD_([A-Za-z0-9_]+)##/g;
  let im;
  while ((im = imgRe.exec(depText)) !== null) {
    fldRe.lastIndex = 0;
    let fm;
    while ((fm = fldRe.exec(im[1])) !== null) {
      const v = readItemValue(item, fm[1]);
      if (v === void 0 || v === null || v === "") return true;
    }
  }
  return false;
}
function fieldRefRegExp(field) {
  const esc = field.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`(?<![A-Za-z0-9-])${esc}(?![A-Za-z0-9_-])`, "gi");
}
function countFieldRefs(text, field) {
  const re = fieldRefRegExp(field);
  let n = 0;
  while (re.exec(text) !== null) n++;
  return n;
}
function fieldHasRowDependentsBeyond(depText, field, ownText, item) {
  if (!field) return true;
  if (rowImgRecomputePending(depText, item)) return true;
  return countFieldRefs(depText, field) > countFieldRefs(ownText, field);
}
function isZHost(el) {
  return el.getAttribute("data-type") === "Z" || el.getAttribute("data-xone-type") === "Z";
}
function buildRowDepIndex(templateHtml, hostEls = []) {
  if (typeof document === "undefined") return null;
  const out = [];
  const walk = (root, nested) => {
    for (const node of Array.from(root.childNodes)) {
      if (node.nodeType === 1) {
        const el = node;
        for (const a of Array.from(el.attributes)) {
          out.push({ kind: "attr", name: a.name, value: a.value, el, nested, host: false });
        }
        const inner = nested || isZHost(el) || el instanceof HTMLTemplateElement;
        walk(el, inner);
        if (el instanceof HTMLTemplateElement) walk(el.content, true);
      } else if (node.nodeType === 3) {
        const t = node.textContent ?? "";
        if (t.includes("##")) out.push({ kind: "text", name: "", value: t, el: null, nested, host: false });
      }
    }
  };
  try {
    const tpl = document.createElement("template");
    tpl.innerHTML = templateHtml;
    walk(tpl.content, false);
  } catch {
    return null;
  }
  for (const h of hostEls) {
    for (const a of Array.from(h.attributes)) {
      out.push({ kind: "attr", name: a.name, value: a.value, el: h, nested: false, host: true });
    }
  }
  return out;
}
var ROW_DV_ATTRS = /* @__PURE__ */ new Set(["data-disablevisible", "data-disableedit"]);
function classifyRowFieldDeps(index, field) {
  const fail = { ok: false, dv: false, bind: false };
  if (!field) return fail;
  const lf = field.toLowerCase();
  const eq = (v) => (v ?? "").toLowerCase() === lf;
  let dv = false;
  let bind = false;
  for (const e of index) {
    if (countFieldRefs(e.value, field) === 0) continue;
    if (e.kind === "text" || e.host || e.nested || !e.el) return fail;
    const el = e.el;
    if (ROW_DV_ATTRS.has(e.name)) {
      dv = true;
      continue;
    }
    if (e.name === "data-prop") {
      if (!eq(e.value)) return fail;
      if (el.getAttribute("data-type") === "frame" || el.hasAttribute("data-xone-frame") || isZHost(el)) return fail;
      if (el.tagName === "IMG" || el.querySelector("img")) return fail;
      if (el.closest("[data-hash-type]") || el.querySelector("[data-hash-type]")) return fail;
      if (el.getAttribute("data-xone-type") === "THTML") return fail;
      bind = true;
      continue;
    }
    if (e.name === "name" || e.name === "xone-name") {
      if (!eq(e.value)) return fail;
      if (!eq(el.closest("[data-prop]")?.getAttribute("data-prop"))) return fail;
      continue;
    }
    if (e.name === "aria-label") {
      if (!eq(e.value) || !eq(el.closest("xone-combo")?.getAttribute("data-prop"))) return fail;
      continue;
    }
    if ((e.name === "data-combo-idprop" || e.name === "data-combo-mirrors") && el.tagName === "XONE-COMBO") {
      continue;
    }
    return fail;
  }
  return { ok: true, dv, bind };
}
var COMBO_BINDING_ATTRS = [
  "data-prop",
  "data-combo-idprop",
  "data-combo-mirrors",
  "aria-label",
  "name",
  "xone-name"
];
function comboOwnBindingText(combo) {
  const out = [];
  for (const el of [combo, ...Array.from(combo.querySelectorAll("*"))]) {
    for (const name of COMBO_BINDING_ATTRS) {
      const v = el.getAttribute(name);
      if (v !== null) out.push(`${name}="${v}"`);
    }
  }
  return out.join(" ");
}
function comboBoundFields(combo) {
  const fields = [];
  const push = (f) => {
    if (typeof f === "string" && f !== "") fields.push(f);
  };
  push(combo.getAttribute("data-prop"));
  push(combo.getAttribute("data-combo-idprop"));
  const raw = combo.getAttribute("data-combo-mirrors");
  if (raw) {
    try {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr)) for (const m of arr) push(m?.prop);
    } catch {
    }
  }
  return fields;
}
function buildRowDepText(templateHtml) {
  if (typeof document === "undefined") return templateHtml;
  const out = [];
  const walk = (root, nested) => {
    for (const node of Array.from(root.childNodes)) {
      if (node.nodeType === 1) {
        const el = node;
        for (const a of Array.from(el.attributes)) {
          const name = nested && a.name === "data-xone-img-src" ? "data-xone-nested-img-src" : a.name;
          out.push(`${name}="${a.value}"`);
        }
        walk(el, nested);
        if (el instanceof HTMLTemplateElement) walk(el.content, true);
      } else if (node.nodeType === 3) {
        const t = node.textContent ?? "";
        if (t.includes("##")) out.push(t);
      }
    }
  };
  try {
    const tpl = document.createElement("template");
    tpl.innerHTML = templateHtml;
    walk(tpl.content, false);
  } catch {
    return templateHtml;
  }
  return out.join(" ");
}
function isTypingControl(el) {
  if (!el) return false;
  if (el.tagName === "TEXTAREA" || el.tagName === "SELECT") return true;
  if (el.tagName !== "INPUT") return false;
  const t = (el.type || "text").toLowerCase();
  return !["checkbox", "radio", "file", "button", "submit", "reset", "image", "hidden"].includes(t);
}
function readItemValue(item, field) {
  if (!item || typeof item !== "object") return void 0;
  const it = item;
  if (typeof it.getValue === "function") {
    try {
      return it.getValue(field);
    } catch {
      return void 0;
    }
  }
  return it[field];
}
function sameRowSet(a, b) {
  const n = a.getCount?.() ?? 0;
  if (n !== (b.getCount?.() ?? 0)) return false;
  for (let i = 0; i < n; i++) {
    const ra = readItemValue(a.get?.(i), "ROWID");
    const rb = readItemValue(b.get?.(i), "ROWID");
    if (ra == null || ra === "" || String(ra) !== String(rb ?? "")) return false;
  }
  return true;
}
function sameItem(a, b) {
  if (!a || !b) return false;
  if (a === b) return true;
  const ra = readItemValue(a, "ROWID");
  const rb = readItemValue(b, "ROWID");
  return ra != null && ra !== "" && String(ra) === String(rb ?? "");
}
var NUMERIC_INPUT_MASK_TYPES = /* @__PURE__ */ new Set([
  "N",
  "N1",
  "N2",
  "N3",
  "N4",
  "N5",
  "N6",
  "F"
]);
function maskNumericInsert(current3, selStart, selEnd, data) {
  const norm = data.replace(/\./g, ",");
  if (/[^0-9,eE-]/.test(norm)) return null;
  const prospective = current3.slice(0, selStart) + norm + current3.slice(selEnd);
  if (!/^-?\d*(,\d*)?([eE][+-]?\d*)?$/.test(prospective)) return null;
  return norm;
}
function decimalTextMatchesModel(domText, model) {
  const t = String(domText ?? "").trim();
  if (!/^-?\d+(,\d*)?$|^-?\d*,\d+$/.test(t)) return false;
  const n = Number(t.replace(",", ".").replace(/\.$/, ""));
  if (!Number.isFinite(n)) return false;
  if (model === null || model === void 0 || model === "") return false;
  const m = typeof model === "number" ? model : Number(String(model).replace(",", "."));
  if (!Number.isFinite(m)) return false;
  return n === m;
}
var DV_ROW_CACHE = /* @__PURE__ */ new Map();
function compileDvRow(js) {
  let fn = DV_ROW_CACHE.get(js);
  if (!fn) {
    try {
      fn = compileTranslatedDv(js);
    } catch {
      fn = () => false;
    }
    DV_ROW_CACHE.set(js, fn);
  }
  return fn;
}
var NON_READONLY_INPUT_TYPES = /* @__PURE__ */ new Set([
  "checkbox",
  "radio",
  "button",
  "submit",
  "reset",
  "file",
  "range",
  "color",
  "image",
  "hidden"
]);
function setEditDisabledState(el, disabled) {
  const tag = el.tagName;
  const type = (el.type ?? "").toLowerCase();
  const useReadonly = tag === "TEXTAREA" || tag === "INPUT" && !NON_READONLY_INPUT_TYPES.has(type);
  if (useReadonly) {
    if (disabled) {
      el.setAttribute("readonly", "");
    } else {
      el.removeAttribute("readonly");
      el.removeAttribute("inert");
    }
  } else {
    if (disabled) el.setAttribute("disabled", "");
    else el.removeAttribute("disabled");
  }
}
var INERT_RECOMPUTE = new Proxy(function inert() {
  return INERT_RECOMPUTE;
}, {
  get(_t, prop) {
    return prop === Symbol.toPrimitive ? (() => 0) : INERT_RECOMPUTE;
  },
  apply() {
    return INERT_RECOMPUTE;
  },
  construct() {
    return INERT_RECOMPUTE;
  },
  has() {
    return true;
  }
});
function makeRecomputeSelf(item) {
  const stubs = {
    save() {
    },
    getValue(f) {
      return item[f];
    },
    setValue(f, v) {
      item[f] = v;
    },
    get(f) {
      return item[f];
    },
    set(f, v) {
      item[f] = v;
    },
    getParent() {
      return null;
    },
    setFieldPropertyValue() {
    },
    getFieldPropertyValue() {
      return void 0;
    },
    getName() {
      return "";
    }
  };
  return new Proxy(item, {
    get(target, prop) {
      if (typeof prop === "string" && Object.prototype.hasOwnProperty.call(stubs, prop)) {
        return stubs[prop];
      }
      return target[prop];
    },
    set(target, prop, value) {
      target[prop] = value;
      return true;
    },
    has(target, prop) {
      if (typeof prop === "string" && Object.prototype.hasOwnProperty.call(stubs, prop)) return true;
      return prop in target;
    }
  });
}
var CONTENTS_EDITOR_SELECTOR = "input:not([type=hidden]), textarea, select, xone-combo, [contenteditable]";
var ALLOW_EDIT = 1;
var ALLOW_VIEW = 2;
function contentsEditMask(host) {
  const locked = host.closest('[data-contents-edit-locked="1"]') !== null;
  const allowView = host.getAttribute("data-contents-allow-view") === "true";
  return (locked ? 0 : ALLOW_EDIT) | (allowView ? ALLOW_VIEW : 0);
}
function setContentsHostEditLock(el, locked) {
  const tag = el.tagName;
  const type = (el.type ?? "").toLowerCase();
  const useReadonly = tag === "TEXTAREA" || tag === "INPUT" && !NON_READONLY_INPUT_TYPES.has(type) || el.hasAttribute("contenteditable");
  if (locked) {
    el.setAttribute("data-host-edit-locked", "1");
    if (useReadonly) {
      if (!el.hasAttribute("readonly")) {
        el.setAttribute("readonly", "");
        el.setAttribute("data-host-set-readonly", "1");
      }
    } else if (!el.hasAttribute("disabled")) {
      el.setAttribute("disabled", "");
      el.setAttribute("data-host-set-disabled", "1");
    }
  } else {
    if (!el.hasAttribute("data-host-edit-locked")) return;
    el.removeAttribute("data-host-edit-locked");
    if (el.hasAttribute("data-host-set-readonly")) {
      el.removeAttribute("readonly");
      el.removeAttribute("data-host-set-readonly");
    }
    if (el.hasAttribute("data-host-set-inert")) {
      el.removeAttribute("inert");
      el.removeAttribute("data-host-set-inert");
    }
    if (el.hasAttribute("data-host-set-disabled")) {
      el.removeAttribute("disabled");
      el.removeAttribute("data-host-set-disabled");
    }
  }
}
function trackNodePromise(p) {
  try {
    const g = globalThis;
    if (!g.__xonePendingWrites) g.__xonePendingWrites = /* @__PURE__ */ new Set();
    const set = g.__xonePendingWrites;
    set.add(p);
    const remove = () => {
      set.delete(p);
    };
    p.then(remove, remove);
  } catch {
  }
  return p;
}
var HYDRATED = /* @__PURE__ */ Symbol("xone-island-hydrated");
var ELEMENT_TAG = "xone-island";
var __xoneLoadedScripts = /* @__PURE__ */ new Map();
var PAGE_EXPAND_OPEN = /* @__PURE__ */ new Map();
function loadExternalScriptOnce(src) {
  const cached = __xoneLoadedScripts.get(src);
  if (cached) return cached;
  const p = new Promise((resolve) => {
    const already = document.querySelector(
      `script[data-xone-widget-src="${CSS.escape(src)}"]`
    );
    if (already) {
      resolve();
      return;
    }
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.setAttribute("data-xone-widget-src", src);
    s.onload = () => resolve();
    s.onerror = () => resolve();
    document.head.appendChild(s);
  });
  __xoneLoadedScripts.set(src, p);
  return p;
}
function resolveMacrosForApi(api, value) {
  let out = value;
  out = out.replace(/##FLD_([A-Za-z0-9_]+)##/g, (_m, field) => {
    try {
      const v = api.self?.getValue?.(field);
      return v == null ? "" : String(v);
    } catch {
      return "";
    }
  });
  try {
    if (api.appData?.expandMacros) out = api.appData.expandMacros(out);
  } catch {
  }
  return out;
}
var GLOBAL_MACRO_EVENT = "xone:global-macro";
var WIDGET_UNRESOLVED_MACRO = /##[A-Za-z0-9_]+##/;
var WIDGET_MACRO_POLL_MAX = 24;
var WIDGET_MACRO_POLL_MS = 250;
function resolveWidgetMacros(runtime, value) {
  return resolveMacrosForApi(runtime.api, value);
}
function resolveWidgetMacrosWithApi(api, value) {
  return resolveMacrosForApi(api, value);
}
var EXPAND_NIVEL_FIELDS = ["NIVEL", "MAP_NIVEL"];
var EXPAND_PARENT_FIELDS = ["IDPADRE", "ID_PADRE", "MAP_IDPADRE", "MAP_ID_PADRE"];
function resolveExpandNivelField(item) {
  return EXPAND_NIVEL_FIELDS.find((f) => f in item);
}
function resolveExpandParentId(item) {
  for (const f of EXPAND_PARENT_FIELDS) {
    const v = item[f];
    if (v !== void 0 && v !== null) return v;
  }
  return "";
}
function resolveAutoCollapse(raw, isShellMenu) {
  if (raw === null || raw === void 0 || raw.trim() === "") return isShellMenu;
  const v = raw.trim().toLowerCase();
  return !(v === "false" || v === "0" || v === "no");
}
var EXPAND_RENDER_GEN = 0;
function paintExpandSelected(host, row, color) {
  for (const prev of Array.from(host.querySelectorAll('.xone-expand-children [data-selected="true"]'))) {
    if (prev === row) continue;
    prev.style.backgroundColor = "";
    prev.removeAttribute("data-selected");
  }
  if (color !== "") row.style.backgroundColor = xoneColorToCss(color);
  row.setAttribute("data-selected", "true");
}
function applyExpandSelection(childContainer) {
  const host = childContainer.closest("[data-expand-sel-key]");
  const selKey = host?.getAttribute("data-expand-sel-key") ?? "";
  if (!host || !selKey) return;
  let selId = null;
  try {
    selId = sessionStorage.getItem(selKey);
  } catch {
    selId = null;
  }
  if (!selId) return;
  const color = host.closest("[data-child-cell-selected-bgcolor]")?.getAttribute("data-child-cell-selected-bgcolor") ?? "";
  for (const row of Array.from(childContainer.querySelectorAll(".xone-contents-row"))) {
    const id = String((getRowItem(row) ?? expandRowItem(row))?.["ID"] ?? "");
    if (id === selId) {
      paintExpandSelected(host, row, color);
      return;
    }
  }
}
var XoneIslandElement = class _XoneIslandElement extends HTMLElement {
  [HYDRATED] = false;
  cleanup = [];
  /** Re-carga del contents (la fija wireContents). */
  _reloadContents = null;
  /** Re-render del contents SIN recargar del backend (la fija wireContents). */
  _rerenderContents = null;
  /** WEAL-322: grupos abiertos de un `expanview` de página (sobreviven a re-renders). */
  _expandOpenLocal = /* @__PURE__ */ new Set();
  /** Evita doble wiring del click de selecteditem en re-cargas. */
  _selectedItemWired = false;
  /**
   * WEAL-150: fila actualmente resaltada por selección de usuario
   * (`cell-selected-bgcolor`). Se limpia su `background-color` inline al
   * seleccionar otra fila para mantener selección única (paridad Android
   * MainListCollItem.applyFormatToCell → `item.isSelected()`).
   */
  _selectedRowEl;
  /**
   * WEAL-622: índice de la fila seleccionada por script
   * (`getControl(name).setSelectedItem(pos)`). Se re-aplica tras cada
   * re-render/recarga del contents para que un `ui.refresh` posterior no
   * pierda el sombreado. `null` = sin selección por script.
   */
  _scriptSelectedIndex = null;
  /** Evita doble wiring del sync de scroll de la cabecera (header-contents). */
  _headerSyncWired = false;
  /** Evita doble wiring del ResizeObserver del ancho de tabla (header-contents). */
  _hcResizeWired = false;
  /** WEAL-125: evita doble wiring de los observers de `data-overflowing`. */
  _ovfWired = false;
  // ── WEAL-142 — estado de paginación fluida (fluid-load / start-from-bottom) ──
  /** Carga la página anterior (más antigua) y la prepende (la fija wireContents). */
  _loadOlderRows = null;
  /** Scroll al fondo del contenedor de scroll del contents (la fija wireContents). */
  _scrollToBottom = null;
  /** Registra/limpia el listener de reach-top (la fija wireContents). */
  _setOnReachTop = null;
  /**
   * WEAL-142 — API pública consumida por `@xone-web/app-orchestrator` (ui):
   * `ui.loadContentsOlderRows(name)` → `loadOlderRows()`. Carga la página
   * anterior (mensajes más antiguos de un chat fluid-load) y la prepende.
   */
  async loadOlderRows() {
    if (this._loadOlderRows) await this._loadOlderRows();
  }
  /** WEAL-142 — `control.scrollToBottom()` (chat/start-from-bottom). */
  scrollToBottom() {
    if (this._scrollToBottom) this._scrollToBottom();
  }
  /** WEAL-142 — `ui.setContentsOnReachTop(name, cb)`. `null` desregistra. */
  setOnReachTop(fn) {
    if (this._setOnReachTop) this._setOnReachTop(fn);
  }
  /**
   * Refresca el control: para un island de contents re-lanza la carga
   * (respetando el filtro runtime de la colección). Entry point de
   * `ui.getView(self).getControl(name).refresh()`.
   */
  refresh() {
    if (this._reloadContents) {
      this._reloadContents();
    }
  }
  /**
   * WEAL-150/622: pinta `row` como fila seleccionada con el color
   * `cell-selected-bgcolor` y despinta la previa (selección única).
   */
  paintSelectedRow(row, selectedBgColor) {
    const prev = this._selectedRowEl;
    if (prev && prev !== row) {
      prev.style.backgroundColor = "";
      prev.removeAttribute("data-selected");
    }
    row.style.backgroundColor = xoneColorToCss(selectedBgColor);
    row.setAttribute("data-selected", "true");
    this._selectedRowEl = row;
  }
  /** Despinta la fila seleccionada (si la hay). */
  unpaintSelectedRow() {
    const prev = this._selectedRowEl;
    if (prev) {
      prev.style.backgroundColor = "";
      prev.removeAttribute("data-selected");
    }
    this._selectedRowEl = void 0;
  }
  /** Fila `.xone-contents-row` de ESTE island (no de islands anidados). */
  findOwnContentsRow(position) {
    const rows = this.querySelectorAll(
      `.xone-contents-row[data-row-index="${position}"]`
    );
    for (const r of Array.from(rows)) {
      if (r.parentElement?.closest("xone-island") === this) return r;
    }
    return null;
  }
  /**
   * WEAL-622 — paridad Android `XOneContentRecyclerView.setSelectedItem(int)`
   * (`@ScriptAllowed`): marca como seleccionada la fila `position` del
   * contents con el color `cell-selected-bgcolor`. Como en Android, NO
   * dispara `<selecteditem>`: solo cambia la selección visual. Posición fuera
   * de rango o contents sin `cell-selected-bgcolor` → no hace nada.
   */
  setSelectedItem(position) {
    const pos = Number(position);
    if (!Number.isInteger(pos) || pos < 0) return;
    const row = this.findOwnContentsRow(pos);
    if (!row) return;
    const color = row.closest("[data-cell-selected-bgcolor]")?.getAttribute("data-cell-selected-bgcolor") ?? "";
    if (color === "") return;
    this._scriptSelectedIndex = pos;
    this.paintSelectedRow(row, color);
  }
  /** WEAL-622 — paridad Android `clearSelectedItem()`: quita la selección. */
  clearSelectedItem() {
    this._scriptSelectedIndex = null;
    this.unpaintSelectedRow();
  }
  /** Re-aplica la selección por script tras re-pintar las filas. */
  reapplyScriptSelection() {
    const idx = this._scriptSelectedIndex;
    if (idx === null) return;
    this._selectedRowEl = void 0;
    const row = this.findOwnContentsRow(idx);
    if (!row) return;
    const color = row.closest("[data-cell-selected-bgcolor]")?.getAttribute("data-cell-selected-bgcolor") ?? "";
    if (color === "") return;
    this.paintSelectedRow(row, color);
  }
  /**
   * Re-renderiza el contents desde los items YA cargados en la colección
   * compartida, SIN recargar del backend. Entry point de `ui.refresh(name)`
   * (paridad Android: `Refresh(control)` re-lee la fuente en memoria, no toca
   * la BD; la carga es un paso aparte). El proyecto siempre hace `loadAll`
   * antes (`actualizarDatosContent`), así que los datos están disponibles.
   * Fallback a `refresh()` (reload) si el island aún no cableó el re-render.
   */
  rerender() {
    if (this._rerenderContents) {
      this._rerenderContents();
    } else {
      this.refresh();
    }
  }
  connectedCallback() {
    if (this[HYDRATED]) return;
    const lazy = this.getAttribute("lazy") ?? "interaction";
    if (lazy === "eager") {
      void this.hydrate();
      return;
    }
    if (lazy === "visible") {
      this.observeVisible();
      return;
    }
    this.observeFirstInteraction();
  }
  disconnectedCallback() {
    for (const fn of this.cleanup) {
      try {
        fn();
      } catch {
      }
    }
    this.cleanup = [];
  }
  observeFirstInteraction() {
    const evts = [
      "pointerdown",
      "mousedown",
      "focus",
      "click",
      "input",
      "change",
      "keydown"
    ];
    const onTrigger = () => {
      void this.hydrate();
      for (const e of evts) this.removeEventListener(e, onTrigger, true);
    };
    for (const e of evts) {
      this.addEventListener(e, onTrigger, { capture: true });
    }
  }
  observeVisible() {
    if (typeof IntersectionObserver === "undefined") {
      void this.hydrate();
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            obs.disconnect();
            void this.hydrate();
            return;
          }
        }
      },
      { rootMargin: "100px" }
    );
    obs.observe(this);
    this.cleanup.push(() => obs.disconnect());
  }
  async hydrate() {
    if (this[HYDRATED]) return;
    const runtime = window[RUNTIME_KEY];
    if (!runtime) {
      return;
    }
    try {
      if ((this.getAttribute("data-type") ?? "").toLowerCase() === "widget") {
        await this.mountWidget(runtime);
      } else {
        this.wireListeners(runtime);
      }
      this[HYDRATED] = true;
      this.setAttribute("hydrated", "");
    } catch (err) {
      try {
        runtime.api.console.error("[xone-island] hydrate failed", err);
      } catch {
      }
    }
  }
  /**
   * Fuerza la hidratación on-demand (R2): usado al activar una pestaña para
   * montar los islands lazy que estaban en un panel `hidden` (display:none),
   * donde el IntersectionObserver (`visible`) y la interacción (`interaction`)
   * nunca disparan. Idempotente.
   */
  hydrateNow() {
    void this.hydrate();
  }
  /**
   * Monta un widget externo declarado con `web-widget` (data-type="widget").
   * Carga el script externo (deduplicado), crea el custom element, resuelve
   * las macros de sus atributos contra el runtime y lo inserta en el host.
   * El DOM lo manipula el RUNTIME (no el código de proyecto, que sigue sin
   * tocar `document`). Ver docs/xone-web-platform/03-web-widget-embedding.md.
   */
  async mountWidget(runtime, attempt = 0) {
    const tag = this.getAttribute("data-widget-tag");
    if (!tag) return;
    const host = this.querySelector(".xone-widget-host");
    if (!host || host.childElementCount > 0 || this._widgetMounting) return;
    const src = resolveWidgetMacros(runtime, this.getAttribute("data-widget-src") ?? "");
    let attrs = {};
    try {
      const raw = this.getAttribute("data-widget-attrs");
      attrs = raw ? JSON.parse(raw) : {};
    } catch {
    }
    const resolved = [];
    for (const [name, value] of Object.entries(attrs)) {
      resolved.push([name, resolveWidgetMacros(runtime, String(value))]);
    }
    const stillPending = WIDGET_UNRESOLVED_MACRO.test(src) || resolved.some(([, v]) => WIDGET_UNRESOLVED_MACRO.test(v));
    if (stillPending) {
      this.listenWidgetMacros(runtime);
      if (attempt >= 0 && attempt < WIDGET_MACRO_POLL_MAX) {
        const t = window.setTimeout(() => {
          void this.mountWidget(runtime, attempt + 1);
        }, WIDGET_MACRO_POLL_MS);
        this.cleanup.push(() => window.clearTimeout(t));
      } else if (attempt === WIDGET_MACRO_POLL_MAX) {
        try {
          console.warn(`[xone-island] widget <${tag}> no montado: macros sin resolver`);
        } catch {
        }
      }
      return;
    }
    this.unlistenWidgetMacros();
    this._widgetMounting = true;
    try {
      if (src) {
        try {
          await loadExternalScriptOnce(src);
        } catch {
        }
      }
      if (host.childElementCount > 0) return;
      const el = document.createElement(tag);
      for (const [name, value] of resolved) {
        try {
          el.setAttribute(name, value);
        } catch {
        }
      }
      host.appendChild(el);
      const shadowCss = resolveWidgetMacros(runtime, this.getAttribute("data-widget-shadow-css") ?? "");
      if (shadowCss.trim()) {
        this.cleanup.push(applyWidgetShadowCss(el, shadowCss));
      }
    } finally {
      this._widgetMounting = false;
    }
  }
  /** WEAL-422: true mientras un montaje de widget espera al script externo. */
  _widgetMounting = false;
  /** WEAL-422: listener de `xone:global-macro` mientras el widget espera macros. */
  _widgetMacroListener = null;
  listenWidgetMacros(runtime) {
    if (this._widgetMacroListener) return;
    const fn = () => {
      void this.mountWidget(runtime, -1);
    };
    this._widgetMacroListener = fn;
    window.addEventListener(GLOBAL_MACRO_EVENT, fn);
    this.cleanup.push(() => this.unlistenWidgetMacros());
  }
  unlistenWidgetMacros() {
    const fn = this._widgetMacroListener;
    if (!fn) return;
    this._widgetMacroListener = null;
    window.removeEventListener(GLOBAL_MACRO_EVENT, fn);
  }
  wireGroupTabs(runtime) {
    const collName = this.getAttribute("data-coll") ?? "";
    const storageKey = `xone-active-group:${collName}`;
    const hooks = {
      fireGroupEvent: (eventName, _groupId, groupName) => {
        if (groupName === "") return;
        const handler = runtime.events[`${groupName}_${eventName}`];
        if (typeof handler === "function") {
          void invokeHandler(runtime, handler, []);
        }
      },
      canLeaveGroup: () => {
        return true;
      },
      persistActiveGroup: (groupId) => {
        try {
          window.sessionStorage?.setItem(storageKey, groupId);
        } catch {
        }
      },
      readActiveGroup: () => {
        try {
          return window.sessionStorage?.getItem(storageKey) ?? null;
        } catch {
          return null;
        }
      },
      // WEAL-95: la clave es por COLECCIÓN (no por objeto) — en entradas
      // que no son "back" se limpia para no heredar la pestaña de otro
      // registro (paridad Android: entrada nueva = grupo por defecto).
      clearActiveGroup: () => {
        try {
          window.sessionStorage?.removeItem(storageKey);
        } catch {
        }
      },
      // WEAL-95: `true` solo si ESTA carga de página vino de una
      // navegación "atrás" (ui.back/onback). El AppContext expone la
      // dirección consumida de `xone:navDir` en `api.navDirection`.
      isBackNavigation: () => runtime.api.navDirection === "back",
      // WEAL-95: gate del restore manual — el before-edit del proyecto
      // re-inicializa el campo discriminador y pisaría el restore.
      awaitLifecycleReady: () => {
        const lr = runtime.api.lifecycleReady;
        return lr && typeof lr.then === "function" ? lr.then(() => void 0) : Promise.resolve();
      },
      hydratePanel: (panel) => {
        const islands = panel.querySelectorAll(ELEMENT_TAG);
        for (const el of islands) {
          if (el instanceof _XoneIslandElement) el.hydrateNow();
        }
      }
    };
    const cleanup = wireGroupTabs(this, hooks);
    this.cleanup.push(cleanup);
  }
  wireDrawer(runtime) {
    const hooks = {
      fireGroupEvent: (eventName, groupName, _detail) => {
        if (groupName === "") return;
        const handler = runtime.events[`${groupName}_${eventName}`];
        if (typeof handler === "function") {
          void invokeHandler(runtime, handler, []);
        }
      }
    };
    const cleanup = wireDrawer(this, hooks);
    this.cleanup.push(cleanup);
  }
  wireListeners(runtime) {
    const eventsSpec = (this.getAttribute("data-events") ?? "").split(",").map((s) => s.trim()).filter((s) => s !== "");
    const propName = this.getAttribute("data-prop") ?? "";
    const propType = (this.getAttribute("data-type") ?? "").toUpperCase();
    for (const spec of eventsSpec) {
      if (spec === "bind") {
        this.wireBind(runtime, propName, propType);
      } else if (spec === "click") {
        this.wireClickInline(runtime, propName);
      } else if (spec === "frame-click") {
        this.wireFrameClick(runtime, propName);
      } else if (spec === "change") {
        this.wireChangeInline(runtime, propName);
      } else if (spec === "contents") {
        this.wireContents(runtime, propName);
      } else if (spec === "group-tabs") {
        this.wireGroupTabs(runtime);
      } else if (spec === "drawer") {
        this.wireDrawer(runtime);
      } else if (spec.startsWith("method:")) {
        const methodName = spec.slice("method:".length);
        this.wireMethod(runtime, propName, methodName);
      }
    }
  }
  /**
   * Resuelve el `api` para el handler de un control que puede vivir en una
   * FILA de un contents. Si el control está dentro de `.xone-contents-row`, el
   * `self` del handler es el OBJETO DE LA FILA (`getCollection(data-src del
   * island host Z).get(data-row-index)`) — p.ej. el botón Móvil de un lead
   * ejecuta `llamarLead` con `self`=ese lead (no el `self` de la página).
   * Fuera de una fila devuelve el `api` de la página sin cambios.
   */
  resolveRowApi(runtime) {
    const row = this.closest(".xone-contents-row");
    if (!row) return runtime.api;
    const host = this.closest('xone-island[data-type="Z"]');
    const src = host?.getAttribute("data-src") ?? "";
    const idx = Number(row.getAttribute("data-row-index") ?? "0");
    const coll = src ? resolveContentsColl(
      runtime.api.appData,
      src,
      host?.getAttribute("data-contents-name")
    ) : null;
    const item = expandRowItem(row) ?? coll?.get?.(idx);
    if (item && typeof item === "object") {
      return { ...runtime.api, self: item, This: item };
    }
    const stamped = getRowItem(row);
    if (stamped && typeof stamped === "object") {
      return { ...runtime.api, self: stamped, This: stamped };
    }
    return runtime.api;
  }
  /**
   * WEAL-107: runtime cuyo `self`/`This` es el OBJETO DE LA FILA CONTENEDORA
   * cuando ESTE island (un `<contents>`) está anidado dentro de la fila de otro
   * contents. A diferencia de `resolveRowApi` (pensada para controles hoja, que
   * usa `this.closest(...)` y por tanto se encontraría a sí misma si `this` es un
   * Z), aquí buscamos el island host de la fila PARTIENDO DE LA FILA
   * (`row.closest`), excluyendo así el propio Z. Para islands de nivel de página
   * (sin fila contenedora) devuelve el runtime original sin cambios.
   */
  parentRowScopedRuntime(runtime) {
    const item = this.parentRowSelf(runtime);
    return item && typeof item === "object" ? { ...runtime, api: { ...runtime.api, self: item, This: item } } : runtime;
  }
  /**
   * WEAL-107: el OBJETO de la fila contenedora (la actividad hija) cuando ESTE
   * island (un `<contents>`) está anidado dentro de la fila de otro contents.
   * Devuelve `undefined` para islands de nivel de página (sin fila contenedora).
   */
  parentRowSelf(runtime) {
    const row = this.closest(".xone-contents-row");
    if (!row) return void 0;
    const host = row.closest('xone-island[data-type="Z"]');
    const src = host?.getAttribute("data-src") ?? "";
    if (!src) return void 0;
    const idx = Number(row.getAttribute("data-row-index") ?? "0");
    const coll = resolveContentsColl(
      runtime.api.appData,
      src,
      host?.getAttribute("data-contents-name")
    );
    const item = expandRowItem(row) ?? coll?.get?.(idx);
    if (item && typeof item === "object") return item;
    const stamped = getRowItem(row);
    return stamped && typeof stamped === "object" ? stamped : void 0;
  }
  wireBind(runtime, propName, propType) {
    if (propName === "") return;
    const input = this.querySelector(
      "input,textarea,select"
    );
    if (!input) return;
    const resolveBindTarget = () => {
      const row = this.closest(".xone-contents-row");
      if (!row) return runtime.api.self;
      const host = this.closest('xone-island[data-type="Z"]');
      const src = host?.getAttribute("data-src") ?? "";
      const idx = Number(row.getAttribute("data-row-index") ?? "0");
      const coll = src ? resolveContentsColl(
        runtime.api.appData,
        src,
        host?.getAttribute("data-contents-name")
      ) : null;
      const item = expandRowItem(row) ?? coll?.get?.(idx);
      return item && typeof item === "object" ? item : runtime.api.self;
    };
    const hashType = this.getAttribute("data-hash-type");
    const hashEncode = this.getAttribute("data-encode");
    const hashField = (hashType ?? "").trim() !== "";
    const commitValue = (raw) => hashField && typeof raw === "string" ? transformFieldValue(raw, hashType, hashEncode) : raw;
    const readCommittedValue = () => {
      if (propType === "NC" && "checked" in input) {
        return input.checked ? 1 : 0;
      }
      if (propType === "D") {
        return input.value === "" ? null : input.valueAsDate;
      }
      if (propType === "DT") {
        return _XoneIslandElement.datetimeLocalToDate(input.value);
      }
      if (propType !== "NC" && NUMERIC_FIELD_TYPES.has(propType)) {
        try {
          return fromStorage(input.value, propType);
        } catch {
          return commitValue(input.value);
        }
      }
      return commitValue(input.value);
    };
    let initial = void 0;
    try {
      initial = resolveBindTarget().getValue?.(propName);
    } catch {
    }
    if (initial !== void 0 && initial !== null) {
      if (propType === "NC" && "checked" in input) {
        input.checked = Boolean(initial) && initial !== "0";
      } else if (!hashField) {
        input.value = _XoneIslandElement.modelDateToInputValue(input, initial);
      }
    }
    const uncheckRadioSiblings = () => {
      if (propType !== "NC") return;
      if (!(input instanceof HTMLInputElement) || input.type !== "radio") return;
      if (!input.checked) return;
      const group = input.getAttribute("data-radio-group") ?? "";
      if (group === "") return;
      const global = input.getAttribute("data-global-radio-group") === "true";
      const myRow = this.closest(".xone-contents-row");
      const myColl = this.getAttribute("data-coll") ?? "";
      const root = global ? document : myRow ?? document;
      const esc = typeof CSS !== "undefined" && typeof CSS.escape === "function" ? CSS.escape(group) : group.replace(/["\\]/g, "\\$&");
      const candidates = root.querySelectorAll(
        `input[data-xone-type="NC"][data-radio-group="${esc}"]`
      );
      const target = resolveBindTarget();
      for (const sib of Array.from(candidates)) {
        if (sib === input) continue;
        const sibProp = sib.name || "";
        if (sibProp === "" || sibProp === propName) continue;
        if (!global) {
          const sibRow = sib.closest(".xone-contents-row");
          if (sibRow !== myRow) continue;
          const sibIsland = sib.closest("xone-island");
          const sibColl = sibIsland?.getAttribute("data-coll") ?? "";
          if (sibColl !== myColl) continue;
        }
        let modelOn = false;
        try {
          const cur = target.getValue?.(sibProp);
          modelOn = cur !== void 0 && cur !== null && cur !== 0 && cur !== "0" && cur !== "" && cur !== false;
        } catch {
        }
        const domOn = sib.checked;
        if (!modelOn && !domOn) continue;
        sib.checked = false;
        if (modelOn) {
          try {
            void target.setValue?.(sibProp, 0);
          } catch (err) {
            runtime.api.console.error("[xone-island] radio-group setValue", err);
          }
        }
        if (!myRow) {
          const domSync = runtime.api.domSync;
          try {
            domSync?.syncConditionalsForField?.(sibProp);
          } catch {
          }
        }
      }
    };
    const onInput = () => {
      uncheckRadioSiblings();
      const v = readCommittedValue();
      try {
        void resolveBindTarget().setValue?.(propName, v);
      } catch (err) {
        runtime.api.console.error("[xone-island] bind setValue", err);
      }
      const onchangeColl = this.getAttribute("data-onchange-coll");
      if (onchangeColl) {
        const key = `${propName}_onchange`;
        const pageColl = this.getAttribute("data-coll") ?? "";
        const pageHandler = (pageColl ? runtime.eventsByColl?.[pageColl]?.[key] : void 0) ?? runtime.events?.[key];
        if (typeof pageHandler !== "function") {
          const headerHandler = runtime.eventsByColl?.[onchangeColl]?.[key];
          if (typeof headerHandler === "function") {
            const pageSelf = runtime.api.self;
            const headerSelf = new Proxy(pageSelf, {
              get(target, prop, receiver) {
                if (prop === "getParent") return () => pageSelf;
                const val = Reflect.get(target, prop, receiver);
                return typeof val === "function" ? val.bind(target) : val;
              }
            });
            void invokeHandler(
              runtime,
              headerHandler,
              [v],
              { ...runtime.api, self: headerSelf, This: headerSelf }
            );
          }
        }
      }
      if (!this.closest("[data-row-index]") && !this.closest(".xone-contents-row")) {
        const domSync = runtime.api.domSync;
        try {
          domSync?.syncConditionalsForField?.(propName);
        } catch {
        }
      }
    };
    const ev = propType === "NC" ? "change" : "input";
    input.addEventListener(ev, onInput);
    this.cleanup.push(() => input.removeEventListener(ev, onInput));
    if (input instanceof HTMLInputElement && propType !== "NC" && NUMERIC_INPUT_MASK_TYPES.has(propType)) {
      const numInput = input;
      const onBeforeInput = (e) => {
        const ie = e;
        const it = ie.inputType || "";
        if (!it.startsWith("insert")) return;
        const data = ie.data;
        if (data == null || data === "") return;
        const start = numInput.selectionStart ?? numInput.value.length;
        const end = numInput.selectionEnd ?? numInput.value.length;
        const norm = maskNumericInsert(numInput.value, start, end, data);
        if (norm === null) {
          e.preventDefault();
          return;
        }
        if (norm !== data) {
          e.preventDefault();
          try {
            numInput.setRangeText(norm, start, end, "end");
          } catch {
            numInput.value = numInput.value.slice(0, start) + norm + numInput.value.slice(end);
          }
          numInput.dispatchEvent(new Event("input", { bubbles: true }));
        }
      };
      input.addEventListener("beforeinput", onBeforeInput);
      this.cleanup.push(
        () => input.removeEventListener("beforeinput", onBeforeInput)
      );
    }
    const oea = this.getAttribute("data-oneditoraction");
    if (oea && oea.trim() !== "" && propType !== "NC") {
      const isTextarea = input.tagName === "TEXTAREA";
      const onKeydown = (e) => {
        if (e.key !== "Enter") return;
        if (isTextarea && e.shiftKey) return;
        if (isTextarea) e.preventDefault();
        const target = resolveBindTarget();
        try {
          void target.setValue?.(propName, readCommittedValue());
        } catch {
        }
        execInlineAction(oea, target, (err) => {
          try {
            runtime.api.console.error("[xone-island] oneditoraction", err);
          } catch {
          }
        });
      };
      input.addEventListener("keydown", onKeydown);
      this.cleanup.push(() => input.removeEventListener("keydown", onKeydown));
    }
    const otc = this.getAttribute("data-ontextchanged");
    const otcEl = input;
    if (otc && otc.trim() !== "" && propType !== "NC" && (otcEl instanceof HTMLInputElement || otcEl instanceof HTMLTextAreaElement)) {
      const textInput = otcEl;
      let lastText = textInput.value;
      const onInput2 = (ev2) => {
        const newText = textInput.value;
        if (newText === lastText) return;
        const oldText = lastText;
        lastText = newText;
        const data = ev2.data;
        const e = {
          target: propName,
          keyPressed: typeof data === "string" ? data : "",
          oldText,
          newText,
          oldLength: oldText.length,
          newLength: newText.length
        };
        execInlineAction(otc, resolveBindTarget(), (err) => {
          try {
            runtime.api.console.error("[xone-island] ontextchanged", err);
          } catch {
          }
        }, { e });
      };
      textInput.addEventListener("input", onInput2);
      this.cleanup.push(() => textInput.removeEventListener("input", onInput2));
    }
  }
  /**
   * Resuelve un handler de evento: primero en el módulo de la COLECCIÓN del
   * island (`data-coll` → `runtime.eventsByColl`), después en el módulo de la
   * PÁGINA (`runtime.events`) como fallback. Los islands anidados en filas de
   * un contents llevan `data-coll` de la coll HIJA, cuyos handlers sintéticos
   * (onclick/onchange/selecteditem) tienen el cuerpo row-scoped (paridad
   * Android: los eventos de las props de fila pertenecen a la coll del
   * contents, no a la página host).
   *
   * El orden importa cuando la PÁGINA y la coll hija embebida declaran una
   * prop HOMÓNIMA (mismo nombre → mismo evento sintético). P.ej. `MAP_BT_COPIAR`
   * existe en la página `ClienteEdicion` (`copiarPortapapeles(self.MAP_CORREO)`)
   * y en la coll hija `ClienteVinculo` (`copiarPortapapeles(self.MAP_PAREJA)`).
   * Si se consultara la página primero, su handler SOMBREARÍA al de la fila y,
   * ejecutado sobre el self de la fila (que no tiene `MAP_CORREO`), pasaría
   * `undefined` a `copiarPortapapeles` (WEAL-26). Preferir la coll del island
   * lo evita; los controles de PÁGINA no tienen entrada en `eventsByColl`
   * (su módulo vive en `runtime.events`), así que caen al fallback → mismo
   * resultado que antes, sin regresión.
   */
  resolveHandler(runtime, key) {
    const collName = this.getAttribute("data-coll") ?? "";
    const mod = collName !== "" ? runtime.eventsByColl?.[collName] : void 0;
    const fromColl = mod?.[key];
    if (typeof fromColl === "function") return fromColl;
    const fromPage = runtime.events[key];
    return typeof fromPage === "function" ? fromPage : void 0;
  }
  wireClickInline(runtime, propName) {
    const handlerName = `${propName}_onclick`;
    const handler = this.resolveHandler(runtime, handlerName);
    if (typeof handler !== "function") {
      return;
    }
    const button = this.querySelector("button");
    const target = button ?? this;
    let isRunning = false;
    const onClick = (e) => {
      if (isRunning) return;
      isRunning = true;
      const p = runInteractiveHandler(runtime, () => {
        if (this.closest("[data-row-index]")) markButtonCommit(this);
        return invokeHandler(runtime, handler, [e], this.resolveRowApi(runtime));
      });
      void p.finally(() => {
        isRunning = false;
      });
    };
    target.addEventListener("click", onClick);
    this.cleanup.push(() => target.removeEventListener("click", onClick));
  }
  wireFrameClick(runtime, frameName) {
    const handlerName = `${frameName}_onclick`;
    const handler = this.resolveHandler(runtime, handlerName);
    if (typeof handler !== "function") return;
    const frame = this.querySelector(`[data-xone-frame="${frameName}"]`) ?? this;
    frame.style.cursor = "pointer";
    let isRunning = false;
    const onClick = (e) => {
      e.stopPropagation();
      if (isRunning) return;
      isRunning = true;
      const p = runInteractiveHandler(
        runtime,
        () => invokeHandler(runtime, handler, [e])
      );
      trackNodePromise(p);
      void p.finally(() => {
        isRunning = false;
      });
    };
    frame.addEventListener("click", onClick);
    this.cleanup.push(() => frame.removeEventListener("click", onClick));
  }
  wireChangeInline(runtime, propName) {
    const handlerName = `${propName}_onchange`;
    const handler = this.resolveHandler(runtime, handlerName);
    if (typeof handler !== "function") return;
    const input = this.querySelector("input,textarea,select");
    if (!input) return;
    const onChange = () => {
      void invokeHandler(runtime, handler, [input.value], this.resolveRowApi(runtime));
    };
    input.addEventListener("change", onChange);
    this.cleanup.push(() => input.removeEventListener("change", onChange));
  }
  wireMethod(runtime, propName, methodName) {
    const handler = this.resolveHandler(runtime, methodName);
    const gotoGroup = this.getAttribute("data-goto-group");
    if (typeof handler !== "function" && gotoGroup === null) return;
    let methodArgs = [];
    const rawArgs = this.getAttribute("data-method-args");
    if (rawArgs) {
      try {
        const parsed = JSON.parse(rawArgs);
        if (Array.isArray(parsed)) methodArgs = parsed;
      } catch {
      }
    }
    const button = this.querySelector("button");
    const target = button ?? this;
    let isRunning = false;
    const onClick = () => {
      if (typeof handler === "function") {
        if (isRunning) return;
        const rowApi = this.resolveRowApi(runtime);
        const resolvedArgs = methodArgs.map(
          (a) => typeof a === "string" && a.includes("##") ? resolveWidgetMacrosWithApi(rowApi, a) : a
        );
        isRunning = true;
        const nodePromise = runInteractiveHandler(
          runtime,
          () => invokeHandler(runtime, handler, resolvedArgs, rowApi)
        );
        trackNodePromise(nodePromise);
        void nodePromise.finally(async () => {
          try {
            await whenWritesIdle();
          } catch {
          }
          isRunning = false;
        });
      }
      if (gotoGroup !== null) {
        try {
          runtime.api.ui.showGroup?.(gotoGroup);
        } catch {
        }
      }
    };
    target.addEventListener("click", onClick);
    this.cleanup.push(() => target.removeEventListener("click", onClick));
    void propName;
  }
  wireContents(runtime, propName) {
    let srcColl = this.getAttribute("data-src") ?? "";
    let filter = this.getAttribute("data-filter") ?? void 0;
    let sort = this.getAttribute("data-sort") ?? void 0;
    const innerDiv = this.querySelector("div[data-src]");
    if (!srcColl && innerDiv) {
      srcColl = innerDiv.getAttribute("data-src") ?? "";
      filter = filter ?? innerDiv.getAttribute("data-filter") ?? void 0;
      sort = sort ?? innerDiv.getAttribute("data-sort") ?? void 0;
    }
    if (!srcColl) {
      this.setAttribute("data-status", "no-src");
      return;
    }
    const contentsInstanceName = this.getAttribute("data-contents-name") ?? innerDiv?.getAttribute("data-contents-name") ?? "";
    const contentsColl = () => resolveContentsColl(runtime.api.appData, srcColl, contentsInstanceName);
    const container = innerDiv ?? this;
    const rppAttr = parseInt(container.getAttribute("data-rows-per-page") ?? "", 10);
    const rowsPerPage = Number.isFinite(rppAttr) && rppAttr > 0 ? rppAttr : null;
    const fluidLoad = container.getAttribute("data-fluid-load") === "true";
    const startFromBottom = container.getAttribute("data-start-from-bottom") === "true";
    const FLUID_LOAD_PAGE_SIZE = 50;
    const fluidPageSize = rowsPerPage ?? (fluidLoad ? FLUID_LOAD_PAGE_SIZE : null);
    let fluidOffset = 0;
    const templateEl = container.querySelector(
      "template[data-item-template]"
    );
    const templateHtml = templateEl?.innerHTML ?? "";
    let viewmode = (container.getAttribute("data-viewmode") ?? "recyclerview").toLowerCase();
    const contentsName = this.getAttribute("data-contents-name") ?? propName;
    const bareContentsName = contentsName.replace(/^@/, "");
    if (bareContentsName) {
      const scope = this.closest("[data-xone-frame]") ?? this.parentElement;
      const siblingIsland = scope?.querySelector(
        `xone-island[data-prop="${bareContentsName}"]`
      );
      const siblingVm = siblingIsland?.querySelector("[data-viewmode]");
      if (siblingIsland && siblingIsland !== this && siblingVm) {
        const sv = siblingVm.getAttribute("data-viewmode")?.toLowerCase();
        if (sv) viewmode = sv;
        for (const attr of ["data-columns", "data-gallery-explicit", "data-orientation", "data-cell-height", "data-bgcolor", "data-chart-type", "data-chart-config", "data-cell-even-color", "data-cell-odd-color", "data-cell-selected-bgcolor"]) {
          const v = siblingVm.getAttribute(attr);
          if (v !== null) container.setAttribute(attr, v);
        }
        container.setAttribute("data-viewmode", viewmode);
        const box = siblingIsland.style;
        if (box.width && !this.style.width) this.style.width = box.width;
        if (box.height && !this.style.height) this.style.height = box.height;
        if (box.flexShrink && !this.style.flexShrink) this.style.flexShrink = box.flexShrink;
        if (box.minHeight && !this.style.minHeight) this.style.minHeight = box.minHeight;
        if (!this.style.width) this.style.width = "100%";
        siblingIsland.style.display = "none";
      }
    }
    const columns = parseInt(container.getAttribute("data-columns") ?? "2", 10);
    const orientation = (container.getAttribute("data-orientation") ?? "vertical").toLowerCase();
    const galleryExplicit = container.getAttribute("data-gallery-explicit") === "true";
    const cellHeight = container.getAttribute("data-cell-height") ?? "";
    const showNoData = container.getAttribute("data-show-no-data") ?? "true";
    const bgcolor = container.getAttribute("data-bgcolor") ?? "";
    this.setAttribute("data-status", "loading");
    const macroRuntime = this.parentRowScopedRuntime(runtime);
    const resolvedFilter = this.resolveMacros(macroRuntime, filter);
    const renderFromColl = (coll) => {
      const count = coll.getCount?.() ?? 0;
      const prevScrollLeft = container.scrollLeft;
      const prevScrollTop = container.scrollTop;
      const restoreScroll = () => {
        const maxLeft = Math.max(0, container.scrollWidth - container.clientWidth);
        const maxTop = Math.max(0, container.scrollHeight - container.clientHeight);
        if (prevScrollLeft > 0) container.scrollLeft = Math.min(prevScrollLeft, maxLeft);
        if (prevScrollTop > 0) container.scrollTop = Math.min(prevScrollTop, maxTop);
      };
      let focusToRestore = null;
      const activeEl = typeof document !== "undefined" ? document.activeElement : null;
      if (activeEl && container.contains(activeEl) && (activeEl.tagName === "INPUT" || activeEl.tagName === "TEXTAREA" || activeEl.tagName === "SELECT")) {
        const rowEl = activeEl.closest("[data-row-index]");
        const propEl = activeEl.closest("[data-prop]");
        const field = propEl?.getAttribute("data-prop") ?? activeEl.getAttribute("name") ?? activeEl.getAttribute("data-prop");
        if (rowEl && field) {
          let selStart = null;
          let selEnd = null;
          let rawValue = null;
          if (activeEl instanceof HTMLInputElement || activeEl instanceof HTMLTextAreaElement) {
            try {
              selStart = activeEl.selectionStart;
              selEnd = activeEl.selectionEnd;
            } catch {
            }
            if (activeEl instanceof HTMLInputElement && activeEl.getAttribute("inputmode") === "decimal") {
              rawValue = activeEl.value;
            }
          }
          focusToRestore = {
            rowIndex: rowEl.getAttribute("data-row-index") ?? "",
            rowKey: rowEl.getAttribute("data-row-key"),
            field,
            selStart,
            selEnd,
            rawValue
          };
        }
      }
      const restoreFocus = () => {
        if (!focusToRestore) return;
        const key = focusToRestore.rowKey;
        const rowEl = key !== null ? Array.from(container.querySelectorAll("[data-row-key]")).find((el) => el.getAttribute("data-row-key") === key) ?? null : container.querySelector(
          `[data-row-index="${focusToRestore.rowIndex}"]`
        );
        if (!rowEl) return;
        const input = rowEl.querySelector(
          `[data-prop="${focusToRestore.field}"] input, [data-prop="${focusToRestore.field}"] textarea, select[data-prop="${focusToRestore.field}"]`
        );
        if (!input) return;
        try {
          input.focus();
          if (focusToRestore.rawValue !== null && input instanceof HTMLInputElement && input.value !== focusToRestore.rawValue) {
            input.value = focusToRestore.rawValue;
          }
          if ((input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement) && focusToRestore.selStart !== null && focusToRestore.selEnd !== null) {
            try {
              input.setSelectionRange(focusToRestore.selStart, focusToRestore.selEnd);
            } catch {
            }
          }
        } catch {
        }
      };
      container.innerHTML = "";
      this.applyContentsLayout(container, viewmode, columns, orientation, bgcolor, galleryExplicit);
      if (startFromBottom) container.classList.add("xone-reverse-column");
      if (count === 0) {
        if (showNoData.toLowerCase() !== "false") {
          const empty = document.createElement("div");
          empty.className = "xone-contents-empty";
          empty.textContent = "No hay datos";
          container.appendChild(empty);
        }
        this.setAttribute("data-status", "empty");
        return;
      }
      const items = this.collectItems(coll, count);
      const chartRaw = container.getAttribute("data-chart-config");
      if (chartRaw) {
        try {
          const cfg = JSON.parse(chartRaw);
          renderChart(container, cfg, items);
          this.setAttribute("data-status", "loaded");
          return;
        } catch (e) {
          runtime.api.console.error("[xone-island] chart render failed", e);
        }
      }
      const isExpandView = viewmode === "expanview" || viewmode === "expandview";
      if (isExpandView) {
        this.renderExpandView(runtime, container, templateHtml, items, cellHeight);
      } else {
        for (let i = 0; i < items.length; i++) {
          this.appendContentRow(container, templateHtml, items[i], i, cellHeight);
        }
      }
      this.setAttribute("data-status", "loaded");
      this.wireSelectedItem(runtime, propName, container);
      this.applySelectableCursor(runtime, propName, container);
      this.reapplyScriptSelection();
      this.applyHeaderedTableWidths(container);
      restoreScroll();
      this.wireHeaderScrollSync(container);
      this.updateOverflowState(container);
      restoreFocus();
    };
    const loadContents = async (bypassLock = false) => {
      try {
        const ready = runtime.api.lifecycleReady;
        if (ready) {
          try {
            await ready;
          } catch {
          }
        }
        const coll = contentsColl();
        const lifecycleFilter = typeof coll.getFilter === "function" ? coll.getFilter() : "";
        const ownFilterHasMacro = typeof filter === "string" && filter.includes("##");
        const effectiveFilter = !ownFilterHasMacro && lifecycleFilter && String(lifecycleFilter).trim().length > 0 ? String(lifecycleFilter) : resolvedFilter;
        const needsReload = typeof coll.isFull === "function" && coll.isFull() === false || coll.isStale?.() === true;
        const relock = bypassLock && coll.isLocked?.() === true && needsReload && typeof coll.unlock === "function" && typeof coll.lock === "function";
        if (relock) coll.unlock();
        try {
          let pageOpt;
          if (fluidPageSize != null) {
            fluidOffset = 0;
            if (startFromBottom && typeof coll.countRemote === "function") {
              try {
                const total = await coll.countRemote();
                if (Number.isFinite(total) && total > fluidPageSize) {
                  fluidOffset = total - fluidPageSize;
                }
              } catch {
              }
            }
            pageOpt = { size: fluidPageSize, offset: fluidOffset };
          }
          await coll.loadAll(effectiveFilter, sort, pageOpt);
        } finally {
          if (relock) coll.lock();
        }
        renderFromColl(coll);
        if (startFromBottom) this.scrollContainerToBottom(container);
        if (this.getAttribute("data-status") === "empty") {
          const rowSelf = this.parentRowSelf(runtime);
          if (rowSelf && typeof rowSelf.getContents === "function") {
            try {
              const viaRow = await Promise.resolve(
                rowSelf.getContents(contentsInstanceName || srcColl)
              );
              if (viaRow && (viaRow.getCount?.() ?? 0) > 0) {
                renderFromColl(viaRow);
              }
            } catch {
            }
          }
        }
      } catch (err) {
        this.setAttribute("data-status", "error");
        try {
          runtime.api.console.error("[xone-island] contents load failed", err);
        } catch {
        }
        const errObj = err;
        const status = errObj?.status;
        const code = errObj?.code ?? "";
        container.innerHTML = "";
        const placeholder = document.createElement("div");
        placeholder.className = "xone-contents-error";
        placeholder.style.cssText = "padding:16px;text-align:center;color:#999;font-size:13px;";
        if (status === 401 || code.includes("AUTH")) {
          placeholder.textContent = "Inicia sesi\xF3n para ver este contenido";
        } else if (status === 403) {
          placeholder.textContent = "No tienes permisos para ver este contenido";
        } else if (status === 404) {
          placeholder.textContent = "Contenido no disponible";
        } else {
          placeholder.textContent = "Error al cargar datos";
        }
        container.appendChild(placeholder);
      }
    };
    this._scrollToBottom = () => this.scrollContainerToBottom(container);
    let olderRowsBusy = false;
    this._loadOlderRows = async () => {
      if (fluidPageSize == null || olderRowsBusy) return;
      if (fluidOffset <= 0) return;
      olderRowsBusy = true;
      try {
        const coll = contentsColl();
        const newOffset = Math.max(0, fluidOffset - fluidPageSize);
        const size = fluidOffset - newOffset;
        if (size <= 0) return;
        const wasLocked = coll.isLocked?.() === true;
        if (wasLocked && typeof coll.unlock === "function") coll.unlock();
        try {
          await coll.loadAll(void 0, void 0, {
            size,
            offset: newOffset,
            prepend: true
          });
        } finally {
          if (wasLocked && typeof coll.lock === "function") coll.lock();
        }
        fluidOffset = newOffset;
        renderFromColl(coll);
      } catch (err) {
        try {
          runtime.api.console.error("[xone-island] loadOlderRows failed", err);
        } catch {
        }
      } finally {
        olderRowsBusy = false;
      }
    };
    let reachTopFn = null;
    let reachTopArmed = true;
    const onReachTopScroll = () => {
      const atTop = container.scrollTop <= 60;
      if (!atTop) {
        reachTopArmed = true;
        return;
      }
      if (!reachTopArmed || !reachTopFn) return;
      if (fluidOffset <= 0) return;
      reachTopArmed = false;
      try {
        reachTopFn();
      } catch {
      }
    };
    this._setOnReachTop = (fn) => {
      reachTopFn = fn;
      if (fn) {
        container.removeEventListener("scroll", onReachTopScroll);
        container.addEventListener("scroll", onReachTopScroll, { passive: true });
      } else {
        container.removeEventListener("scroll", onReachTopScroll);
      }
    };
    this.cleanup.push(() => container.removeEventListener("scroll", onReachTopScroll));
    this._reloadContents = () => {
      this.setAttribute("data-status", "loading");
      void loadContents();
    };
    this._rerenderContents = () => {
      const rowSelf = this.parentRowSelf(runtime);
      if (rowSelf && typeof rowSelf.getContents === "function") {
        const shared = contentsColl();
        let pending;
        try {
          pending = Promise.resolve(rowSelf.getContents(contentsInstanceName || srcColl));
        } catch {
          pending = Promise.resolve(null);
        }
        void pending.catch(() => null).then((viaRow) => {
          const v = viaRow;
          if (v && v !== shared && v.isLoading?.() !== true && shared.isLoading?.() !== true && (v.getCount?.() ?? 0) > 0 && !sameRowSet(v, shared)) {
            this.setAttribute("data-status", "loading");
            void loadContents(true);
            return;
          }
          rerenderShared();
        });
        return;
      }
      rerenderShared();
    };
    const rerenderShared = () => {
      try {
        const coll = contentsColl();
        const stale = coll.isStale?.() === true;
        if ((coll.getCount?.() ?? 0) > 0 && !stale) {
          renderFromColl(coll);
        } else if (coll.isLoading?.() === true) {
          this.setAttribute("data-status", "loading");
          const whenLoaded = coll.whenLoaded?.();
          if (whenLoaded) {
            void whenLoaded.then(() => {
              try {
                renderFromColl(coll);
              } catch (err) {
                try {
                  runtime.api.console.error(
                    "[xone-island] rerender(in-flight) failed",
                    err
                  );
                } catch {
                }
              }
            });
          } else {
            void loadContents(true);
          }
        } else {
          this.setAttribute("data-status", "loading");
          void loadContents(true);
        }
      } catch (err) {
        try {
          runtime.api.console.error("[xone-island] rerender failed", err);
        } catch {
        }
      }
    };
    if (typeof document !== "undefined") {
      let depText = null;
      const getDepText = () => {
        if (depText === null) {
          const attrs = (el) => Array.from(el.attributes).map((a) => `${a.name}="${a.value}"`).join(" ");
          depText = `${buildRowDepText(templateHtml)} ${attrs(this)} ${container !== this ? attrs(container) : ""}`;
        }
        return depText;
      };
      let depIndex;
      const getDepIndex = () => {
        if (depIndex === void 0) {
          depIndex = buildRowDepIndex(templateHtml, container !== this ? [this, container] : [this]);
        }
        return depIndex;
      };
      const onItemChanged = (e) => {
        const detail = e.detail;
        if (detail?.src && detail.src === srcColl && this._rerenderContents) {
          if (this.canSkipRowRebuild(container, contentsColl, detail.changes, getDepText, getDepIndex)) return;
          this._rerenderContents();
        }
      };
      document.addEventListener(CONTENTS_ITEM_CHANGED_EVENT, onItemChanged);
      this.cleanup.push(() => document.removeEventListener(CONTENTS_ITEM_CHANGED_EVENT, onItemChanged));
    }
    void loadContents();
  }
  /**
   * WEAL-287 — ¿se puede omitir el rebuild de la fila ante este lote de
   * cambios? Solo en el caso "el usuario está escribiendo en un control de la
   * fila y nada más de la fila depende de ese campo". Condiciones (todas):
   *  1. el lote trae el detalle de campos (sin él = recarga/descarte → rebuild)
   *     y ninguno es un cambio de PROPIEDAD visual (WEAL-111 → rebuild);
   *  2. el foco está en un control de texto/select de UNA fila de ESTE island
   *     (no de un `<contents>` anidado), ligado a ese mismo campo e item;
   *  3. el DOM ya muestra exactamente el valor del modelo (si un onchange lo
   *     normalizó, o es un numérico con formato, se repinta como siempre);
   *  4. `fieldHasRowDependents` = false (nada de la fila lo usa).
   * Así el caso que SÍ necesita repintar (un campo que cambia el
   * disablevisible/color de su fila, o el filtro de un contents anidado) sigue
   * reconstruyendo, y el foco/caret (WEAL-192) y el scroll (WEAL-122) quedan
   * intactos porque no se toca el DOM.
   */
  canSkipRowRebuild(container, contentsColl, changes, getDepText, getDepIndex) {
    try {
      if (!Array.isArray(changes) || changes.length === 0) return false;
      if (typeof document === "undefined") return false;
      const combo = getCommittingCombo();
      if (combo && container.contains(combo)) {
        if (this.canSkipComboRowRebuild(container, combo, contentsColl, changes, getDepText)) return true;
        return !!getDepIndex && this.patchRowAfterButton(container, combo, contentsColl, changes, getDepText, getDepIndex);
      }
      const button = getCommittingButton();
      if (button && getDepIndex && container.contains(button)) {
        return this.patchRowAfterButton(container, button, contentsColl, changes, getDepText, getDepIndex);
      }
      const active = document.activeElement;
      if (!isTypingControl(active) || !container.contains(active)) {
        return !!getDepIndex && this.patchScriptWrite(container, contentsColl, changes, getDepText, getDepIndex);
      }
      const rowEl = active.closest("[data-row-index]");
      if (!rowEl || !container.contains(rowEl)) return false;
      const ownerIsland = rowEl.parentElement?.closest("xone-island");
      if (ownerIsland !== this) return false;
      const propEl = active.closest("[data-prop]");
      const activeField = propEl?.getAttribute("data-prop") ?? active.getAttribute("name") ?? active.getAttribute("data-prop") ?? "";
      if (!activeField) return false;
      const coll = contentsColl();
      const rowItem = expandRowItem(rowEl) ?? coll?.get?.(Number(rowEl.getAttribute("data-row-index") ?? "-1"));
      if (!rowItem) return false;
      for (const ch of changes) {
        if (!ch || typeof ch.field !== "string" || ch.field === "") return false;
        if (ch.kind === "property") return false;
        if (ch.field.toLowerCase() !== activeField.toLowerCase()) return false;
        if (!sameItem(ch.item, rowItem)) return false;
        const model = readItemValue(ch.item, ch.field);
        if (!_XoneIslandElement.domMatchesModel(active, model)) return false;
      }
      return !fieldHasRowDependents(getDepText(), activeField, rowItem);
    } catch {
      return false;
    }
  }
  /**
   * WEAL-302 — `canSkipRowRebuild` para una selección en un `<xone-combo>`
   * enlazado (`linkedto`/`mapcol`). El combo escribe VARIOS campos en el mismo
   * lote (display + idProp + espejos) y el foco no queda en él (el panel cuelga
   * de `<body>`), así que el caso de WEAL-287 no lo cubría → rebuild completo de
   * la fila en cada selección (parpadeo de los bloques de la actividad de Acción
   * Judicial y re-carga de sus `<contents>` anidados). Condiciones (todas):
   *  1. el combo que comite es de UNA fila de ESTE island (no de uno anidado);
   *  2. cada cambio del lote es de VALOR, del item de esa fila y de un campo
   *     que el propio combo liga (`comboBoundFields`);
   *  3. el combo ya muestra el valor del modelo (si un onchange lo cambió, se
   *     repinta como siempre);
   *  4. ninguno de esos campos aparece en la plantilla de la fila fuera del
   *     propio combo (`fieldHasRowDependentsBeyond`).
   */
  canSkipComboRowRebuild(container, combo, contentsColl, changes, getDepText) {
    const rowEl = combo.closest("[data-row-index]");
    if (!rowEl || !container.contains(rowEl)) return false;
    if (rowEl.parentElement?.closest("xone-island") !== this) return false;
    if (combo.parentElement?.closest("xone-island") !== this) return false;
    const displayProp = combo.getAttribute("data-prop") ?? "";
    if (!displayProp) return false;
    const fields = comboBoundFields(combo);
    const fieldSet = new Set(fields.map((f) => f.toLowerCase()));
    const coll = contentsColl();
    const rowItem = expandRowItem(rowEl) ?? coll?.get?.(Number(rowEl.getAttribute("data-row-index") ?? "-1"));
    if (!rowItem) return false;
    for (const ch of changes) {
      if (!ch || typeof ch.field !== "string" || ch.field === "") return false;
      if (ch.kind === "property") return false;
      if (!fieldSet.has(ch.field.toLowerCase())) return false;
      if (!sameItem(ch.item, rowItem)) return false;
    }
    const model = readItemValue(rowItem, displayProp);
    const modelStr = model === null || model === void 0 ? "" : String(model);
    const native = combo.querySelector("select.xone-combo-native");
    if (native) {
      if (native.value !== modelStr) return false;
    } else {
      const disp = combo.querySelector(".xone-combo-display");
      if (!disp) return false;
      const expected = modelStr === "" ? combo.getAttribute("data-combo-default") ?? "" : modelStr;
      if ((disp.textContent ?? "") !== expected) return false;
    }
    const depText = getDepText();
    const ownText = comboOwnBindingText(combo);
    for (const f of fields) {
      if (fieldHasRowDependentsBeyond(depText, f, ownText, rowItem)) return false;
    }
    return true;
  }
  /**
   * WEAL-302 (ronda 2) — `canSkipRowRebuild` para el `onclick` de un botón de
   * una fila (`<prop type="B" onclick="self.X=1;">`, p.ej. «Mostrar datos»,
   * «+ Solicitar pago», «Cancelar» de la actividad de Acción Judicial). El
   * lote puede traer VARIOS campos («Cancelar» limpia 7). En vez de
   * reconstruir la fila, se PARCHEA in situ si TODOS los campos del lote solo
   * afectan a la fila vía (`classifyRowFieldDeps`):
   *  - fórmulas `data-disablevisible`/`data-disableedit` de la propia fila →
   *    se re-evalúan contra el item (mismo evaluador que el rebuild), y
   *  - el valor de controles ligados (`data-prop`) → se repinta con el mismo
   *    binding que `appendContentRow` (`bindRowPropEl`; combos como su
   *    `paintDisplay`).
   * Cualquier otra dependencia (macro `##FLD_X##`, filtro de un contents
   * anidado, atributo del host, imagen diferida WEAL-85, cambio de PROPIEDAD
   * WEAL-111, otro item, valor null) → `false` y rebuild de siempre. Devuelve
   * `true` SOLO si ya ha parcheado.
   */
  patchRowAfterButton(container, button, contentsColl, changes, getDepText, getDepIndex) {
    const rowEl = button.closest("[data-row-index]");
    if (!rowEl || !container.contains(rowEl)) return false;
    if (rowEl.parentElement?.closest("xone-island") !== this) return false;
    return this.patchRowInPlace(container, rowEl, contentsColl, changes, getDepText, getDepIndex);
  }
  /**
   * WEAL-354 — escritura por SCRIPT (sin combo ni botón marcados) sobre UN item
   * de este island: localiza su fila y la parchea in situ con
   * `patchRowInPlace` (mismas reglas que WEAL-302 r2: solo campos ligados a
   * controles o usados en fórmulas dv/de de la fila; cualquier otra
   * dependencia —macro `##FLD##`, filtro de un `<contents>` anidado, color,
   * imagen…— devuelve `false` y se reconstruye como siempre).
   *
   * Caso real: «Acción judicial» (`ActividadesClienteHijoAccionJudicial`). El
   * botón Refrescar de «Tasas y depósitos» recalcula los importes tras una
   * carga asíncrona (`objAJ.MAP_TASAS_* = …`); la marca del botón ya ha
   * caducado y el rebuild de la fila ENTERA destruía y re-montaba «La demanda»
   * (re-select de `ActividadesClienteDemanda`) → parpadeo del bloque izquierdo.
   *
   * Excepción: si el foco está en un control de texto de esa fila ligado a
   * uno de los campos del lote, no se parchea (repintar el valor movería el
   * caret): se deja el comportamiento previo (WEAL-287 / rebuild).
   */
  patchScriptWrite(container, contentsColl, changes, getDepText, getDepIndex) {
    const item = changes[0]?.item;
    if (!item) return false;
    for (const ch of changes) {
      if (!ch || !sameItem(ch.item, item)) return false;
    }
    const coll = contentsColl();
    let rowEl = null;
    for (const el of Array.from(container.querySelectorAll("[data-row-index]"))) {
      if (el.parentElement?.closest("xone-island") !== this) continue;
      const rowItem = expandRowItem(el) ?? coll?.get?.(Number(el.getAttribute("data-row-index") ?? "-1"));
      if (rowItem && sameItem(rowItem, item)) {
        if (rowEl) return false;
        rowEl = el;
      }
    }
    if (!rowEl) return false;
    const active = typeof document !== "undefined" ? document.activeElement : null;
    if (isTypingControl(active) && rowEl.contains(active)) {
      const propEl = active.closest("[data-prop]");
      const f = (propEl?.getAttribute("data-prop") ?? active.getAttribute("name") ?? "").toLowerCase();
      if (f && changes.some((ch) => (ch.field ?? "").toLowerCase() === f)) return false;
    }
    return this.patchRowInPlace(container, rowEl, contentsColl, changes, getDepText, getDepIndex);
  }
  /** WEAL-302 r2 / WEAL-354 — parche in situ de UNA fila (ver `patchRowAfterButton`). */
  patchRowInPlace(container, rowEl, contentsColl, changes, getDepText, getDepIndex) {
    if (!container.contains(rowEl)) return false;
    if (rowEl.parentElement?.closest("xone-island") !== this) return false;
    const index = getDepIndex();
    if (!index) return false;
    const coll = contentsColl();
    const rowItem = expandRowItem(rowEl) ?? coll?.get?.(Number(rowEl.getAttribute("data-row-index") ?? "-1"));
    if (!rowItem) return false;
    if (rowImgRecomputePending(getDepText(), rowItem)) return false;
    const fields = [];
    const bindFields = [];
    for (const ch of changes) {
      if (!ch || typeof ch.field !== "string" || ch.field === "") return false;
      if (ch.kind === "property") return false;
      if (!sameItem(ch.item, rowItem)) return false;
      if (fields.some((f) => f.toLowerCase() === ch.field.toLowerCase())) continue;
      const dep = classifyRowFieldDeps(index, ch.field);
      if (!dep.ok) return false;
      fields.push(ch.field);
      if (dep.bind) {
        const v = readItemValue(rowItem, ch.field);
        if (v === void 0 || v === null) return false;
        bindFields.push(ch.field);
      }
    }
    if (fields.length === 0) return false;
    const ownEls = (selector) => {
      const all = [];
      if (rowEl.matches(selector)) all.push(rowEl);
      all.push(...rowEl.querySelectorAll(selector));
      return all.filter((el) => {
        const z = el.parentElement?.closest('xone-island[data-type="Z"],[data-xone-type="Z"]');
        return !z || !rowEl.contains(z);
      });
    };
    const refsAny = (text) => fields.some((f) => countFieldRefs(text, f) > 0);
    const lowerBind = new Set(bindFields.map((f) => f.toLowerCase()));
    if (lowerBind.size > 0) {
      for (const el of ownEls("[data-prop]")) {
        const f = (el.getAttribute("data-prop") ?? "").toLowerCase();
        if (!lowerBind.has(f)) continue;
        const host = el.tagName === "XONE-COMBO" ? el : el.closest("xone-combo");
        if (host && host.getAttribute("data-combo-locked") !== "true") {
          if (host === el) this.repaintRowCombo(el, readItemValue(rowItem, el.getAttribute("data-prop") ?? ""));
          continue;
        }
        this.bindRowPropEl(el, rowItem);
      }
    }
    for (const el of ownEls("[data-disablevisible]")) {
      const js = el.getAttribute("data-disablevisible") ?? "";
      if (!js || !refsAny(js)) continue;
      let hidden = false;
      try {
        hidden = Boolean(compileDvRow(js)(rowItem));
      } catch {
        hidden = false;
      }
      if (hidden) el.setAttribute("hidden", "");
      else el.removeAttribute("hidden");
    }
    for (const el of ownEls("[data-disableedit]")) {
      const js = el.getAttribute("data-disableedit") ?? "";
      if (!js || !refsAny(js)) continue;
      let disabled = false;
      try {
        disabled = Boolean(compileDvRow(js)(rowItem));
      } catch {
        disabled = false;
      }
      setEditDisabledState(el, disabled);
    }
    this.applyRowPropOverrides(rowEl, rowItem);
    return true;
  }
  /** WEAL-302 (r2): repinta un `<xone-combo>` de fila como su `paintDisplay`. */
  repaintRowCombo(combo, value) {
    const str = value === void 0 || value === null ? "" : String(value);
    const native = combo.querySelector("select.xone-combo-native");
    if (native) {
      if (str !== "") {
        native.value = str;
        return;
      }
      const opts = Array.from(native.options);
      const def2 = opts.findIndex((o) => o.defaultSelected);
      native.selectedIndex = def2 >= 0 ? def2 : opts.length > 0 ? 0 : -1;
      return;
    }
    const display = combo.querySelector(".xone-combo-display");
    if (!display) return;
    const def = combo.getAttribute("data-combo-default") ?? "";
    const text = str === "" ? def : str;
    display.textContent = text;
    display.classList.toggle("xone-combo-placeholder", text === def && text !== "");
  }
  collectItems(coll, count) {
    const items = [];
    for (let i = 0; i < count; i++) {
      const item = coll.get?.(i);
      if (item) items.push(item);
    }
    return items;
  }
  renderExpandView(runtime, container, templateHtml, items, cellHeight) {
    container.classList.add("is-expandview");
    container.style.display = "flex";
    container.style.flexDirection = "column";
    container.style.overflowY = "auto";
    const nivelField = items.length > 0 ? resolveExpandNivelField(items[0]) : void 0;
    const parents = nivelField !== void 0 ? items.filter((it) => Number(it[nivelField]) === 0) : items;
    const childrenByParent = /* @__PURE__ */ new Map();
    if (nivelField !== void 0) {
      for (const it of items) {
        if (Number(it[nivelField]) !== 0) {
          const pid = resolveExpandParentId(it);
          const key = String(pid);
          if (!childrenByParent.has(key)) childrenByParent.set(key, []);
          childrenByParent.get(key).push(it);
        }
      }
    }
    const childSrc = container.getAttribute("data-child-src") ?? "";
    const childFilter = container.getAttribute("data-child-filter") ?? "";
    const childSort = container.getAttribute("data-child-sort") ?? "";
    const isShellMenu = !!document.querySelector("[data-xone-router-slot]") && !this.closest("[data-xone-router-slot]");
    const innerDivForKey = this.querySelector("div[data-src]");
    const expandStateName = this.getAttribute("data-contents-name") || innerDivForKey?.getAttribute("data-contents-name") || this.getAttribute("data-src") || innerDivForKey?.getAttribute("data-src") || "";
    const expandStateKey = isShellMenu && expandStateName ? `xone:expand:${expandStateName}` : "";
    const readOpenGroups = () => {
      if (!expandStateKey) return /* @__PURE__ */ new Set();
      try {
        const raw = sessionStorage.getItem(expandStateKey);
        if (!raw) return /* @__PURE__ */ new Set();
        const arr = JSON.parse(raw);
        return Array.isArray(arr) ? new Set(arr.map(String)) : /* @__PURE__ */ new Set();
      } catch {
        return /* @__PURE__ */ new Set();
      }
    };
    const writeOpenGroups = (open) => {
      if (!expandStateKey) return;
      try {
        sessionStorage.setItem(expandStateKey, JSON.stringify([...open]));
      } catch {
      }
    };
    const openGroups = readOpenGroups();
    if (expandStateKey) container.setAttribute("data-expand-sel-key", `xone:expand-sel:${expandStateName}`);
    else container.removeAttribute("data-expand-sel-key");
    let pageOpen = this._expandOpenLocal;
    if (!expandStateKey && expandStateName) {
      pageOpen = PAGE_EXPAND_OPEN.get(expandStateName) ?? this._expandOpenLocal;
      PAGE_EXPAND_OPEN.set(expandStateName, pageOpen);
      this._expandOpenLocal = pageOpen;
    }
    const autoCollapse = resolveAutoCollapse(
      container.getAttribute("data-auto-collapse") ?? this.getAttribute("data-auto-collapse"),
      isShellMenu
    );
    const expandGen = String(++EXPAND_RENDER_GEN);
    const collapseSiblings = (opened) => {
      if (!autoCollapse) return;
      for (const el of Array.from(container.children)) {
        if (el !== opened && el instanceof HTMLDetailsElement && el.classList.contains("xone-expand-group") && el.getAttribute("data-expand-gen") === expandGen && el.open) {
          el.open = false;
        }
      }
    };
    let restoredOne = false;
    let childLoadQueue = Promise.resolve();
    for (let i = 0; i < parents.length; i++) {
      const item = parents[i];
      const details = document.createElement("details");
      details.className = "xone-expand-group";
      details.setAttribute("data-row-index", String(i));
      details.setAttribute("data-expand-gen", expandGen);
      const summary = document.createElement("summary");
      summary.className = "xone-expand-summary xone-content-item";
      summary.setAttribute("data-row-index", String(i));
      summary.setAttribute("data-row-key", `g:${String(item["ID"] ?? i)}#${i}`);
      summary.style.cssText = "cursor:pointer;list-style:none;display:flex;align-items:center;";
      summary.innerHTML = templateHtml;
      this.recomputeRowDisplayFields(summary, item);
      this.expandItemFldMacros(summary, item);
      this.bindItemValues(summary, item);
      setRowItem(summary, item);
      for (const el of summary.querySelectorAll("[style]")) {
        if (el.style.height && (el.style.height.includes("dvh") || el.style.height.includes("vh"))) {
          el.style.height = "auto";
        }
      }
      details.appendChild(summary);
      const childContainer = document.createElement("div");
      childContainer.className = "xone-expand-children";
      childContainer.style.paddingLeft = "24px";
      details.appendChild(childContainer);
      details.addEventListener("toggle", () => {
        if (details.open) collapseSiblings(details);
      });
      const parentId = String(item["ID"] ?? i);
      const inlineChildren = childrenByParent.get(parentId);
      let childrenLoaded = false;
      const loadChildrenOnce = () => {
        if (childrenLoaded || !childSrc || inlineChildren && inlineChildren.length > 0) return;
        childrenLoaded = true;
        childLoadQueue = childLoadQueue.then(() => this.loadExpandChildren(
          runtime,
          childContainer,
          childSrc,
          childFilter,
          childSort,
          templateHtml,
          item,
          cellHeight
        )).catch(() => void 0);
      };
      if (inlineChildren && inlineChildren.length > 0) {
        for (let j = 0; j < inlineChildren.length; j++) {
          const childRow = document.createElement("article");
          childRow.className = "xone-content-item xone-contents-row xone-expand-child";
          childRow.style.cssText = "cursor:pointer;display:flex;align-items:center;";
          childRow.innerHTML = templateHtml;
          this.recomputeRowDisplayFields(childRow, inlineChildren[j]);
          this.expandItemFldMacros(childRow, inlineChildren[j]);
          this.bindItemValues(childRow, inlineChildren[j]);
          for (const el of childRow.querySelectorAll("[style]")) {
            if (el.style.height && (el.style.height.includes("dvh") || el.style.height.includes("vh"))) {
              el.style.height = "auto";
            }
          }
          childRow.setAttribute("data-row-index", String(j));
          childRow.setAttribute("data-row-key", `c:${parentId}#${i}:${String(inlineChildren[j]["ID"] ?? "")}#${j}`);
          setRowItem(childRow, inlineChildren[j]);
          childContainer.appendChild(childRow);
        }
        applyExpandSelection(childContainer);
      } else if (childSrc) {
        details.addEventListener("toggle", () => {
          if (details.open) loadChildrenOnce();
        });
      }
      if (!expandStateKey) {
        const groupKey = String(item["ID"] ?? i);
        details.addEventListener("toggle", () => {
          if (details.open) pageOpen.add(groupKey);
          else pageOpen.delete(groupKey);
        });
        if (pageOpen.has(groupKey) && !details.open && !(autoCollapse && restoredOne)) {
          details.open = true;
          restoredOne = true;
          loadChildrenOnce();
        } else if (autoCollapse && restoredOne) {
          pageOpen.delete(groupKey);
        }
      }
      if (expandStateKey) {
        const groupKey = String(item["ID"] ?? i);
        details.addEventListener("toggle", () => {
          const cur = readOpenGroups();
          if (details.open) {
            if (autoCollapse) cur.clear();
            cur.add(groupKey);
          } else cur.delete(groupKey);
          writeOpenGroups(cur);
        });
        summary.addEventListener("click", (e) => {
          const t = e.target;
          if (t?.closest?.('button, input, select, textarea, a[href], [data-stop-propagation="true"]')) return;
          const cur = readOpenGroups();
          if (details.open) cur.delete(groupKey);
          else if (autoCollapse) {
            cur.clear();
            cur.add(groupKey);
          } else cur.add(groupKey);
          writeOpenGroups(cur);
        });
        if (openGroups.has(groupKey) && !details.open && !(autoCollapse && restoredOne)) {
          restoredOne = true;
          details.open = true;
          loadChildrenOnce();
        }
      }
      container.appendChild(details);
    }
  }
  /**
   * WEAL-58: tipo temporal XOne (`D`/`DT`/`H`) declarado en el wrapper
   * `[data-prop]` de una fila de contents vía `data-xone-fieldtype`, o `null`.
   */
  static dateFieldTypeOf(pel) {
    const ft = pel.getAttribute("data-xone-fieldtype");
    return ft === "D" || ft === "DT" || ft === "H" ? ft : null;
  }
  /**
   * WEAL-58: normaliza un valor de modelo (posible DATETIME ISO crudo) al
   * formato que acepta `<input type=date|datetime-local|time>`. Inputs no-fecha
   * o valores no-ISO → sin cambios (idempotente).
   */
  /**
   * Convierte el `value` de un `<input type=datetime-local>`
   * (`"YYYY-MM-DDTHH:mm"` [`:ss`]) a un `Date` con esos MISMOS componentes en
   * UTC. Paridad con el fix de `D`: read (aquí) y write (`modelDateToInputValue`)
   * usan componentes UTC, así el wall-clock introducido se preserva end-to-end
   * y el wire (UTC) round-trip-ea sin desfase. Se parsea el string porque
   * `valueAsDate` es `null` para `datetime-local`. Vacío / no parseable → `null`.
   */
  static datetimeLocalToDate(value) {
    if (value === "") return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/.exec(value);
    if (!m) return null;
    return new Date(Date.UTC(
      Number(m[1]),
      Number(m[2]) - 1,
      Number(m[3]),
      Number(m[4]),
      Number(m[5]),
      Number(m[6] ?? "0")
    ));
  }
  /**
   * Igual que `normalizeDateInputValue` pero acepta el valor de MODELO crudo:
   * si es un `Date` (lo que ahora almacena un campo `D`/`DT` tras editarlo — ver
   * `wireBind.readCommittedValue`), lo formatea al `value` del `<input>` con
   * componentes UTC (simétrico a `valueAsDate`/`datetimeLocalToDate`); si es
   * string u otro, delega en `normalizeDateInputValue`. Evita que `String(dateObj)`
   * (`"Sat Jun 15 1985 …"`) VACÍE el `<input>` al re-hidratar.
   */
  /**
   * WEAL-287/387/612 — ¿el control enfocado ya refleja el valor del modelo?
   * (condición 3 de `canSkipRowRebuild`). Comparación de texto con dos
   * normalizaciones:
   *  - WEAL-387: numérico enmascarado (`inputmode="decimal"`): el DOM muestra la
   *    coma del usuario («25,5») y el modelo el número coercido (25.5).
   *  - WEAL-612: `<input type=date|datetime-local|time>`: el modelo guarda un
   *    `Date` (`readCommittedValue`, WEAL-58/59) y `String(Date)` («Sun Nov 15
   *    2026 …») nunca casaba con `value` («2026-11-15») → rebuild de la fila en
   *    cada `input` (p.ej. al cambiar de mes en el calendario nativo), que
   *    desmontaba el `<input>` y cerraba el popup. Se usa el mismo normalizador
   *    que el camino de escritura (`modelDateToInputValue`).
   */
  static domMatchesModel(active, model) {
    const modelStr = model === null || model === void 0 ? "" : String(model);
    if (active.value === modelStr) return true;
    if (!(active instanceof HTMLInputElement)) return false;
    if (active.getAttribute("inputmode") === "decimal") {
      return decimalTextMatchesModel(active.value, model);
    }
    const t = active.type;
    if (t === "date" || t === "datetime-local" || t === "time") {
      return active.value === _XoneIslandElement.modelDateToInputValue(active, model);
    }
    return false;
  }
  static modelDateToInputValue(inp, value) {
    return formatXoneInputDate(value, inp.type);
  }
  static normalizeDateInputValue(inp, str) {
    const type = inp.type;
    if (type !== "date" && type !== "datetime-local" && type !== "time") return str;
    if (str === "") return "";
    const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(str);
    if (!m) return str;
    const [, y, mo, d, h = "00", mi = "00"] = m;
    if (type === "date") return `${y}-${mo}-${d}`;
    if (type === "time") return `${h}:${mi}`;
    return `${y}-${mo}-${d}T${h}:${mi}`;
  }
  bindItemValues(el, item) {
    const propEls = el.querySelectorAll("[data-prop]");
    for (const pel of propEls) {
      const field = pel.getAttribute("data-prop");
      if (!field) continue;
      if (pel.getAttribute("data-xone-type") === "Z" || pel.getAttribute("data-type") === "Z") continue;
      if (pel.getAttribute("data-type") === "frame" || pel.hasAttribute("data-xone-frame")) continue;
      const value = item[field];
      const imgEarly = pel.tagName === "IMG" ? pel : pel.querySelector("img");
      const str = value === void 0 || value === null ? "" : String(value);
      const rowDateFt = _XoneIslandElement.dateFieldTypeOf(pel);
      const dispStr = rowDateFt ? formatXoneDisplayDate(value, rowDateFt) : str;
      if (imgEarly) {
        if (str.trim() !== "") {
          imgEarly.style.removeProperty("visibility");
          imgEarly.classList.remove("xone-img-empty");
          imgEarly.src = this.normalizeImgSrc(str);
        } else {
          const existing = imgEarly.getAttribute("src") ?? "";
          const hasValidSrc = existing.trim() !== "" && !existing.includes("##");
          if (!hasValidSrc) this.hideBrokenImage(imgEarly);
        }
        continue;
      }
      if (value !== void 0 && this.bindImgPlaceholderValue(pel, str)) continue;
      if (value === void 0 || value === null) continue;
      if (pel.getAttribute("data-xone-static") === "true" && str === "" && (pel.textContent ?? "") !== "") continue;
      const labelEl = pel.querySelector(".xone-label-text");
      if (labelEl) {
        labelEl.textContent = str;
        continue;
      }
      const textEl = pel.querySelector(".xone-text-value");
      if (textEl) {
        textEl.textContent = dispStr;
        continue;
      }
      const input = pel.querySelector("input,textarea");
      if (input) {
        const inp = input;
        if (inp.type === "checkbox" || inp.type === "radio" || pel.getAttribute("data-xone-type") === "NC") {
          inp.checked = str !== "" && str !== "0" && str.toLowerCase() !== "false";
        } else {
          inp.value = _XoneIslandElement.normalizeDateInputValue(inp, str);
        }
        continue;
      }
      if (pel.getAttribute("data-xone-type") === "THTML") {
        pel.innerHTML = str;
        rebindInlineHandlers(pel);
        continue;
      }
      if (pel.tagName === "SPAN" || pel.tagName === "DIV") pel.textContent = dispStr;
    }
    const imgs = el.querySelectorAll("img[src]");
    for (const img of imgs) {
      const src = img.getAttribute("src") ?? "";
      if (src.trim() === "" || src.includes("##")) {
        this.hideBrokenImage(img);
        continue;
      }
      if (!src.startsWith("/") && !src.startsWith("http") && !src.startsWith("data:")) {
        img.src = this.normalizeImgSrc(src);
      }
    }
    this.applyRowDisableVisible(el, item);
    this.applyRowDisableEdit(el, item);
    this.inheritContentsEditLock(el);
    this.applyRowPropOverrides(el, item);
  }
  /**
   * Evalúa el `disablevisible` POR-FILA de un `<contents>`: los props con
   * `data-disablevisible` (p.ej. MAP_SEL/MAP_NOSEL con dv `MAP_COLOR=0` / `=1`)
   * se ocultan/muestran según los campos de ESTA fila (`item`), no del `self`
   * de página. Paridad Android `ControlsUtils.isPropertyVisible` por-item. El
   * generador hornea la fórmula ya traducida (`_coerce(row["X"])==0`); se
   * evalúa CSP-safe (`compileTranslatedDv`, sin `new Function`) y se togglea
   * `hidden`. Cubre el propio `el` y sus descendientes. Lo llaman AMBOS paths
   * de fila: `bindItemValues` (expandview) y `appendContentRow`
   * (recyclerview/gridview — p.ej. el menú WebMenuPadre).
   */
  applyRowDisableVisible(el, item) {
    const dvEls = [];
    if (el.hasAttribute("data-disablevisible")) dvEls.push(el);
    dvEls.push(...el.querySelectorAll("[data-disablevisible]"));
    for (const dvEl of dvEls) {
      const js = dvEl.getAttribute("data-disablevisible");
      if (!js) continue;
      let hidden = false;
      try {
        hidden = Boolean(compileDvRow(js)(item));
      } catch {
        hidden = false;
      }
      if (hidden) dvEl.setAttribute("hidden", "");
      else dvEl.removeAttribute("hidden");
    }
  }
  /**
   * Evalúa `disableedit` POR FILA contra el `item` y pone read-only/disabled
   * el control (misma fórmula/evaluador que disablevisible; solo cambia el
   * efecto DOM). Cubre el propio `el` y sus descendientes. Lo llaman AMBOS
   * paths de fila (`bindItemValues`, `appendContentRow`), igual que
   * `applyRowDisableVisible`. Paridad Android `ControlsUtils.isPropertyDisabled`
   * por objeto de fila.
   */
  applyRowDisableEdit(el, item) {
    const deEls = [];
    if (el.hasAttribute("data-disableedit")) deEls.push(el);
    deEls.push(...el.querySelectorAll("[data-disableedit]"));
    for (const deEl of deEls) {
      const js = deEl.getAttribute("data-disableedit");
      if (!js) continue;
      let disabled = false;
      try {
        disabled = Boolean(compileDvRow(js)(item));
      } catch {
        disabled = false;
      }
      setEditDisabledState(deEl, disabled);
    }
  }
  /**
   * WEAL-85 — DEFECTO B. Los campos display-only computados por el script de
   * "carga de objeto" (`onchange[ID]` → `comprobarEtapa*`, p.ej.
   * `MAP_IMG_PROGRESO*` según `IDESTADO`) NUNCA se calculan en una fila de
   * `<contents>`: el runtime web sólo enlaza el `row` crudo del DoAction
   * (`expandItemFldMacros`/`bindItemValues`) y no dispara `onchange[ID]` por
   * objeto. Resultado: `##FLD_MAP_IMG_PROGRESO*##` resuelve vacío → las bolitas
   * quedan `xone-img-empty` (bug del reporte, escenario Devolución intereses).
   *
   * Paridad Android (`setValue(ID)`→`ExecuteFieldEvent`, que en la carga del
   * objeto dispara el `onchange[ID]`): tras enlazar el item y ANTES de expandir
   * sus macros, ejecutamos el handler `ID_onchange` de la coll con `self` = un
   * SANDBOX sobre el `item` (`makeRecomputeSelf`, con `save()` suprimido). El
   * handler computa los campos display SOBRE el item; luego `expandItemFldMacros`
   * los resuelve. Síncrono y best-effort (un fallo no rompe el render).
   *
   * GATE (blast radius): SÓLO se dispara si la fila tiene `<img
   * data-xone-img-src>` cuyo macro `##FLD_X##` referencia un campo AUSENTE/vacío
   * en el item — el caso EXACTO de las bolitas de progreso. Las filas sin ese
   * patrón (la inmensa mayoría) no ejecutan nada.
   */
  recomputeRowDisplayFields(el, item) {
    const runtime = typeof window !== "undefined" ? window[RUNTIME_KEY] : void 0;
    if (!runtime) return;
    const collName = this.getAttribute("data-src") ?? this.getAttribute("data-contents-name") ?? this.getAttribute("data-coll") ?? "";
    if (collName === "") return;
    const handler = runtime.eventsByColl?.[collName]?.["ID_onchange"];
    if (typeof handler !== "function") return;
    const fldRe = /##FLD_([A-Za-z0-9_]+)##/g;
    let needs = false;
    for (const img of el.querySelectorAll("img[data-xone-img-src]")) {
      const tpl = img.getAttribute("data-xone-img-src") ?? "";
      fldRe.lastIndex = 0;
      let m;
      while ((m = fldRe.exec(tpl)) !== null) {
        const field = m[1];
        const v = item[field];
        if (v === void 0 || v === null || v === "") {
          needs = true;
          break;
        }
      }
      if (needs) break;
    }
    if (!needs) return;
    const sandbox = makeRecomputeSelf(item);
    const restoreSelf = setXoneGlobalSelf(sandbox);
    try {
      const api = {
        ...runtime.api,
        self: sandbox,
        This: sandbox,
        appData: INERT_RECOMPUTE,
        ui: INERT_RECOMPUTE,
        $http: INERT_RECOMPUTE
      };
      void handler(api, item["ID"]);
    } catch {
    } finally {
      restoreSelf();
    }
  }
  /**
   * Hereda el candado de NIVEL-HOST (contents host Z con `disableedit` de
   * PÁGINA) en los editores de una fila recién insertada/re-renderizada. Se
   * llama tras `applyRowDisableEdit` (el candado del host GANA solo sobre los
   * editores SIN condición propia por-fila). Solo actúa si el island host está
   * marcado `data-contents-edit-locked="1"` (lo fija el generador en el estado
   * inicial y domSync de forma reactiva).
   *
   * WEAL-176: los editores con su PROPIO `data-disableedit` (p.ej.
   * DOCUMENTO_VISIBLE con `disableedit="MAP_BLOQUEO=1"`) tienen su condición
   * por-fila ya resuelta por `applyRowDisableEdit` — ÉL es la autoridad. El
   * candado estático/reactivo del host NO debe pisarlos: quedan editables
   * cuando su propia fórmula es falsa (paridad Android: el `disableedit` del
   * prop gana sobre el `locked` heredado del contents host). El resto de
   * editores (sin `disableedit` propio) siguen bloqueándose por completo.
   */
  inheritContentsEditLock(rowEl) {
    if (this.getAttribute("data-contents-edit-locked") !== "1") return;
    if (this.getAttribute("data-contents-edit-inrow") === "1" && !this.hasAttribute("data-contents-disableedit")) return;
    const eds = [];
    if (rowEl.matches(CONTENTS_EDITOR_SELECTOR)) eds.push(rowEl);
    eds.push(...rowEl.querySelectorAll(CONTENTS_EDITOR_SELECTOR));
    for (const ed of eds) {
      if (ed.hasAttribute("data-disableedit")) continue;
      if (ed.getAttribute("data-xone-type") === "NC" && ed.closest('[data-stop-propagation="true"]') !== null)
        continue;
      setContentsHostEditLock(ed, true);
    }
  }
  /**
   * Aplica un atributo reactivo ya resuelto sobre un elemento de fila (espejo
   * acotado de `domSync.patchPropAttributeDOM`, sin dependencia del self de
   * página). `title` → texto del span del control (botón/label/valor).
   */
  applyRowReactiveAttr(el, attr, value) {
    if (attr === "title") {
      const span = el.querySelector(
        ".xone-btn-text, .xone-label-text, .xone-text-value"
      );
      if (span) span.textContent = value;
      else el.textContent = value;
      return;
    }
    if (attr === "bgcolor") {
      el.style.backgroundColor = this.dataColorToCss(value);
      return;
    }
    if (attr === "forecolor") {
      el.style.color = this.dataColorToCss(value);
      return;
    }
    if (attr === "text-bgcolor") {
      const sub = el.querySelector("input, textarea");
      (sub ?? el).style.backgroundColor = this.dataColorToCss(value);
      return;
    }
    if (attr === "text-forecolor") {
      el.style.setProperty("--xone-text-forecolor", this.dataColorToCss(value));
      return;
    }
    if (attr === "border-color") {
      el.style.borderColor = this.dataColorToCss(value);
      return;
    }
    if (attr === "img" || attr === "src" || attr === "path") {
      const img = el.tagName === "IMG" ? el : el.querySelector("img");
      if (!img) return;
      if (value.trim() === "" || value.includes("##")) {
        this.hideBrokenImage(img);
        return;
      }
      img.src = this.normalizeImgSrc(value);
      img.style.removeProperty("visibility");
      img.classList.remove("xone-img-empty");
      img.removeAttribute("data-xone-img-src");
      return;
    }
    if (attr === "imgbk") {
      const url = this.normalizeImgSrc(value);
      el.style.backgroundImage = `url("${url.replace(/"/g, '\\"')}")`;
      return;
    }
  }
  /**
   * WEAL-111: re-aplica sobre una fila recién construida los overrides de
   * propiedad que el script fijó en runtime sobre ESE item
   * (`item.setFieldPropertyValue(field, attr, value)`).
   *
   * El re-render de un `<contents>` reconstruye las filas desde la plantilla
   * SSR, así que sin este paso el override se perdería en el primer repintado
   * (justo el que dispara el propio `setFieldPropertyValue`): el icono volvería
   * al del template. Se aplica DESPUÉS del binding de valores para que gane
   * sobre el `src` derivado del campo, y es idempotente (el caché del item es
   * la fuente de verdad). Las macros `##FLD_` no se cachean, así que nunca
   * llegan aquí (se re-evalúan por fila en `expandItemFldMacros`).
   */
  applyRowPropOverrides(row, item) {
    let overrides;
    try {
      const getter = item.getCachedFieldAttributes;
      if (typeof getter !== "function") return;
      overrides = getter.call(item) ?? [];
    } catch {
      return;
    }
    for (const ov of overrides) {
      if (!/^[A-Za-z][A-Za-z0-9_]*$/.test(ov.field)) continue;
      const el = row.querySelector(`[data-prop="${ov.field}"]`);
      if (!el) continue;
      try {
        this.applyRowReactiveAttr(el, ov.attribute, ov.value);
      } catch {
      }
    }
  }
  /**
   * Normaliza un color DATA-DRIVEN (resuelto de una macro `##FLD_...##` en
   * `bgcolor`/`forecolor`/`text-bgcolor`/`text-forecolor`) a CSS válido,
   * PRESERVANDO la opacidad (WEAL-52).
   *
   * Contrato: los colores que viajan como DATO (p.ej. `e.COLOR` de la paleta
   * de estados de Alivia) llegan en orden CSS `#RRGGBBAA` (alpha DETRÁS), NO
   * en el ARGB `#AARRGGBB` de Android (`Color.parseColor`, alpha DELANTE). Los
   * navegadores soportan `#RRGGBBAA` de forma nativa, así que un hex de 8
   * dígitos data-driven se pasa TAL CUAL. Antes se rotaba el alpha al frente
   * (`#FF000080`→`#000080FF`), corrompiendo el color (rojo 50% → azul opaco) y
   * perdiendo la opacidad. El resto (`#RRGGBB`, `rgb()`, nombres, macros sin
   * expandir) también se pasa tal cual.
   *
   * NOTA: los literales ESTÁTICOS `#AARRGGBB` de XML/CSS siguen convirtiéndose
   * en build-time por `transformColors`/`toCssColor` (static=ARGB); este path
   * es SOLO runtime data-driven (data-driven=RGBA) — contrato coherente.
   */
  dataColorToCss(raw) {
    return raw;
  }
  /**
   * Oculta un <img> sin recurso (src vacío o macro ##FLD_## sin expandir)
   * conservando su caja. Quita el src (evita el GET 404 → glifo de
   * imagen-rota del navegador) y aplica visibility:hidden (mantiene el
   * width/height = LayoutParams, no draws nothing). Paridad Android
   * XOneImageView.java:479. Idempotente.
   */
  hideBrokenImage(img) {
    img.removeAttribute("src");
    img.setAttribute("alt", "");
    img.style.visibility = "hidden";
    img.classList.add("xone-img-empty");
  }
  normalizeImgSrc(raw) {
    if (!raw || /^(?:https?:|data:|blob:|\/\/)/i.test(raw) || raw.includes("##")) return raw;
    let n = raw.replace(/\\/g, "/");
    if (n.startsWith("./")) n = n.slice(2);
    if (n.startsWith("/")) return n;
    if (n.includes("/")) return `/${n}`;
    return `/icons/${n}`;
  }
  /**
   * WEAL-515: placeholder SSR de un prop IMG/PH sin `<img>`
   * (`span.xone-prop-img.xone-img-empty`, controlRenderer.renderEmptyImagePlaceholder)
   * cuyo campo de fila trae una ruta → se pinta un `<img>` dentro; vacío → caja
   * vacía. NUNCA texto (antes caía en el fallback `textContent` y la URL salía
   * escrita). Paridad Android XOneImageView (path del campo; vacío no dibuja).
   * Devuelve true si `el` es un placeholder de imagen (ya tratado).
   */
  bindImgPlaceholderValue(el, str) {
    if (!el.classList.contains("xone-prop-img") && !el.classList.contains("xone-prop-ph")) return false;
    for (const n of Array.from(el.childNodes)) {
      if (n.nodeType === 3) el.removeChild(n);
    }
    let img = el.querySelector("img");
    const v = str.trim();
    if (v === "" || v.includes("##")) {
      if (img) this.hideBrokenImage(img);
      el.classList.add("xone-img-empty");
      return true;
    }
    if (img === null) {
      img = document.createElement("img");
      img.alt = "";
      img.className = "xone-prop-img-value";
      const host = el;
      const hasW = host.style.width !== "";
      const hasH = host.style.height !== "";
      img.style.display = "block";
      img.style.objectFit = "contain";
      img.style.objectPosition = "center";
      img.style.maxWidth = "100%";
      img.style.width = hasW ? "100%" : "auto";
      img.style.height = hasH ? "100%" : "auto";
      el.appendChild(img);
    }
    const src = this.normalizeImgSrc(v.replace(/\\/g, "/"));
    if (img.getAttribute("src") !== src) img.src = src;
    img.style.removeProperty("visibility");
    img.classList.remove("xone-img-empty");
    el.classList.remove("xone-img-empty");
    return true;
  }
  /**
   * WMRED-4 — desarrolla las macros del filtro de hijos de un `expanview` a
   * literales SQL **según el TIPO del valor**, que es como lo hace Android.
   *
   * Canon (`XoneDataCollection.EvaluateMacro` → `ObjectDeveloper.
   * DevelopObjectValue`, `xoneutils_lib`): el valor de una macro que entra en
   * un filtro NO se inyecta crudo, se *desarrolla*: las cadenas se escapan
   * (`'` → `''`) y se **entrecomillan**, los números van crudos y el nulo va
   * como `NULL`. Por eso los `.xne` escriben la macro DESNUDA —
   * `find_in_set(##USER_USERTYPE##,PERMISOS)`— y no `'##USER_USERTYPE##'`.
   *
   * Antes aquí se hacía `String(val)` y luego `resolveMacros`, cuyo primer
   * paso es `appData.expandMacros` (sustitución textual cruda). Resultado:
   * `find_in_set(ADMINWEB,PERMISOS)`, donde `ADMINWEB` no es una cadena sino
   * un NOMBRE DE COLUMNA que no existe → el backend devolvía `errorprocdata`
   * y la rama se pintaba como «Error al cargar». Con números colaba de puro
   * milagro (`IDPADRE=4` es SQL válido), que es justo por qué pasó inadvertido.
   *
   * Reglas, en este orden:
   *   1. La macro ya venía entrecomillada en el `.xne` (`'##X##'`) → se
   *      respeta al autor y solo se escapa el contenido. Evita `''X''`.
   *   2. El valor es numérico → crudo (paridad Android: los números no se
   *      entrecomillan, y así siguen valiendo usos no-valor como `LIMIT ##N##`).
   *   3. Resto → `developObjectValue`, el port del desarrollador de Android.
   *
   * Es genérico: no mira el nombre del proyecto ni de la colección, solo el
   * tipo del valor. Una macro sin resolver se deja INTACTA (`##X##`) en vez de
   * vaciarse, para que el fallo sea visible y no una consulta silenciosamente
   * distinta — el fail-closed que ya pedía WEAL-229.
   */
  developExpandFilterMacros(runtime, filterTemplate, parentItem) {
    const appData = runtime.api.appData;
    const develop = (val, qL, qR) => {
      if (qL === "'" || qR === "'") {
        return `${qL}${String(val).replace(/'/g, "''")}${qR}`;
      }
      const s = String(val);
      if (s.trim() !== "" && Number.isFinite(Number(s))) return s;
      return developObjectValue(val);
    };
    return filterTemplate.replace(
      /('?)##([A-Za-z0-9_]+)##('?)/g,
      (match, qL, name, qR) => {
        try {
          if (name.startsWith("FLD_") || name.startsWith("OWN_")) {
            const val = parentItem[name.slice(4)];
            if (val === void 0 || val === null) return match;
            return develop(val, qL, qR);
          }
          if (typeof appData.getGlobalMacro === "function") {
            const val = appData.getGlobalMacro(name);
            if (val !== void 0 && val !== null && val !== "") {
              return develop(val, qL, qR);
            }
          }
        } catch {
        }
        return match;
      }
    );
  }
  loadExpandChildren(runtime, container, src, filterTemplate, sort, templateHtml, parentItem, cellHeight) {
    let resolvedFilter = filterTemplate;
    if (resolvedFilter.includes("##")) {
      resolvedFilter = this.developExpandFilterMacros(runtime, resolvedFilter, parentItem);
    }
    container.setAttribute("data-expand-lazy", "true");
    const loading = document.createElement("div");
    loading.className = "xone-expand-loading";
    loading.textContent = "Cargando...";
    container.appendChild(loading);
    return (async () => {
      try {
        const coll = runtime.api.appData.getCollection(src);
        await coll.loadAll(resolvedFilter, sort);
        const count = coll.getCount?.() ?? 0;
        container.innerHTML = "";
        for (let i = 0; i < count; i++) {
          const child = coll.get?.(i);
          if (child) {
            this.appendContentRow(container, templateHtml, child, i, cellHeight);
            container.lastElementChild?.setAttribute(
              "data-row-key",
              `l:${String(parentItem["ID"] ?? "")}:${i}`
            );
          }
        }
        applyExpandSelection(container);
      } catch {
        container.innerHTML = "<div class='xone-contents-error' style='padding:8px;font-size:12px;color:#c00;'>Error al cargar</div>";
      }
    })();
  }
  /**
   * Header-contents (lista tipo tabla con `header-contents="<Coll>"`): las
   * celdas van a ancho EXACTO (sin encoger, ver CSS `.xone-contents-headed`),
   * así cada título de columna cae sobre su columna de datos. Cuando la tabla
   * es más ancha que el viewport, el listado (`.xone-prop-z`) scrollea en
   * horizontal; la BANDA de cabecera es un hermano fuera de esa caja, por lo
   * que hay que trasladarla en X junto con el scroll para que siga alineada.
   * Idempotente (una vez por island); listener limpiado en disconnectedCallback.
   */
  wireHeaderScrollSync(box) {
    if (this._headerSyncWired) return;
    const headed = this.closest(".xone-contents-headed");
    if (!headed) return;
    const headerInner = headed.querySelector(".xone-contents-header > *");
    if (!headerInner) return;
    this._headerSyncWired = true;
    const sync = () => {
      headerInner.style.transform = `translateX(${-box.scrollLeft}px)`;
    };
    box.addEventListener("scroll", sync, { passive: true });
    this.cleanup.push(() => box.removeEventListener("scroll", sync));
    sync();
  }
  /**
   * Header-contents: hace que el frame de fila (frmContent) CREZCA con todas
   * sus columnas, para que el fondo/hover de fila abarque toda la tabla (no se
   * corte a media tabla al scrollear). Las columnas usan `width:N%` DEL PROPIO
   * frame → si el frame crece, las celdas (% de él) crecen también → bucle. Se
   * rompe la circularidad reescribiendo cada `width:N%` a
   * `calc(var(--xone-hc-base) * N/100)`, con `--xone-hc-base` = ancho VISIBLE
   * de la caja (referencia FIJA, no el frame). Así las celdas dejan de depender
   * del ancho del frame y el frame/Line (max-content, ver CSS) crecen a la suma.
   * Se re-ejecuta en cada render (filas nuevas); las celdas ya convertidas
   * (calc) se saltan. En `resize` solo se actualiza la base (el calc recomputa).
   */
  applyHeaderedTableWidths(box) {
    const headed = this.closest(".xone-contents-headed");
    if (!headed) return;
    const toFixedBase = (v) => {
      const m = /^([\d.]+)%$/.exec(v);
      return m ? `calc(var(--xone-hc-base) * ${parseFloat(m[1]) / 100})` : null;
    };
    const applyWidths = () => {
      const base = box.clientWidth;
      if (base <= 0) return false;
      headed.style.setProperty("--xone-hc-base", `${base}px`);
      const rowFrame = this.querySelector("article[data-row-index] > .xone-frame");
      const rowH = rowFrame ? rowFrame.getBoundingClientRect().height : 0;
      if (rowH > 0) headed.style.setProperty("--xone-hc-h", `${rowH}px`);
      const cqhToFixedH = (v) => {
        if (rowH <= 0) return null;
        const m = /^([\d.]+)cqh$/.exec(v);
        return m ? `calc(var(--xone-hc-h) * ${parseFloat(m[1]) / 100})` : null;
      };
      const rewriteRow = (row) => {
        if (!row) return;
        for (const cell of Array.from(row.children)) {
          const nw = toFixedBase(cell.style.width);
          if (nw) cell.style.width = nw;
          const nml = toFixedBase(cell.style.marginLeft);
          if (nml) cell.style.marginLeft = nml;
          const nmr = toFixedBase(cell.style.marginRight);
          if (nmr) cell.style.marginRight = nmr;
          const nh = cqhToFixedH(cell.style.height);
          if (nh) cell.style.height = nh;
        }
      };
      rewriteRow(headed.querySelector(".xone-contents-header .xone-row"));
      for (const item of Array.from(this.querySelectorAll("article[data-row-index]"))) {
        rewriteRow(item.querySelector(".xone-row"));
      }
      this.pinHeaderedColumns(headed);
      return true;
    };
    applyWidths();
    if (!this._hcResizeWired && typeof ResizeObserver !== "undefined") {
      this._hcResizeWired = true;
      const ro = new ResizeObserver(() => {
        applyWidths();
      });
      ro.observe(box);
      this.cleanup.push(() => ro.disconnect());
    }
  }
  /**
   * WEAL-97 — alineación estructural por columna de una lista headed.
   *
   * Particiona las celdas de la banda de cabecera y de cada fila en
   * "unidades de columna" (una celda con `margin-left > 0` inicia columna;
   * una celda sin margen va pegada a la anterior — botones de orden, tiras
   * de color…). Si hay mapeo 1:1 cabecera↔fila, fija el inicio de cada
   * unidad de fila a la x del título de su slot ajustando el `margin-left`
   * de su primera celda.
   *
   * WEAL-180 — la decisión de pinear es del CONTENT entero, no fila a fila:
   * solo se pinea si TODAS las filas mapean 1:1 con la cabecera. Si alguna
   * fila no mapea (variabilidad estructural entre tipos de fila por
   * `disablevisible` — content 'Documento' de Alivia), NO se pinea NINGUNA y
   * todas conservan el mismo `calc(var(--xone-hc-base)*N)` de base fija,
   * quedando consistentes entre sí (se evita la mezcla px-fijo/calc() para la
   * misma columna en filas equivalentes). Cero regresión en tablas homogéneas
   * (todas las filas mapean → todas se pinean; `/deudas-list/`, WEAL-97/135).
   *
   * La geometría se mide RELATIVA a la propia `.xone-row` (inmune al
   * translateX del scroll-sync de la cabecera). Cálculo puro en
   * `headerColumnAlign.ts` (testeable sin DOM).
   */
  pinHeaderedColumns(headed) {
    const headerRow = headed.querySelector(".xone-contents-header .xone-row");
    if (!headerRow) return;
    const measure = (row) => {
      const rowLeft = row.getBoundingClientRect().left;
      const cells = [];
      const els = [];
      for (const cell of Array.from(row.children)) {
        const rect = cell.getBoundingClientRect();
        if (rect.width === 0 && rect.height === 0) continue;
        cells.push({
          left: rect.left - rowLeft,
          width: rect.width,
          marginLeft: parseFloat(getComputedStyle(cell).marginLeft) || 0
        });
        els.push(cell);
      }
      return { cells, els };
    };
    const header = measure(headerRow);
    if (header.cells.length === 0) return;
    const rowsGeom = [];
    for (const item of Array.from(this.querySelectorAll("article[data-row-index]"))) {
      const row = item.querySelector(".xone-row");
      if (!row) continue;
      rowsGeom.push(measure(row));
    }
    const tablePins = computeHeaderedTablePins(
      header.cells,
      rowsGeom.map((r) => r.cells)
    );
    if (!tablePins) return;
    for (let r = 0; r < rowsGeom.length; r++) {
      const rowGeom = rowsGeom[r];
      for (const adj of tablePins[r]) {
        const el = rowGeom.els[adj.index];
        const current3 = rowGeom.cells[adj.index].marginLeft;
        if (el && Math.abs(adj.marginLeft - current3) > 0.25) {
          el.style.marginLeft = `${adj.marginLeft}px`;
        }
      }
    }
  }
  /**
   * WEAL-142 / WEAL-144 — deja el contenedor de scroll de un content
   * start-from-bottom pegado al fondo (últimos mensajes visibles).
   *
   * WEAL-144 — el render de un content `fluid-load`/`start-from-bottom` (patrón
   * chat) NO termina en un número fijo de frames: (1) una página fluida de 50
   * filas puede tardar más de 2 frames en asentar el layout en CPUs lentas o bajo
   * carga, y (2) las filas multimedia (imagen/vídeo) crecen su alto REAL solo
   * cuando el navegador termina de descargar y decodificar el recurso —
   * potencialmente cientos de ms DESPUÉS. Con el reintento fijo anterior (snap
   * síncrono + 2 `requestAnimationFrame` ≈ 30 ms) el `scrollHeight` crecía tras el
   * último intento y nadie corregía el scroll: el chat quedaba visualmente por
   * encima del fondo real.
   *
   * Mecanismo (sin números mágicos de tiempo):
   *  (1) Bucle "sticky-to-bottom" por `requestAnimationFrame`: re-pega al fondo
   *      mientras el `scrollHeight` siga cambiando (filas asentando su layout),
   *      hasta que se estabiliza varios frames seguidos o un techo de seguridad.
   *  (2) Espera de medios: engancha `load`/`loadedmetadata`/`error` a las
   *      imágenes/vídeos recién insertados que aún no han decodificado y re-pega
   *      cuando cada uno termina (con techo de seguridad para no colgar listeners).
   *  (3) Cesión de control: si el usuario interactúa con el scroll (rueda/touch/
   *      teclado) y deja de estar pegado al fondo, dejamos de forzar (no le
   *      arrancamos el scroll de las manos).
   *
   * Idempotente y best-effort. Respalda tanto el arranque `start-from-bottom`
   * como `control.scrollToBottom()` (botón "bajar" del chat).
   */
  scrollContainerToBottom(container) {
    let lastSetTop = -1;
    const toBottom = () => {
      try {
        container.scrollTop = container.scrollHeight;
        lastSetTop = container.scrollTop;
      } catch {
      }
    };
    toBottom();
    const hasRaf = typeof requestAnimationFrame === "function";
    const hasTimeout = typeof setTimeout === "function";
    if (!hasRaf && !hasTimeout) return;
    if (!hasRaf) {
      setTimeout(toBottom, 0);
      return;
    }
    const NEAR_BOTTOM_PX = 40;
    let userTookControl = false;
    let done = false;
    const cleanups = [];
    const finish = () => {
      if (done) return;
      done = true;
      for (const c of cleanups) {
        try {
          c();
        } catch {
        }
      }
    };
    const externalWriteDivergesFromBottom = () => {
      if (lastSetTop < 0) return false;
      const maxTop = container.scrollHeight - container.clientHeight;
      const movedByOther = Math.abs(container.scrollTop - lastSetTop) > NEAR_BOTTOM_PX;
      const awayFromBottom = maxTop - container.scrollTop > NEAR_BOTTOM_PX;
      return movedByOther && awayFromBottom;
    };
    const snap = () => {
      if (done || userTookControl) return;
      if (externalWriteDivergesFromBottom()) {
        userTookControl = true;
        finish();
        return;
      }
      toBottom();
    };
    const onUserIntent = () => {
      const maxTop = container.scrollHeight - container.clientHeight;
      if (maxTop - container.scrollTop > NEAR_BOTTOM_PX) {
        userTookControl = true;
        finish();
      }
    };
    for (const ev of ["wheel", "touchmove", "keydown"]) {
      container.addEventListener(ev, onUserIntent, { passive: true });
      cleanups.push(() => container.removeEventListener(ev, onUserIntent));
    }
    let prevHeight = container.scrollHeight;
    let stable = 0;
    let frame = 0;
    const STABLE_FRAMES = 3;
    const MAX_FRAMES = 180;
    const tick = () => {
      if (done) return;
      if (externalWriteDivergesFromBottom()) {
        userTookControl = true;
        finish();
        return;
      }
      frame++;
      const curHeight = container.scrollHeight;
      if (curHeight !== prevHeight) {
        snap();
        stable = 0;
      } else {
        stable++;
      }
      prevHeight = curHeight;
      if (!userTookControl && stable < STABLE_FRAMES && frame < MAX_FRAMES) {
        requestAnimationFrame(tick);
      } else {
        snap();
      }
    };
    requestAnimationFrame(tick);
    let pendingMedia = 0;
    const wireMedia = (el, events) => {
      pendingMedia++;
      const h = () => {
        snap();
      };
      for (const e of events) el.addEventListener(e, h, { once: true });
      cleanups.push(() => {
        for (const e of events) el.removeEventListener(e, h);
      });
    };
    container.querySelectorAll("img").forEach((img) => {
      if (!(img.complete && img.naturalHeight !== 0)) wireMedia(img, ["load", "error"]);
    });
    container.querySelectorAll("video").forEach((vid) => {
      if (vid.readyState < 1) wireMedia(vid, ["loadedmetadata", "error"]);
    });
    if (hasTimeout) {
      setTimeout(finish, pendingMedia > 0 ? 4e3 : 1e3);
    }
  }
  applyContentsLayout(container, viewmode, columns, orientation, _bgcolor, galleryExplicit = false) {
    container.classList.add("xone-contents");
    const galleryGrid = viewmode === "gridview" && orientation === "horizontal" && galleryExplicit && columns > 1;
    if (viewmode === "expanview" || viewmode === "expandview") {
      container.classList.add("is-expandview");
      container.style.display = "flex";
      container.style.flexDirection = "column";
      container.style.overflowY = "auto";
    } else if (viewmode === "gridview" && orientation === "horizontal" && !galleryGrid) {
      container.classList.add("is-gridview");
      container.style.display = "flex";
      container.style.flexDirection = "row";
      container.style.flexWrap = "nowrap";
      container.style.overflowX = "auto";
      container.style.overflowY = "hidden";
      container.dataset["orientation"] = "horizontal";
      container.style.gap = "0";
    } else if (viewmode === "gridview") {
      container.classList.add("is-gridview");
      if (galleryGrid) {
        container.classList.add("is-gallery");
        container.dataset["orientation"] = "vertical";
        container.style.overflowX = "hidden";
        container.style.alignItems = "start";
      }
      container.style.display = "grid";
      container.style.gridTemplateColumns = `repeat(${columns > 0 ? columns : 2}, minmax(0, 1fr))`;
      container.style.gap = "0";
      container.style.alignContent = "start";
      container.style.gridAutoRows = "min-content";
    } else {
      container.classList.add("is-recyclerview");
      container.style.display = "flex";
      if (orientation === "horizontal") {
        container.style.flexDirection = "row";
        container.style.overflowX = "auto";
        container.style.overflowY = "hidden";
        container.style.scrollSnapType = "x mandatory";
        container.dataset["orientation"] = "horizontal";
      } else {
        container.style.flexDirection = "column";
        container.dataset["orientation"] = "vertical";
      }
      container.style.gap = "0";
    }
    const declaredMaxH = this.dataset["xoneMaxHeight"] ?? (this.style.maxHeight && this.style.maxHeight !== "100%" ? this.style.maxHeight : "");
    if (declaredMaxH) {
      this.dataset["xoneMaxHeight"] = declaredMaxH;
      this.style.maxHeight = declaredMaxH;
    } else {
      this.style.maxHeight = "100%";
    }
    this.style.minHeight = "0";
    this.style.overflow = "hidden";
    container.style.maxHeight = "100%";
    container.style.minHeight = "0";
    if (container.dataset["orientation"] !== "horizontal") {
      if (!container.style.height) container.style.height = "100%";
      container.style.overflowY = "auto";
      this.wireOverflowAffordance(container);
    }
  }
  /**
   * WEAL-125 — marca `data-overflowing="true"` en el contenedor de un contents
   * cuyo contenido NO cabe en su caja (`height="N%"` del .xne resuelto por el
   * navegador, ver `applyContentsLayout`). La affordance visual real la da el
   * scrollbar declarado en el CSS baseline (`.xone-contents:not([data-orientation
   * ="horizontal"])`); este atributo es el refuerzo (carril de scroll tenue) y,
   * sobre todo, la marca ESTABLE que QA puede asertar sin depender de la
   * captura de píxeles del scrollbar nativo (que varía por SO/navegador).
   *
   * Lectura de layout (`scrollHeight`/`clientHeight`) coalescida en un rAF para
   * no forzar un reflow por cada fila insertada en un grid grande.
   */
  updateOverflowState(container) {
    const overflowing = container.scrollHeight - container.clientHeight > 1;
    if (overflowing) container.setAttribute("data-overflowing", "true");
    else container.removeAttribute("data-overflowing");
  }
  wireOverflowAffordance(container) {
    if (this._ovfWired) return;
    this._ovfWired = true;
    let scheduled = false;
    const update = () => {
      if (scheduled) return;
      scheduled = true;
      const run = () => {
        scheduled = false;
        this.updateOverflowState(container);
      };
      if (typeof requestAnimationFrame === "function") requestAnimationFrame(run);
      else run();
    };
    update();
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(update);
      ro.observe(container);
      this.cleanup.push(() => ro.disconnect());
    }
    if (typeof MutationObserver !== "undefined") {
      const mo = new MutationObserver(update);
      mo.observe(container, { childList: true });
      this.cleanup.push(() => mo.disconnect());
    }
  }
  /**
   * Expande macros ##FLD_X## en atributos DOM de un elemento ya clonado.
   * Paridad Android FieldPropertyValue(prop, attr, bEvaluate=true) →
   * ReplaceFieldValueMacros (XoneDataObject.java:5199-5256).
   *
   * Opera a nivel DOM (no string) para poder ser selectivo: expande
   * src, style, title pero preserva data-reactive-attrs (que necesita
   * el template original para re-evaluación futura).
   */
  expandItemFldMacros(el, item) {
    const fldRe = /##FLD_([A-Za-z0-9_]+)##/g;
    const resolve = (s) => s.replace(fldRe, (_m, field) => {
      const val = item[field];
      return val !== void 0 && val !== null ? String(val) : "";
    });
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    for (let tn = walker.nextNode(); tn !== null; tn = walker.nextNode()) {
      const txt = tn.nodeValue;
      if (txt !== null && txt.includes("##FLD_")) {
        tn.nodeValue = resolve(txt);
      }
    }
    for (const node of [el, ...el.querySelectorAll("[data-reactive-attrs]")]) {
      if (!(node instanceof HTMLElement)) continue;
      const rawDra = node.getAttribute("data-reactive-attrs");
      if (rawDra === null) continue;
      let bindings;
      try {
        bindings = JSON.parse(rawDra);
      } catch {
        continue;
      }
      let allResolved = true;
      for (const b of bindings) {
        if (!b || typeof b.tpl !== "string") continue;
        const resolved = resolve(b.tpl);
        if (resolved.includes("##")) {
          allResolved = false;
          continue;
        }
        this.applyRowReactiveAttr(node, b.attr, resolved);
      }
      if (allResolved) node.removeAttribute("data-reactive-attrs");
    }
    const ATTRS_TO_EXPAND = ["src", "style", "title", "alt"];
    const allEls = el.querySelectorAll("*");
    const targets = [el, ...allEls];
    for (const node of targets) {
      if (!(node instanceof HTMLElement)) continue;
      for (const attrName of ATTRS_TO_EXPAND) {
        const val = node.getAttribute(attrName);
        if (val && val.includes("##FLD_")) {
          const resolved = resolve(val);
          node.setAttribute(attrName, resolved);
        }
      }
      if (node.tagName === "IMG") {
        const tpl = node.getAttribute("data-xone-img-src");
        if (tpl !== null) {
          const resolved = resolve(tpl).trim();
          node.removeAttribute("data-xone-img-src");
          if (resolved === "" || resolved.includes("##")) {
            this.hideBrokenImage(node);
          } else {
            node.src = this.normalizeImgSrc(resolved);
            node.style.removeProperty("visibility");
            node.classList.remove("xone-img-empty");
          }
          continue;
        }
        const src = node.getAttribute("src") || "";
        if (src.trim() === "" || src.includes("##")) {
          this.hideBrokenImage(node);
        } else if (!src.startsWith("/") && !src.startsWith("http") && !src.startsWith("data:")) {
          node.src = this.normalizeImgSrc(src);
        }
      }
    }
  }
  /**
   * Binding de VALOR de un `[data-prop]` de una fila de `<contents>` (ruta
   * recyclerview de `appendContentRow`). WEAL-302 (r2): extraído para que el
   * parche in situ de `patchRowAfterButton` repinte EXACTAMENTE igual que un
   * rebuild.
   */
  bindRowPropEl(el, item) {
    const field = el.getAttribute("data-prop");
    if (!field) return;
    if (el.getAttribute("data-xone-type") === "Z" || el.getAttribute("data-type") === "Z") return;
    if (el.getAttribute("data-type") === "frame" || el.hasAttribute("data-xone-frame")) return;
    const value = item[field];
    if (value === void 0 || value === null) return;
    const str = String(value);
    const rowDateFt = _XoneIslandElement.dateFieldTypeOf(el);
    const dispStr = rowDateFt ? formatXoneDisplayDate(value, rowDateFt) : str;
    if (el.getAttribute("data-xone-static") === "true" && str === "" && (el.textContent ?? "") !== "") return;
    if (el.tagName !== "IMG" && el.querySelector("img") === null && this.bindImgPlaceholderValue(el, str)) return;
    const labelEl = el.querySelector(".xone-label-text");
    if (labelEl) {
      labelEl.textContent = dispStr;
      return;
    }
    const textEl = el.querySelector(".xone-text-value");
    if (textEl) {
      textEl.textContent = dispStr;
      return;
    }
    const img = el.tagName === "IMG" ? el : el.querySelector("img");
    if (img && str) {
      img.src = this.normalizeImgSrc(str);
      return;
    }
    const input = el.querySelector("input,textarea");
    if (input) {
      const inp = input;
      if (inp.type === "checkbox" || inp.type === "radio" || el.getAttribute("data-xone-type") === "NC") {
        inp.checked = str !== "" && str !== "0" && str.toLowerCase() !== "false";
      } else {
        inp.value = _XoneIslandElement.normalizeDateInputValue(inp, str);
      }
      return;
    }
    if (el.tagName === "SPAN" || el.tagName === "DIV") {
      el.textContent = dispStr;
    }
  }
  appendContentRow(container, templateHtml, item, index, cellHeight) {
    const row = document.createElement("article");
    row.className = "xone-content-item xone-contents-row";
    row.innerHTML = templateHtml;
    this.recomputeRowDisplayFields(row, item);
    this.expandItemFldMacros(row, item);
    if (container.classList.contains("is-gridview")) {
      if (container.classList.contains("is-gallery")) {
        row.style.minWidth = "0";
        for (const child of row.children) {
          if (!(child instanceof HTMLElement)) continue;
          child.style.width = "auto";
          child.style.alignSelf = "stretch";
          if (child.style.height && child.style.height !== "auto")
            child.style.height = "auto";
          if (child.style.containerType === "size")
            child.style.containerType = "inline-size";
        }
      } else if (container.dataset["orientation"] === "horizontal") {
        for (const child of row.children) {
          if (child instanceof HTMLElement && child.style.width && child.style.width !== "auto") {
            row.style.flex = `0 0 ${child.style.width}`;
            row.style.width = child.style.width;
            child.style.width = "100%";
            row.style.containerType = "size";
            break;
          }
        }
      } else {
        row.style.minWidth = "0";
        for (const child of row.children) {
          if (!(child instanceof HTMLElement)) continue;
          const declared = child.style.width;
          if (declared === "") continue;
          if (declared.endsWith("%")) continue;
          child.style.width = "auto";
          child.style.alignSelf = "stretch";
        }
      }
    }
    if (cellHeight && cellHeight !== "-2") {
      const numMatch = cellHeight.match(/^(\d+)p$/);
      if (numMatch) {
        row.style.height = `calc(${numMatch[1]} * var(--xone-p-unit, 1px))`;
        row.style.flexShrink = "0";
        row.style.overflow = "hidden";
      }
    }
    const propEls = row.querySelectorAll("[data-prop]");
    for (const el of propEls) this.bindRowPropEl(el, item);
    const zebraHost = container.closest("[data-cell-even-color]");
    const evenColor = zebraHost?.getAttribute("data-cell-even-color") ?? "";
    const oddColor = zebraHost?.getAttribute("data-cell-odd-color") ?? "";
    if (evenColor !== "" && oddColor !== "") {
      row.style.backgroundColor = xoneColorToCss(index % 2 === 0 ? evenColor : oddColor);
    }
    this.applyRowDisableVisible(row, item);
    this.applyRowDisableEdit(row, item);
    this.inheritContentsEditLock(row);
    this.applyRowPropOverrides(row, item);
    row.setAttribute("data-row-index", String(index));
    setRowItem(row, item);
    container.appendChild(row);
  }
  resolveMacros(runtime, filter) {
    if (!filter || !filter.includes("##")) return filter;
    const macroPattern = /('?)##([A-Za-z0-9_]+)##('?)/g;
    const appData = runtime.api.appData;
    const self = runtime.api.self;
    let result = filter;
    if (typeof appData.expandMacros === "function") {
      result = appData.expandMacros(result);
    }
    if (result.includes("##")) {
      result = result.replace(macroPattern, (_match, qL, name, qR) => {
        try {
          if ((name.startsWith("FLD_") || name.startsWith("OWN_")) && self) {
            const fieldName = name.slice(4);
            const val = typeof self.getValue === "function" ? self.getValue(fieldName) : self[fieldName];
            if (val !== void 0 && val !== null && val !== "") {
              return developObjectValue(val);
            }
            if (val === "") return `${qL}${qR}`;
            if (typeof appData.getGlobalMacro === "function") {
              const macroVal = appData.getGlobalMacro(fieldName);
              if (macroVal !== void 0 && macroVal !== null) return `${qL}${String(macroVal)}${qR}`;
            }
          }
          if (typeof appData.getGlobalMacro === "function") {
            const val = appData.getGlobalMacro(name);
            if (val !== void 0 && val !== null) return `${qL}${String(val)}${qR}`;
          }
        } catch {
        }
        return `${qL}##${name}##${qR}`;
      });
    }
    return result;
  }
  /**
   * WEAL-110 — marca el contents como SELECCIONABLE (`data-selectable="true"`)
   * solo si un click de fila hace algo de verdad. El cursor (`pointer` vs el
   * default de `.xone-content-item`) lo decide la CSS base a partir de este
   * atributo; el runtime NO fija ya `cursor` inline, así que una regla CSS de
   * mayor especificidad puede excluir las zonas editables sin `!important`.
   *
   * Es seleccionable cuando:
   *  - hay `<selecteditem>` con script REAL (no el boilerplate de Studio con
   *    el cuerpo vacío que arrastran los ~30 contents de detalle de actividad
   *    de Alivia — ese consume el click y no hace nada: no es navegación), o
   *  - no hay `<selecteditem>` pero aplica el fallback WEAL-14 (fila del
   *    CUERPO, sin candado de nivel-host, cuya coll tiene ficha generada).
   *
   * El route map se puebla de forma ASÍNCRONA desde el manifest, así que el
   * gate del fallback se re-evalúa al pasar el ratón por encima (que es
   * justo cuando el cursor importa) en vez de dejar la fila mal marcada por
   * una carrera de hidratación.
   */
  applySelectableCursor(runtime, propName, container) {
    const contentsName = this.getAttribute("data-contents-name") ?? "";
    const sanitized = contentsName.replace(/^@/, "_");
    const srcColl = this.getAttribute("data-src") ?? "";
    const handler = (sanitized ? runtime.events[`${sanitized}_selecteditem`] : void 0) ?? runtime.events[`${propName}_selecteditem`] ?? (srcColl ? runtime.eventsByColl?.[srcColl]?.["selecteditem"] : void 0) ?? runtime.events["selecteditem"];
    const hasHandler = typeof handler === "function";
    const childSrcForCursor = this.getAttribute("data-child-src") ?? container?.getAttribute?.("data-child-src") ?? "";
    const childSelHandler = childSrcForCursor ? runtime.eventsByColl?.[childSrcForCursor]?.["selecteditem"] : void 0;
    const childMeaningful = typeof childSelHandler === "function" && !isEmptyEventHandler(childSelHandler);
    const meaningful = hasHandler && !isEmptyEventHandler(handler) || childMeaningful;
    const update = () => {
      let selectable = meaningful;
      if (!selectable && !hasHandler && srcColl !== "") {
        const fromLayout = !this.closest("[data-xone-router-slot]");
        if (!fromLayout && contentsEditMask(this) !== 0) {
          const ui = runtime.api.ui;
          selectable = typeof ui.hasEditView === "function" && ui.hasEditView(srcColl);
        }
      }
      if (selectable) container.setAttribute("data-selectable", "true");
      else container.removeAttribute("data-selectable");
    };
    update();
    if (!container.hasAttribute("data-selectable") && !hasHandler && srcColl !== "") {
      const onOver = () => update();
      container.addEventListener("pointerover", onOver);
      this.cleanup.push(() => container.removeEventListener("pointerover", onOver));
    }
  }
  wireSelectedItem(runtime, propName, container) {
    if (this._selectedItemWired) return;
    const contentsName = this.getAttribute("data-contents-name") ?? "";
    const sanitized = contentsName.replace(/^@/, "_");
    const srcColl = this.getAttribute("data-src") ?? "";
    const scopedHandler = (sanitized ? runtime.events[`${sanitized}_selecteditem`] : void 0) ?? runtime.events[`${propName}_selecteditem`] ?? (srcColl ? runtime.eventsByColl?.[srcColl]?.["selecteditem"] : void 0);
    const handler = scopedHandler ?? runtime.events["selecteditem"];
    const hasHandler = typeof handler === "function";
    if (!hasHandler && !srcColl) return;
    const target = container ?? this;
    const onClick = (e) => {
      const evTarget = e.target;
      if (evTarget?.closest?.('[data-stop-propagation="true"]')) return;
      if (evTarget?.closest?.("button, input, select, textarea, a[href]")) return;
      const row = evTarget.closest?.(".xone-contents-row, .xone-expand-summary");
      if (!row) return;
      const index = Number(row.getAttribute("data-row-index") ?? "0");
      const isExpandSummary = row.classList.contains("xone-expand-summary");
      const isExpandChildRow = !!row.closest('[data-expand-lazy="true"]');
      const childSrc = isExpandChildRow ? target.getAttribute?.("data-child-src") ?? "" : "";
      const rawChildHandler = childSrc ? runtime.eventsByColl?.[childSrc]?.["selecteditem"] : void 0;
      const childHandler = typeof rawChildHandler === "function" && !isEmptyEventHandler(rawChildHandler) ? rawChildHandler : void 0;
      const effHandler = childHandler ?? handler;
      const effHasHandler = typeof effHandler === "function";
      if (isExpandSummary && (typeof scopedHandler !== "function" || isEmptyEventHandler(scopedHandler))) return;
      const expandChildHost = row instanceof HTMLElement ? row.closest(".xone-expand-children")?.closest("[data-child-cell-selected-bgcolor]") : null;
      if (row instanceof HTMLElement && expandChildHost) {
        const color = expandChildHost.getAttribute("data-child-cell-selected-bgcolor") ?? "";
        const childId = String((getRowItem(row) ?? expandRowItem(row))?.["ID"] ?? "");
        const selKey = row.closest("[data-expand-sel-key]")?.getAttribute("data-expand-sel-key") ?? "";
        if (selKey && childId) {
          try {
            sessionStorage.setItem(selKey, childId);
          } catch {
          }
        }
        paintExpandSelected(expandChildHost, row, color);
      } else if (row instanceof HTMLElement) {
        const selectedBgColor = (container ?? this).closest("[data-cell-selected-bgcolor]")?.getAttribute("data-cell-selected-bgcolor") ?? "";
        if (selectedBgColor !== "") {
          this._scriptSelectedIndex = null;
          this.paintSelectedRow(row, selectedBgColor);
        }
      }
      const fromLayout = !this.closest("[data-xone-router-slot]");
      const shellOverlay = runtime.api.shellOverlay;
      if (!effHasHandler) {
        if (fromLayout) return;
        if (contentsEditMask(this) === 0) return;
        openRowEditViewFallback(runtime, srcColl, index, contentsName);
        return;
      }
      void (async () => {
        if (fromLayout) shellOverlay?.beginSelection();
        try {
          let itemSelf = void 0;
          if (isExpandChildRow || isExpandSummary) {
            itemSelf = getRowItem(row);
          }
          if (itemSelf === void 0 && srcColl) {
            const coll = resolveContentsColl(
              runtime.api.appData,
              srcColl,
              contentsName
            );
            itemSelf = coll?.get?.(index);
          }
          const selfForGlobals = itemSelf && typeof itemSelf === "object" ? itemSelf : runtime.api.self;
          const restoreSelf = setXoneGlobalSelf(selfForGlobals);
          try {
            if (itemSelf && typeof itemSelf === "object") {
              const itemApi = {
                ...runtime.api,
                self: itemSelf,
                This: itemSelf
              };
              await effHandler(itemApi, index);
            } else {
              await effHandler(runtime.api, index);
            }
            maybeOpenPushedObject(runtime, fromLayout);
          } finally {
            restoreSelf();
          }
        } catch (err) {
          try {
            runtime.api.console.error("[xone-island] selecteditem error", err);
          } catch {
          }
        } finally {
          if (fromLayout) shellOverlay?.endSelection();
        }
      })();
    };
    target.addEventListener("click", onClick);
    this.cleanup.push(() => target.removeEventListener("click", onClick));
    this._selectedItemWired = true;
    void propName;
  }
};
function xoneColorToCss(raw) {
  const c = raw.trim();
  const m = /^#([0-9a-fA-F]{8})$/.exec(c);
  if (m === null) return c;
  const hex = m[1];
  const a = parseInt(hex.slice(0, 2), 16) / 255;
  const r = parseInt(hex.slice(2, 4), 16);
  const g = parseInt(hex.slice(4, 6), 16);
  const b = parseInt(hex.slice(6, 8), 16);
  return `rgba(${r}, ${g}, ${b}, ${a.toFixed(3)})`;
}
function isDataObjectLike(v) {
  if (v === null || typeof v !== "object") return false;
  const o = v;
  return typeof o.getValue === "function" && typeof o.getOwnerCollection === "function";
}
function openRowEditViewFallback(runtime, srcColl, index, contentsName) {
  try {
    const ui = runtime.api.ui;
    if (typeof ui.hasEditView !== "function" || !ui.hasEditView(srcColl)) return;
    const coll = resolveContentsColl(
      runtime.api.appData,
      srcColl,
      contentsName
    );
    const obj = coll?.get?.(index);
    if (!isDataObjectLike(obj)) return;
    const appData = runtime.api.appData;
    if (typeof appData.pushValue !== "function") return;
    appData.pushValue(obj);
    maybeOpenPushedObject(runtime, false);
  } catch (err) {
    try {
      runtime.api.console.error("[xone-island] selecteditem fallback error", err);
    } catch {
    }
  }
}
function maybeOpenPushedObject(runtime, fromLayout) {
  try {
    const appData = runtime.api.appData;
    if (typeof appData.popValue !== "function") return;
    const v = appData.popValue();
    if (v === void 0 || v === null) return;
    if (!isDataObjectLike(v)) {
      appData.pushValue?.(v);
      return;
    }
    const ui = runtime.api.ui;
    ui.openEditView?.(v, fromLayout ? { rootSwitch: true } : void 0);
  } catch {
  }
}
var xoneSelfInvocationSeq = 0;
function setXoneGlobalSelf(self) {
  try {
    const w = typeof window !== "undefined" ? window : globalThis;
    const setter = w["__xoneSetSelf"];
    const getter = w["__xoneGetSelf"];
    if (typeof setter !== "function") return () => {
    };
    const readSelf = () => typeof getter === "function" ? getter() : void 0;
    const prev = readSelf();
    const token = ++xoneSelfInvocationSeq;
    setter(self);
    let restored = false;
    return () => {
      if (restored) return;
      restored = true;
      try {
        const current3 = readSelf();
        if (current3 === self) {
          setter(prev);
        } else {
          try {
            w["console"]?.warn?.(
              "[xone-island] self ambiente descuadrado (WEAL-127): restore omitido para no pisar una invocaci\xF3n m\xE1s reciente. Causa probable: un handler de proyecto lanz\xF3 trabajo as\xEDncrono sin esperarlo (p.ej. $http.post en modo callback).",
              { token }
            );
          } catch {
          }
        }
      } catch {
      }
    };
  } catch {
    return () => {
    };
  }
}
async function invokeHandler(runtime, handler, args, apiOverride) {
  const api = apiOverride ?? runtime.api;
  const restoreSelf = setXoneGlobalSelf(api.self);
  try {
    await handler(api, ...args);
    maybeOpenPushedObject(runtime);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    try {
      runtime.api.console.error("[xone-island] handler error:", msg, err);
    } catch {
      console.error("[xone-island] handler error:", err);
    }
    if (!isSessionLostError(err)) {
      try {
        const ui = runtime.api.ui;
        ui?.showToast?.(`Error: ${msg}`);
      } catch {
      }
    }
  } finally {
    restoreSelf();
  }
}
function isSessionLostError(err) {
  return !!err && typeof err === "object" && err.code === "session_lost";
}
async function runInteractiveHandler(runtime, run) {
  const ir = runtime.api.interactiveReady;
  if (ir && typeof ir.then === "function") {
    try {
      await ir;
    } catch {
    }
  }
  await run();
}
function dispatchFieldOnchangeFromModel(runtime, self, collName, field, value) {
  const key = `${field}_onchange`;
  const fromColl = collName ? runtime.eventsByColl?.[collName]?.[key] : void 0;
  const handler = typeof fromColl === "function" ? fromColl : runtime.events?.[key];
  if (typeof handler !== "function") return;
  const api = self && typeof self === "object" ? { ...runtime.api, self, This: self } : runtime.api;
  void invokeHandler(runtime, handler, [value], api);
}
async function runXoneNode(name, extraArgs) {
  if (typeof window === "undefined") return;
  const runtime = window[RUNTIME_KEY];
  if (!runtime) return;
  let nodeName = String(name ?? "").trim();
  let args = extraArgs ?? [];
  const m = /^([^(]+)\((.*)\)\s*$/.exec(nodeName);
  if (m && m[1] !== void 0) {
    nodeName = m[1].trim();
    const inner = (m[2] ?? "").trim();
    if (inner !== "" && (extraArgs === void 0 || extraArgs.length === 0)) {
      args = inner.split(",").map((raw) => {
        const t = raw.trim().replace(/^["']|["']$/g, "");
        const n = Number(t);
        return t !== "" && !Number.isNaN(n) ? n : t;
      });
    }
  }
  if (nodeName === "") return;
  let handler = runtime.events?.[nodeName];
  if (typeof handler !== "function") {
    const collName = window.__xone?.collection ?? "";
    const fromColl = collName ? runtime.eventsByColl?.[collName]?.[nodeName] : void 0;
    if (typeof fromColl === "function") handler = fromColl;
  }
  if (typeof handler === "function") {
    await invokeHandler(runtime, handler, args);
    return;
  }
  const g = typeof window !== "undefined" ? window : globalThis;
  const globalFn = g[nodeName];
  if (typeof globalFn === "function") {
    await invokeHandler(
      runtime,
      () => globalFn(runtime.api.self, ...args),
      []
    );
    return;
  }
  try {
    runtime.api.console.warn(`[xone] executeActionAfterDelay: acci\xF3n '${nodeName}' no encontrada (ni nodo ni funci\xF3n global)`);
  } catch {
  }
}
async function runXoneCollLoad(collName, items) {
  if (typeof window === "undefined") return false;
  const runtime = window[RUNTIME_KEY];
  if (!runtime || !collName || items.length === 0) return false;
  const handler = runtime.eventsByColl?.[collName]?.["load"];
  if (typeof handler !== "function") return false;
  const fn = handler;
  for (const item of items) {
    if (!item || typeof item !== "object") continue;
    const restoreSelf = setXoneGlobalSelf(item);
    try {
      const itemApi = {
        ...runtime.api,
        self: item,
        This: item
      };
      await fn(itemApi);
    } catch (err) {
      try {
        runtime.api.console.error(
          `[xone-island] <load> de '${collName}' lanzo:`,
          err
        );
      } catch {
      }
    } finally {
      restoreSelf();
    }
  }
  return true;
}
function markOnchangeOnLoadFired(item) {
  if (!item || typeof item !== "object") return;
  if (item.__xoneOnchangeOnLoadFired === true) return;
  try {
    Object.defineProperty(item, "__xoneOnchangeOnLoadFired", {
      value: true,
      enumerable: false,
      writable: false,
      configurable: false
    });
  } catch {
  }
}
async function runXoneCollOnchangeOnLoad(collName, items) {
  if (typeof window === "undefined") return false;
  const runtime = window[RUNTIME_KEY];
  if (!runtime || !collName || items.length === 0) return false;
  const mod = runtime.eventsByColl?.[collName];
  if (!mod) return false;
  const SUFFIX = "_onchange";
  const onchangeFields = [];
  for (const key of Object.keys(mod)) {
    if (!key.endsWith(SUFFIX)) continue;
    const h = mod[key];
    if (typeof h !== "function") continue;
    onchangeFields.push({
      field: key.slice(0, -SUFFIX.length),
      handler: h
    });
  }
  if (onchangeFields.length === 0) return false;
  for (const item of items) {
    if (!item || typeof item !== "object") continue;
    if (item.__xoneOnchangeOnLoadFired === true) {
      continue;
    }
    const row = item;
    const restoreSelf = setXoneGlobalSelf(item);
    try {
      const itemApi = {
        ...runtime.api,
        self: item,
        This: item
      };
      for (const { field, handler } of onchangeFields) {
        const hasStored = item.hasStoredValue;
        if (typeof hasStored === "function") {
          let stored = false;
          try {
            stored = hasStored.call(item, field) === true;
          } catch {
            stored = false;
          }
          if (!stored) continue;
        }
        const value = row[field];
        if (value === void 0 || value === null) continue;
        try {
          await handler(itemApi, value);
        } catch (err) {
          try {
            runtime.api.console.warn(
              `[xone-island] <onchange> de '${collName}.${field}' no es load-safe (se omite en carga):`,
              err
            );
          } catch {
          }
        }
      }
    } finally {
      restoreSelf();
    }
    markOnchangeOnLoadFired(item);
  }
  return true;
}
var elementRegistered = false;
function registerXoneIsland(opts) {
  if (typeof window === "undefined") {
    return;
  }
  const runtime = {
    api: opts.api,
    events: opts.events,
    ...opts.eventsByColl ? { eventsByColl: opts.eventsByColl } : {}
  };
  window[RUNTIME_KEY] = runtime;
  window.__xoneRunNode = runXoneNode;
  window.__xoneRunCollLoad = runXoneCollLoad;
  window.__xoneRunCollOnchangeOnLoad = runXoneCollOnchangeOnLoad;
  window.__xoneDispatchFieldOnchange = (self, collName, field, value) => dispatchFieldOnchangeFromModel(runtime, self, collName, field, value);
  const layoutOnback = opts.layoutColl ? opts.eventsByColl?.[opts.layoutColl]?.["onback"] : void 0;
  if (typeof layoutOnback === "function") {
    window.__xoneLayoutOnback = () => {
      void invokeHandler(runtime, layoutOnback, []);
    };
  } else {
    try {
      delete window.__xoneLayoutOnback;
    } catch {
      window.__xoneLayoutOnback = void 0;
    }
  }
  if (elementRegistered) return;
  elementRegistered = true;
  if (typeof customElements !== "undefined" && !customElements.get(ELEMENT_TAG)) {
    customElements.define(ELEMENT_TAG, XoneIslandElement);
  }
}
function isInHiddenAncestor(el) {
  let node = el;
  while (node) {
    if (node instanceof HTMLElement) {
      if (node.hasAttribute("hidden")) return true;
      const cs = node.ownerDocument?.defaultView?.getComputedStyle(node);
      if (cs && cs.display === "none") return true;
    }
    node = node.parentElement;
  }
  return false;
}
function hydrateVisibleContentsNow() {
  if (typeof document === "undefined" || typeof window === "undefined") return;
  const vh = window.innerHeight || 0;
  const vw = window.innerWidth || 0;
  const nodes = document.querySelectorAll(ELEMENT_TAG);
  for (const el of Array.from(nodes)) {
    if (!(el instanceof XoneIslandElement)) continue;
    if (!el.hasAttribute("data-src")) continue;
    if ((el.getAttribute("data-type") ?? "").toLowerCase() === "widget") continue;
    if (isInHiddenAncestor(el)) continue;
    const r = el.getBoundingClientRect();
    const inView = r.bottom > 0 && r.top < vh && r.right > 0 && r.left < vw && r.width > 0 && r.height > 0;
    if (!inView) continue;
    el.hydrateNow();
  }
}
function _resetXoneIslandForTests() {
  if (typeof window !== "undefined") {
    delete window[RUNTIME_KEY];
  }
  delete globalThis.__xoneInRowEdit;
  PAGE_EXPAND_OPEN.clear();
  elementRegistered = false;
}

// src/XoneCombo.ts
var RUNTIME_KEY2 = "__xoneIslandRuntime";
var SEARCH_THRESHOLD = 10;
var HYDRATED2 = /* @__PURE__ */ Symbol("xone-combo-hydrated");
var ELEMENT_TAG2 = "xone-combo";
function isBlankLinkedId(v) {
  if (v === null || v === void 0) return true;
  if (typeof v === "number") return !Number.isFinite(v) || v <= 0;
  const s = String(v).trim();
  if (s === "") return true;
  if (/^[+-]?\d+(?:\.\d+)?$/.test(s)) return Number(s) <= 0;
  return false;
}
function isBlankStaticIndex(v) {
  if (v === null || v === void 0) return true;
  return String(v).trim() === "";
}
var XoneComboElement = class extends HTMLElement {
  [HYDRATED2] = false;
  cleanup = [];
  /** Listeners atados mientras el panel está abierto (se limpian en closePanel). */
  panelCleanup = [];
  options = [];
  optionsLoaded = false;
  panel = null;
  lastFilter = null;
  /**
   * WEAL-136: colección lookup PRIVADA de esta instancia (clon del singleton
   * compartido por nombre). Aísla el filtro `##FLD_x##` por fila y evita
   * heredar el estado `locked` de otro consumidor. `null` hasta la 1ª carga.
   */
  lookupColl = null;
  /** Apertura async en vuelo (guard de reentrancia). */
  opening = false;
  /** Restauración de espejos en curso (guard de reentrancia de restoreMappedFields). */
  restoring = false;
  connectedCallback() {
    if (this[HYDRATED2]) return;
    this.tryHydrate(0);
  }
  disconnectedCallback() {
    this.closePanel();
    for (const fn of this.cleanup) {
      try {
        fn();
      } catch {
      }
    }
    this.cleanup = [];
  }
  tryHydrate(attempt) {
    const runtime = window[RUNTIME_KEY2];
    if (!runtime) {
      if (attempt > 40) return;
      const t = window.setTimeout(() => this.tryHydrate(attempt + 1), 250);
      this.cleanup.push(() => window.clearTimeout(t));
      return;
    }
    if (this[HYDRATED2]) return;
    this[HYDRATED2] = true;
    try {
      this.hydrate(runtime);
      this.setAttribute("hydrated", "");
    } catch (err) {
      try {
        runtime.api.console.error("[xone-combo] hydrate", err);
      } catch {
      }
    }
  }
  // ---------- atributos ----------
  get displayProp() {
    return this.getAttribute("data-prop") ?? "";
  }
  get idProp() {
    return this.getAttribute("data-combo-idprop") ?? "";
  }
  get linkedfield() {
    return this.getAttribute("data-combo-linkedfield") ?? "";
  }
  get mapfld() {
    return this.getAttribute("data-combo-mapfld") ?? "";
  }
  get coll() {
    return this.getAttribute("data-combo-coll") ?? "";
  }
  get filter() {
    return this.getAttribute("data-combo-filter") ?? "";
  }
  get mode() {
    return this.getAttribute("data-combo-mode") ?? "inline";
  }
  get isStatic() {
    return this.getAttribute("data-combo-coll") === null;
  }
  get hasSearch() {
    return this.mode === "picker" || this.getAttribute("data-combo-search") === "true";
  }
  get hasCleaner() {
    return this.getAttribute("data-combo-cleaner") === "true";
  }
  get defaultText() {
    return this.getAttribute("data-combo-default") ?? "";
  }
  get idIsNumeric() {
    return this.getAttribute("data-combo-id-numeric") === "true";
  }
  /**
   * WEAL-38: modo locked/display-only — combo-display bloqueada (p.ej.
   * MAP_G_LIDER con clase `propEdicionBloqueado`, emitida por
   * `renderLockedComboDisplay`). CERO interacción (garantía WEAL-13/T04).
   */
  get isLocked() {
    return this.getAttribute("data-combo-locked") === "true";
  }
  get mirrors() {
    const raw = this.getAttribute("data-combo-mirrors");
    if (!raw) return [{ prop: this.displayProp, linkedfield: this.linkedfield }];
    try {
      const arr = JSON.parse(raw);
      return Array.isArray(arr) && arr.length > 0 ? arr : [{ prop: this.displayProp, linkedfield: this.linkedfield }];
    } catch {
      return [{ prop: this.displayProp, linkedfield: this.linkedfield }];
    }
  }
  get staticValues() {
    const raw = this.getAttribute("data-combo-values");
    if (!raw) return [];
    try {
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr : [];
    } catch {
      return [];
    }
  }
  // ---------- binding target ----------
  resolveTarget(runtime) {
    const row = this.closest(".xone-contents-row");
    if (!row) return runtime.api.self ?? {};
    const host = this.closest('xone-island[data-type="Z"]');
    const src = host?.getAttribute("data-src") ?? "";
    const idx = Number(row.getAttribute("data-row-index") ?? "0");
    const coll = src ? resolveContentsColl(
      runtime.api.appData,
      src,
      host?.getAttribute("data-contents-name")
    ) : null;
    const item = expandRowItem(row) ?? coll?.get?.(idx);
    if (item && typeof item === "object") return item;
    const stamped = getRowItem(row);
    if (stamped && typeof stamped === "object") return stamped;
    return runtime.api.self ?? {};
  }
  readField(runtime, field) {
    try {
      return this.resolveTarget(runtime).getValue?.(field);
    } catch {
      return void 0;
    }
  }
  // ---------- hidratación ----------
  hydrate(runtime) {
    if (this.isLocked) return;
    const native = this.querySelector("select.xone-combo-native");
    if (this.isStatic && native) {
      this.wireNativeSelect(runtime, native);
      return;
    }
    this.wireCustomCombo(runtime);
  }
  /** Estático: `<select>` nativo. Reusa write-back directo. */
  wireNativeSelect(runtime, select) {
    const current3 = this.readField(runtime, this.displayProp);
    if (current3 !== void 0 && current3 !== null && current3 !== "") {
      select.value = String(current3);
    }
    const onChange = () => {
      if (this.isDisabled()) return;
      const label = select.value;
      const index = select.selectedIndex;
      this.commitStatic(runtime, label, index);
    };
    select.addEventListener("change", onChange);
    this.cleanup.push(() => select.removeEventListener("change", onChange));
    const reflectDisabled = () => {
      select.disabled = this.isDisabled();
    };
    reflectDisabled();
    const observer = new MutationObserver(reflectDisabled);
    observer.observe(this, {
      attributes: true,
      attributeFilter: ["disabled", "inert", "hidden"]
    });
    this.cleanup.push(() => observer.disconnect());
  }
  /** Dinámico / estático-custom: display + trigger + panel. */
  wireCustomCombo(runtime) {
    const display = this.querySelector(".xone-combo-display");
    const trigger = this.querySelector(".xone-combo-trigger");
    this.paintDisplay(runtime, { preserveExisting: true });
    const open = (ev) => {
      ev.preventDefault();
      if (this.isDisabled()) return;
      void this.openPanel(runtime);
    };
    trigger?.addEventListener("click", open);
    display?.addEventListener("click", open);
    this.cleanup.push(() => {
      trigger?.removeEventListener("click", open);
      display?.removeEventListener("click", open);
    });
  }
  isDisabled() {
    return this.hasAttribute("hidden") || this.hasAttribute("disabled") || this.hasAttribute("inert");
  }
  paintDisplay(runtime, opts) {
    const display = this.querySelector(".xone-combo-display");
    if (!display) return;
    const val = this.readField(runtime, this.displayProp);
    const isEmpty = val === void 0 || val === null || val === "";
    if (isEmpty && opts?.preserveExisting && (display.textContent ?? "").trim() !== "") return;
    const text = isEmpty ? this.defaultText : String(val);
    display.textContent = text;
    display.classList.toggle("xone-combo-placeholder", text === this.defaultText && text !== "");
  }
  // ---------- opciones ----------
  async ensureOptions(runtime) {
    if (this.isStatic) {
      if (this.optionsLoaded) return;
      const vals = this.staticValues;
      this.options = vals.map((label, index) => ({ value: label, label, index }));
      this.optionsLoaded = true;
      return;
    }
    const rtFilter = this.idProp ? this.resolveTarget(runtime).getFieldPropertyValue?.(this.idProp, "filter") : void 0;
    const base = rtFilter !== null && rtFilter !== void 0 && String(rtFilter).trim() !== "" ? String(rtFilter) : this.filter;
    const resolved = this.resolveMacros(runtime, base);
    if (resolved && resolved.includes("##")) {
      try {
        runtime.api.console.warn(
          `[xone-combo] filtro con macros sin resolver, omito carga: ${resolved}`
        );
      } catch {
      }
      this.options = [];
      return;
    }
    const sharedMacros = this.readSharedMacros(runtime);
    const macroSig = Object.keys(sharedMacros).map((k) => `${k}=${sharedMacros[k]}`).join("&");
    const loadKey = macroSig ? `${resolved ?? ""}|m:${macroSig}` : resolved;
    if (this.optionsLoaded && loadKey === this.lastFilter) return;
    this.lastFilter = loadKey ?? "";
    const coll = await this.ensureLookupColl(runtime);
    if (!coll) {
      this.options = [];
      this.optionsLoaded = true;
      return;
    }
    try {
      coll.setFilter?.(resolved ?? "");
      if (typeof coll.setMacro === "function") {
        for (const [k, v] of Object.entries(sharedMacros)) coll.setMacro(k, v);
      }
      await coll.loadAll();
      const n = coll.getCount();
      const opts = [];
      for (let i = 0; i < n; i++) {
        const obj = coll.get(i);
        if (!obj || typeof obj.getValue !== "function") continue;
        const value = obj.getValue(this.mapfld);
        const label = obj.getValue(this.linkedfield);
        opts.push({
          value: value === void 0 || value === null ? "" : String(value),
          label: label === void 0 || label === null ? "" : String(label),
          obj
        });
      }
      this.options = opts;
      this.optionsLoaded = true;
    } catch (err) {
      try {
        runtime.api.console.error("[xone-combo] loadOptions", err);
      } catch {
      }
      this.options = [];
      this.optionsLoaded = true;
    }
  }
  /**
   * WEAL-136: devuelve la colección lookup de ESTE combo, aislada por
   * instancia. Prefiere un `createClone()` del singleton compartido — una
   * colección nueva, nunca `locked`, con filtro/datos propios — para que dos
   * `<xone-combo>` sobre el mismo `mapcol` pero con distinto `##FLD_x##` no se
   * pisen el filtro (A) ni hereden el estado `locked` del otro (B). Si la coll
   * no soporta `createClone` (mocks/tests antiguos, colecciones ghost del
   * manifest), cae al singleton compartido (comportamiento previo WEAL-40).
   * El clon se cachea por instancia y se reutiliza (re-`setFilter`+`loadAll`
   * cuando el filtro efectivo cambia; ver `ensureOptions`/`lastFilter`).
   */
  async ensureLookupColl(runtime) {
    if (this.lookupColl) return this.lookupColl;
    const base = runtime.api.appData.getCollection(this.coll);
    if (!base) return null;
    if (typeof base.createClone === "function") {
      try {
        const clone = await base.createClone();
        if (clone) {
          this.lookupColl = clone;
          return clone;
        }
      } catch (err) {
        try {
          runtime.api.console.warn(
            "[xone-combo] createClone fall\xF3, uso singleton compartido",
            err
          );
        } catch {
        }
      }
    }
    this.lookupColl = base;
    return base;
  }
  /**
   * WEAL-282: macros de colección fijadas por el proyecto sobre el singleton
   * `appData.getCollection(mapcol)` (paridad Android: el combo consulta la
   * colección con sus macros `setMacro`). `{}` si el wrapper no las expone.
   */
  readSharedMacros(runtime) {
    try {
      const base = runtime.api.appData.getCollection(this.coll);
      const all = base?.getAllMacros?.();
      return all && typeof all === "object" ? all : {};
    } catch {
      return {};
    }
  }
  /** Resuelve macros `##FLD_x##`/`##X##` del filtro contra el objeto actual. */
  resolveMacros(runtime, filter) {
    if (!filter) return void 0;
    if (!filter.includes("##")) return filter;
    const appData = runtime.api.appData;
    const self = this.resolveTarget(runtime);
    let result = filter;
    if (typeof appData.expandMacros === "function") {
      result = appData.expandMacros(result);
    }
    if (result.includes("##")) {
      result = result.replace(/##([A-Za-z0-9_]+)##/g, (_m, name) => {
        try {
          if (name.startsWith("FLD_") || name.startsWith("OWN_")) {
            const f = name.slice(4);
            const v = typeof self.getValue === "function" ? self.getValue(f) : self[f];
            if (v !== void 0 && v !== null) return String(v);
            return `##${name}##`;
          }
          if (typeof appData.getGlobalMacro === "function") {
            const v = appData.getGlobalMacro(name);
            if (v !== void 0 && v !== null) return String(v);
          }
        } catch {
        }
        return `##${name}##`;
      });
    }
    return result;
  }
  // ---------- panel / modal ----------
  async openPanel(runtime) {
    if (this.isLocked) return;
    if (this.opening || this.panel) return;
    this.opening = true;
    this.closePanel();
    const isPicker = this.mode === "picker";
    const panel = document.createElement("div");
    panel.className = isPicker ? "xone-combo-modal" : "xone-combo-panel";
    panel.setAttribute("role", "listbox");
    const dialog = this.getAttribute("data-combo-dialog");
    if (dialog) panel.setAttribute("style", dialog);
    let overlay = null;
    if (isPicker) {
      overlay = document.createElement("div");
      overlay.className = "xone-combo-overlay";
      overlay.appendChild(panel);
    }
    let search = null;
    const list = document.createElement("ul");
    list.className = "xone-combo-options";
    panel.appendChild(list);
    const renderOptions = (query) => {
      list.textContent = "";
      const q = query.trim().toLowerCase();
      const rows = [];
      if (this.hasCleaner && q === "") {
        rows.push({ value: "", label: this.defaultText || "\u2014", index: -1 });
      }
      for (const o of this.options) {
        if (q !== "" && !o.label.toLowerCase().includes(q)) continue;
        rows.push(o);
      }
      for (const o of rows) {
        const li = document.createElement("li");
        li.className = "xone-combo-option";
        li.setAttribute("role", "option");
        li.textContent = o.label === "" ? "\xA0" : o.label;
        li.addEventListener("click", () => {
          if (this.isStatic) this.commitStatic(runtime, o.label, o.index ?? -1);
          else this.commitDynamic(runtime, o);
          this.closePanel();
        });
        list.appendChild(li);
      }
      if (rows.length === 0) {
        const li = document.createElement("li");
        li.className = "xone-combo-option xone-combo-empty";
        li.textContent = this.optionsLoaded ? "Sin resultados" : "Cargando\u2026";
        list.appendChild(li);
      }
    };
    const ensureSearchBox = () => {
      if (search) return;
      search = document.createElement("input");
      search.type = "text";
      search.className = "xone-combo-search";
      search.setAttribute("aria-label", "Buscar");
      search.setAttribute("placeholder", "Buscar\u2026");
      search.addEventListener("input", () => renderOptions(search.value));
      panel.insertBefore(search, list);
      window.setTimeout(() => search.focus(), 0);
    };
    if (this.hasSearch) ensureSearchBox();
    if (!isPicker) {
      const rect = this.getBoundingClientRect();
      panel.style.position = "fixed";
      panel.style.left = `${rect.left}px`;
      panel.style.top = `${rect.bottom}px`;
      panel.style.minWidth = `${rect.width}px`;
    }
    const root = overlay ?? panel;
    document.body.appendChild(root);
    this.panel = root;
    this.setAttribute("aria-expanded", "true");
    this.opening = false;
    renderOptions("");
    if (this.optionsLoaded && this.options.length > SEARCH_THRESHOLD) ensureSearchBox();
    void this.ensureOptions(runtime).then(() => {
      if (this.panel !== root) return;
      if (this.options.length > SEARCH_THRESHOLD) ensureSearchBox();
      renderOptions(search ? search.value : "");
    }).catch(() => {
    });
    const onDocClick = (e) => {
      if (root.contains(e.target) || this.contains(e.target)) return;
      this.closePanel();
    };
    const onKey = (e) => {
      if (e.key === "Escape") this.closePanel();
    };
    if (overlay) {
      overlay.addEventListener("click", (e) => {
        if (e.target === overlay) this.closePanel();
      });
    }
    let armed = true;
    const tid = window.setTimeout(() => {
      if (armed) document.addEventListener("click", onDocClick);
    }, 0);
    document.addEventListener("keydown", onKey);
    this.panelCleanup.push(() => {
      armed = false;
      window.clearTimeout(tid);
      document.removeEventListener("click", onDocClick);
      document.removeEventListener("keydown", onKey);
    });
  }
  closePanel() {
    for (const fn of this.panelCleanup) {
      try {
        fn();
      } catch {
      }
    }
    this.panelCleanup = [];
    if (this.panel) {
      try {
        this.panel.remove();
      } catch {
      }
      this.panel = null;
    }
    this.removeAttribute("aria-expanded");
  }
  // ---------- commit de selección ----------
  /**
   * WEAL-15 — Re-evalúa las condiciones (`disablevisible`/`disableedit`) que
   * dependen del idProp recién escrito.
   *
   * `target.setValue()` NO pasa por el set trap del Proxy de `self`
   * (`createAppContext.ts:569-581`), que es quien normalmente dispara
   * `domSync.syncField`. `writeMirrors` ya compensaba esto para los ESPEJOS
   * (`syncField(m.prop)`), pero nadie lo hacía para el **idProp** — y toda
   * fórmula `disablevisible` de un combo depende del idProp
   * (`ID_PROCEDENCIA_DEUDA`), no de la display (`MAP_PROCEDENCIA_DEUDA`). Sin
   * esto la condición no se re-evalúa jamás tras una selección del usuario:
   * el modelo queda correcto y el DOM rancio.
   *
   * Usa `syncConditionalsForField` (no `syncField`) a propósito: `syncField`
   * llamaría a `restoreMappedFieldsFor(idProp)`, que por duck-typing reentra
   * en ESTE mismo combo → `writeMirrors` duplicado.
   */
  syncIdPropConditionals(runtime) {
    if (!this.idProp) return;
    if (this.closest(".xone-contents-row")) return;
    const domSync = runtime.api.domSync;
    try {
      domSync?.syncConditionalsForField?.(this.idProp);
    } catch {
    }
  }
  commitStatic(runtime, label, index) {
    markComboCommit(this);
    const target = this.resolveTarget(runtime);
    void target.setValue?.(this.displayProp, label);
    if (this.idProp && index >= 0) {
      void target.setValue?.(this.idProp, this.idIsNumeric ? index : String(index));
    } else if (this.idProp) {
      void target.setValue?.(this.idProp, label);
    }
    this.syncIdPropConditionals(runtime);
    this.paintDisplay(runtime);
    this.runEditorAction(runtime);
  }
  commitDynamic(runtime, opt) {
    markComboCommit(this);
    const target = this.resolveTarget(runtime);
    if (this.idProp) void target.setValue?.(this.idProp, opt.value);
    this.syncIdPropConditionals(runtime);
    this.writeMirrors(runtime, opt);
    this.runEditorAction(runtime);
  }
  // ---------- RestoreMappedFields (paridad Android) ----------
  /**
   * Escribe los espejos (`data-combo-mirrors` = equivalente web precomputado de
   * `GetNodeList("prop","linkedto",idProp)`) a partir de la opción enlazada, o
   * los LIMPIA si `opt` es `null` (paridad `DoRestoreMapValues(sFieldName, null)`,
   * `XoneDataObject.java:2172-2205`).
   */
  writeMirrors(runtime, opt) {
    const target = this.resolveTarget(runtime);
    const domSync = runtime.api.domSync;
    for (const m of this.mirrors) {
      let v = "";
      if (opt) {
        if (opt.obj && typeof opt.obj.getValue === "function") {
          v = opt.obj.getValue(m.linkedfield);
        } else if (m.prop === this.displayProp) {
          v = opt.label;
        }
      }
      void target.setValue?.(m.prop, v === void 0 || v === null ? "" : v);
      try {
        domSync?.syncField(m.prop);
      } catch {
      }
    }
    this.paintDisplay(runtime);
  }
  /** Busca la opción cargada cuya clave (`mapfld`) coincide con el id. */
  findOption(idValue) {
    if (this.isStatic && this.idIsNumeric) {
      const s = String(idValue).trim();
      if (!/^[+-]?\d+$/.test(s)) return void 0;
      const idx = Number(s);
      return this.options.find((o) => o.index === idx);
    }
    const key = String(idValue).trim();
    return this.options.find((o) => o.value === key);
  }
  /**
   * Equivalente web de `XoneDataObject.RestoreMappedFields` para cambios
   * PROGRAMÁTICOS del campo ID enlazado (`XoneDataObject.java:1721-1802`).
   *
   * En Android, TODO `put()` de un campo dispara `RestoreMappedFields` (`:1390-1399`),
   * de modo que `self.MAP_CODIGO_ESTADO = 0` limpia sola la display enlazada
   * (`MAP_ESTADO`). En web, el único sitio que hidrataba los espejos era
   * `commitDynamic` — la ruta de selección del USUARIO —, así que un cambio por
   * script dejaba la display con el valor viejo EN EL MODELO (WEAL-10: "Borrar
   * filtro" limpiaba el id y el filtro, pero `MAP_ESTADO` seguía valiendo
   * "Propuesta" y el span lo pintaba fielmente).
   *
   * Lo invoca `domSync.syncField(idProp)` por duck-typing (ver `domSync.ts`), que
   * a su vez cuelga del set trap del Proxy de `self`. NUNCA se llama durante la
   * hidratación/`syncAll` (guard `m_bIsLoading` de Android, `:1390`): ahí la
   * display ya trae el valor denormalizado del backend y las opciones aún no
   * están cargadas, así que restaurar borraría un valor legítimo.
   */
  restoreMappedFields() {
    const runtime = window[RUNTIME_KEY2];
    if (!runtime || !this[HYDRATED2] || !this.idProp) return;
    if (this.restoring) return;
    this.restoring = true;
    try {
      const idValue = this.readField(runtime, this.idProp);
      const blank = this.isStatic && this.idIsNumeric ? isBlankStaticIndex(idValue) : isBlankLinkedId(idValue);
      if (blank) {
        this.writeMirrors(runtime, null);
        return;
      }
      const opt = this.findOption(idValue);
      if (opt) {
        this.writeMirrors(runtime, opt);
        return;
      }
      if (this.optionsLoaded) {
        this.writeMirrors(runtime, null);
        return;
      }
      void this.ensureOptions(runtime).then(() => {
        if (this.restoring) return;
        const late = this.findOption(this.readField(runtime, this.idProp));
        if (late) {
          this.writeMirrors(runtime, late);
          return;
        }
        if (this.options.length > 0) this.writeMirrors(runtime, null);
      }).catch(() => {
      });
    } finally {
      this.restoring = false;
    }
  }
  /**
   * Ejecuta el `oneditoraction` del display tras un commit de selección del
   * usuario (`javascript:foo(self,'Vehiculos')`, parser CSP-safe compartido).
   *
   * WEAL-247: el `<onchange>` del campo (`${idProp}_onchange` /
   * `${displayProp}_onchange`) YA NO se dispara aquí. Lo dispara ahora
   * `target.setValue()` de `commitStatic`/`commitDynamic` a través del puente
   * MODELO→handler (WEAL-60): el `self` de página
   * (`createAppContext.setValue` → `fireFieldOnchange` →
   * `__xoneDispatchFieldOnchange` → `XoneIsland.dispatchFieldOnchangeFromModel`)
   * y el `DataObject.setValue` de las filas de `<contents>`
   * (`dispatchFieldOnchangeToRuntime`). Ese puente ya resuelve el handler con la
   * misma preferencia campo-enlazado→display (WEAL-9) y acota el `self` a la fila
   * cuando toca. Volver a dispararlo aquí lo ejecutaba DOS veces por selección
   * (dos `self.save()` → dos `POST DoAction write` — el bug reportado). Es la
   * limpieza simétrica a la que ya se hizo para los inputs de texto en
   * `XoneIsland.onInput` ("se retira para no disparar el onchange dos veces por
   * edición de UI").
   *
   * El `oneditoraction`, en cambio, NO lo cubre el puente de `setValue`, así que
   * se conserva aquí (cubre `actualizarDatosContent`, `exportarCsv`).
   */
  runEditorAction(runtime) {
    const oea = this.getAttribute("data-oneditoraction");
    if (oea) this.runInlineAction(runtime, oea);
  }
  runInlineAction(runtime, raw) {
    execInlineAction(raw, this.resolveTarget(runtime), (err) => {
      try {
        runtime.api.console.error("[xone-combo] oneditoraction", err);
      } catch {
      }
    });
  }
};
var registered = false;
function registerXoneCombo() {
  if (registered) return;
  registered = true;
  if (typeof customElements !== "undefined" && !customElements.get(ELEMENT_TAG2)) {
    customElements.define(ELEMENT_TAG2, XoneComboElement);
  }
}
function _resetXoneComboForTests() {
  registered = false;
}

// src/canvasSizer.ts
import { installCanvasSizer, installCanvasSizerFromDocument } from "@xone-web/css-engine";

// src/breadcrumbRenderer.ts
function construirItems(trail, titulos) {
  return trail.map((entrada, i) => ({
    etiqueta: titulos[entrada.collection] ?? entrada.collection,
    // La ÚLTIMA es la actual: no navega (cursor:auto en el legacy).
    entryId: i === trail.length - 1 ? null : entrada.id
  }));
}
function renderBreadcrumb(host, items, navegar) {
  host.textContent = "";
  if (items.length === 0) return;
  const d = host.dataset;
  const sep = d["bcSeparator"] ?? "/";
  const colorNormal = d["bcForecolor"] ?? "";
  const colorSel = d["bcSelectedForecolor"] ?? "";
  const sizeNormal = d["bcFontsize"] ?? "";
  const sizeSel = d["bcSelectedFontsize"] ?? "";
  const contenedor = host.ownerDocument.createElement("div");
  contenedor.className = "xone-breadcrumb";
  items.forEach((item, i) => {
    const doc = host.ownerDocument;
    const wrap = doc.createElement("div");
    wrap.className = "xone-breadcrumb-item";
    if (i > 0) {
      const s = doc.createElement("span");
      s.className = "xone-breadcrumb-sep";
      s.textContent = ` ${sep} `;
      if (sizeNormal !== "") s.style.fontSize = sizeNormal;
      if (colorNormal !== "") s.style.color = colorNormal;
      wrap.appendChild(s);
    }
    const esActual = item.entryId === null;
    const label = doc.createElement("span");
    label.className = esActual ? "xone-breadcrumb-label xone-breadcrumb-current" : "xone-breadcrumb-label";
    label.textContent = item.etiqueta;
    const color = esActual ? colorSel || colorNormal : colorNormal;
    const size = esActual ? sizeSel || sizeNormal : sizeNormal;
    if (color !== "") label.style.color = color;
    if (size !== "") label.style.fontSize = size;
    label.style.cursor = esActual ? "auto" : "pointer";
    if (!esActual) {
      const destino = item.entryId;
      label.setAttribute("role", "link");
      label.setAttribute("tabindex", "0");
      const ir = (e) => {
        e.preventDefault();
        if (destino !== null) navegar(destino);
      };
      label.addEventListener("click", ir);
      label.addEventListener("keydown", (e) => {
        const k = e.key;
        if (k === "Enter" || k === " ") ir(e);
      });
    } else {
      label.setAttribute("aria-current", "page");
    }
    wrap.appendChild(label);
    contenedor.appendChild(wrap);
  });
  host.appendChild(contenedor);
}
function findBreadcrumbHosts(root) {
  return [...root.querySelectorAll("[data-xone-breadcrumb]")];
}
export {
  GLOBAL_MACRO_EVENT,
  XoneComboElement,
  XoneIslandElement,
  _resetXoneComboForTests,
  _resetXoneIslandForTests,
  construirItems,
  findBreadcrumbHosts,
  hydrateVisibleContentsNow,
  installCanvasSizer,
  installCanvasSizerFromDocument,
  registerXoneCombo,
  registerXoneIsland,
  renderBreadcrumb,
  runXoneCollLoad,
  runXoneCollOnchangeOnLoad,
  runXoneNode
};
//# sourceMappingURL=index.js.map